import React, { useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';
import "@/App.css";
import axios from 'axios';
import LandingPage from './components/LandingPage';
import BrandingPanel from './components/BrandingPanel';
import PricingPdfModal from './components/PricingPdfModal';
import AdminDashboard from './components/AdminDashboard';
import FeedbackModal from './components/FeedbackModal';
import { 
  validatePartnerCode, 
  trackReferral, 
  upgradeReferral,
  downgradeReferral,
  churnReferral,
  saveReferralCode, 
  getReferralCode, 
  clearReferralCode 
} from './services/referralService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Emergent Auth URL - No Google Cloud Console configuration needed!
const EMERGENT_AUTH_URL = 'https://auth.emergentagent.com';

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

// Icons
const Home = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
const Sparkles = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>;
const Copy = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>;
const Check = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polyline points="20 6 9 17 4 12"/></svg>;
const Zap = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;
const Eye = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>;
const EyeOff = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" x2="22" y1="2" y2="22"/></svg>;
const Gift = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 4.8 0 0 1 12 8a4.8 4.8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/></svg>;
const Wand = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M15 4V2"/><path d="M15 16v-2"/><path d="M8 9h2"/><path d="M20 9h2"/><path d="m3 21 9-9"/></svg>;
const Camera = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/></svg>;
const Globe = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>;
const Share = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" x2="15.42" y1="13.51" y2="17.49"/><line x1="15.41" x2="8.59" y1="6.51" y2="10.49"/></svg>;
const FileText = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>;
const Download = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>;
const History = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/></svg>;
const Bookmark = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></svg>;
const DollarSign = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
const Sofa = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M20 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3"/><path d="M2 11v5a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-5a2 2 0 0 0-4 0v2H6v-2a2 2 0 0 0-4 0Z"/></svg>;
const X = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>;
const Plus = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M5 12h14"/><path d="M12 5v14"/></svg>;
const Edit = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const ChevronRight = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m9 18 6-6-6-6"/></svg>;
const ChevronLeft = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m15 18-6-6 6-6"/></svg>;
const ArrowLeft = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>;
const ArrowRight = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>;
const Trash2 = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>;
const Upload = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>;
const Save = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
const Lock = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
const Crown = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7z"/><path d="M4 22h16"/></svg>;
const Video = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2" ry="2"/></svg>;
const Mail = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
const Users = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const MapPin = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>;
const Search = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>;
const Printer = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect width="12" height="8" x="6" y="14"/></svg>;
const HomeIcon = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>;
const User = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const LogOut = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>;
const Menu = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="18" y2="18"/></svg>;
const QrCode = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="5" height="5" x="3" y="3" rx="1"/><rect width="5" height="5" x="16" y="3" rx="1"/><rect width="5" height="5" x="3" y="16" rx="1"/><path d="M21 16h-3a2 2 0 0 0-2 2v3"/><path d="M21 21v.01"/><path d="M12 7v3a2 2 0 0 1-2 2H7"/><path d="M3 12h.01"/><path d="M12 3h.01"/><path d="M12 16v.01"/><path d="M16 12h1"/><path d="M21 12v.01"/><path d="M12 21v-1"/></svg>;
const Hash = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><line x1="4" x2="20" y1="9" y2="9"/><line x1="4" x2="20" y1="15" y2="15"/><line x1="10" x2="8" y1="3" y2="21"/><line x1="16" x2="14" y1="3" y2="21"/></svg>;
const Heart = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>;
const Wrench = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>;
const Loader2 = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>;
const Calendar = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>;
const Send = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M22 2 11 13"/><path d="m22 2-7 20-4-9-9-4 20-7z"/></svg>;
const CheckSquare = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>;
const RefreshCw = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>;
const Maximize2 = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" x2="14" y1="3" y2="10"/><line x1="3" x2="10" y1="21" y2="14"/></svg>;
const Minimize2 = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" x2="21" y1="10" y2="3"/><line x1="3" x2="10" y1="21" y2="14"/></svg>;
const Lightbulb = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/></svg>;
const MessageSquare = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
const GitCompare = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="18" cy="18" r="3"/><circle cx="6" cy="6" r="3"/><path d="M13 6h3a2 2 0 0 1 2 2v7"/><path d="M11 18H8a2 2 0 0 1-2-2V9"/></svg>;
const ImageIcon = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>;
const Settings = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>;
const CreditCard = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>;
const LayoutDashboard = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/></svg>;
const Bell = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>;
const Star = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
const TrendingUp = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>;
const AlertCircle = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>;
const BarChart3 = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/></svg>;
const BarChart2 = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><line x1="18" x2="18" y1="20" y2="10"/><line x1="12" x2="12" y1="20" y2="4"/><line x1="6" x2="6" y1="20" y2="14"/></svg>;
const MessageCircle = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m3 21 1.9-5.7a8.5 8.5 0 1 1 3.8 3.8z"/></svg>;
const Bot = ({ className }) => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M12 8V4H8"/><rect width="16" height="12" x="4" y="8" rx="2"/><path d="M2 14h2"/><path d="M20 14h2"/><path d="M15 13v2"/><path d="M9 13v2"/></svg>;

// AI Chatbot Component - ChatGPT-like Real Estate Expert
const AIChatbot = ({ onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { 
      role: 'assistant', 
      content: "👋 Hi! I'm your **Real Estate Marketing Expert**. I can help you with:\n\n• **App Navigation** - Find any feature instantly\n• **Listing Descriptions** - Tips for compelling copy\n• **Marketing Strategies** - Social media, video, print\n• **Buyer Psychology** - Understanding your audience\n• **Industry Best Practices** - What top agents do\n\nAsk me anything about real estate marketing!" 
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesEndRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const quickActions = [
    { label: "🏠 How to write great listings?", action: "listing_tips" },
    { label: "📱 Social media strategies", action: "social_tips" },
    { label: "🎯 Understanding buyer personas", action: "buyer_tips" },
    { label: "📸 Property photo tips", action: "photo_tips" },
    { label: "✨ What tools do you have?", action: "ai_tools" },
  ];

  const handleQuickAction = (action) => {
    const actionMessages = {
      listing_tips: "What are the best practices for writing compelling property listing descriptions that sell?",
      social_tips: "What are effective social media marketing strategies for real estate agents?",
      buyer_tips: "How can I better understand and target different buyer personas?",
      photo_tips: "What are the best practices for property photography and staging?",
      ai_tools: "What AI marketing tools are available in GeniusList and how can they help me?",
    };
    handleSend(actionMessages[action]);
  };

  const handleSend = async (messageText = input) => {
    if (!messageText.trim()) return;
    
    const userMessage = { role: 'user', content: messageText };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsLoading(true);

    try {
      // Send conversation history for context
      const conversationHistory = updatedMessages.slice(-10).map(m => ({
        role: m.role,
        content: m.content
      }));
      
      const response = await axios.post(`${API}/chatbot`, { 
        message: messageText,
        context: 'GeniusList real estate AI marketing platform',
        conversation_history: conversationHistory
      });
      
      if (response.data.response) {
        setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
        
        // Handle navigation commands
        if (response.data.navigate) {
          setTimeout(() => {
            onNavigate(response.data.navigate);
          }, 1000);
        }
      }
    } catch (error) {
      // Fallback to local responses if API fails
      const fallbackResponse = getLocalResponse(messageText);
      setMessages(prev => [...prev, { role: 'assistant', content: fallbackResponse.message }]);
      if (fallbackResponse.navigate) {
        setTimeout(() => {
          onNavigate(fallbackResponse.navigate);
        }, 1000);
      }
    }
    
    setIsLoading(false);
  };

  const getLocalResponse = (message) => {
    const lowerMessage = message.toLowerCase();
    
    // Navigation requests
    if (lowerMessage.includes('dashboard') || lowerMessage.includes('home')) {
      return { message: "Taking you to the Dashboard! 🏠", navigate: 'dashboard' };
    }
    if (lowerMessage.includes('generate') || lowerMessage.includes('create listing') || lowerMessage.includes('new listing')) {
      return { message: "Let's create a listing! Taking you to the Generate Listing page. 📝", navigate: 'generate' };
    }
    if (lowerMessage.includes('feature sheet')) {
      return { message: "Opening the Feature Sheet generator for you! 📄", navigate: 'feature-sheet' };
    }
    if (lowerMessage.includes('my listings') || lowerMessage.includes('saved')) {
      return { message: "Here are your saved listings! 📋", navigate: 'my-listings' };
    }
    if (lowerMessage.includes('settings')) {
      return { message: "Taking you to Settings! ⚙️", navigate: 'settings' };
    }
    if (lowerMessage.includes('qr code')) {
      return { message: "Let's generate a QR code! 📱", navigate: 'qrcode' };
    }
    if (lowerMessage.includes('video script')) {
      return { message: "Opening the Video Script generator! 🎬", navigate: 'video' };
    }
    if (lowerMessage.includes('social media')) {
      return { message: "Let's create social media content! 📱", navigate: 'social' };
    }
    if (lowerMessage.includes('hashtag')) {
      return { message: "Opening the Hashtag generator! #️⃣", navigate: 'hashtags' };
    }
    if (lowerMessage.includes('email')) {
      return { message: "Let's create email templates! ✉️", navigate: 'clientcomm' };
    }
    if (lowerMessage.includes('translate')) {
      return { message: "Opening the Translation tool! 🌍", navigate: 'translate' };
    }

    // FAQ responses
    if (lowerMessage.includes('how') && (lowerMessage.includes('create') || lowerMessage.includes('generate') || lowerMessage.includes('listing'))) {
      return { 
        message: "Creating a listing is easy! Here's how:\n\n1️⃣ Go to **Generate Listing** in the sidebar\n2️⃣ Fill in property details (type, address, beds, baths, etc.)\n3️⃣ Upload property photos (optional but recommended!)\n4️⃣ Add key features from suggestions or type your own\n5️⃣ Click **Generate Description**\n\nThe AI will create a professional listing description for you! Would you like me to take you there?",
        navigate: null
      };
    }
    
    if (lowerMessage.includes('ai tools') || lowerMessage.includes('marketing tools') || lowerMessage.includes('what tools')) {
      return { 
        message: "GeniusList has **17+ AI Marketing Tools** including:\n\n📝 **A/B Variations** - Multiple description versions\n📱 **QR Code Generator** - Property QR codes\n#️⃣ **Hashtag Generator** - Optimized hashtags\n🎬 **Video Script Generator** - Property video scripts\n📱 **Social Media Posts** - Ready-to-post content\n✉️ **Email Templates** - Client communication\n🌍 **Translation** - Multi-language support\n👥 **Buyer Persona** - Target audience analysis\n🔍 **SEO Analyzer** - Optimize your listings\n📄 **MLS Export** - Professional formats\n\nAnd many more! All available in the **AI Marketing Tools** menu.",
        navigate: null
      };
    }
    
    if (lowerMessage.includes('price') || lowerMessage.includes('pricing') || lowerMessage.includes('cost') || lowerMessage.includes('plan')) {
      return { 
        message: "We have **4 pricing plans**:\n\n🆓 **Free** - $0/mo\n• Basic listing descriptions\n• 5 generations/month\n\n💼 **Basic** - $29.99/mo\n• 50 generations/month\n• Save listings\n\n⭐ **Premium** - $59.99/mo (Most Popular!)\n• Unlimited generations\n• All 17+ AI tools\n• Priority support\n\n🏢 **Brokerage** - $199/mo\n• Everything in Premium\n• Unlimited agent sub-accounts\n• Team collaboration\n\nWould you like to upgrade?",
        navigate: null
      };
    }
    
    if (lowerMessage.includes('feature sheet')) {
      return { 
        message: "The **Feature Sheet Generator** creates professional PDF brochures for your properties! It includes:\n\n📸 Property photos\n📊 Floor plans\n🏠 Property details\n✨ Key features\n👔 Agent branding\n\nPerfect for open houses and client presentations! Want me to open it?",
        navigate: null
      };
    }
    
    if (lowerMessage.includes('photo') || lowerMessage.includes('image')) {
      return { 
        message: "Yes! You can upload property photos and our AI will:\n\n🔍 **Analyze** the images to detect features\n✨ **Suggest** key features automatically\n📝 **Enhance** your listing description\n\nJust click the photo upload area in the Generate Listing section!",
        navigate: null
      };
    }

    if (lowerMessage.includes('help') || lowerMessage.includes('support')) {
      return { 
        message: "I'm here to help! Here's what I can do:\n\n🧭 **Navigate** - I can take you to any section\n❓ **Answer questions** - About features, pricing, how-to\n💡 **Give tips** - Best practices for listings\n\nTry asking:\n• \"How do I create a listing?\"\n• \"What AI tools are available?\"\n• \"Take me to Feature Sheet\"\n• \"What's included in Premium?\"",
        navigate: null
      };
    }

    if (lowerMessage.includes('thank')) {
      return { message: "You're welcome! 😊 Let me know if you need anything else!", navigate: null };
    }

    if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey')) {
      return { message: "Hello! 👋 How can I help you today? I can assist with navigation, answer questions about features, or help you get started with creating listings!", navigate: null };
    }

    // Default response
    return { 
      message: "I'm your **Real Estate Marketing Expert**! I can help with:\n\n🏠 **Listing descriptions** - Tips for compelling copy\n📱 **Social media** - Instagram, Facebook, TikTok strategies\n🎬 **Video marketing** - Script ideas and tips\n🎯 **Buyer psychology** - Understanding your audience\n📸 **Photography** - Staging and photo tips\n\nOr ask me to navigate to any app feature! What would you like to know?",
      navigate: null
    };
  };

  const clearChat = () => {
    setMessages([{ 
      role: 'assistant', 
      content: "👋 Hi! I'm your **Real Estate Marketing Expert**. I can help you with:\n\n• **App Navigation** - Find any feature instantly\n• **Listing Descriptions** - Tips for compelling copy\n• **Marketing Strategies** - Social media, video, print\n• **Buyer Psychology** - Understanding your audience\n• **Industry Best Practices** - What top agents do\n\nAsk me anything about real estate marketing!" 
    }]);
  };

  // Simple markdown-like rendering for bold text
  const renderMessage = (content) => {
    const parts = content.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg z-50 flex items-center justify-center transition-all hover:scale-110"
        style={{ 
          background: 'linear-gradient(135deg, #5B21B6 0%, #3B82F6 100%)',
          boxShadow: '0 4px 20px rgba(91, 33, 182, 0.4)'
        }}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed z-50 flex flex-col overflow-hidden bg-white rounded-2xl shadow-2xl transition-all duration-300 ${
            isExpanded 
              ? 'bottom-4 right-4 left-4 top-4 md:left-auto md:w-[600px] md:h-[700px] md:bottom-6 md:right-6 md:top-auto' 
              : 'bottom-24 right-6 w-96 h-[550px]'
          }`}
          style={{ boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)' }}
        >
          {/* Header */}
          <div 
            className="p-4 text-white flex items-center justify-between"
            style={{ background: 'linear-gradient(90deg, #5B21B6 0%, #3B82F6 100%)' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold">Real Estate Marketing Expert</h3>
                <p className="text-xs text-white/80">Ask me anything about real estate</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearChat}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Clear chat"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title={isExpanded ? "Minimize" : "Expand"}
              >
                {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mr-2 flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                )}
                <div 
                  className={`max-w-[85%] p-4 rounded-2xl text-sm whitespace-pre-line leading-relaxed ${
                    msg.role === 'user' 
                      ? 'bg-purple-600 text-white rounded-br-md' 
                      : 'bg-white text-gray-700 rounded-bl-md shadow-sm border border-gray-100'
                  }`}
                >
                  {renderMessage(msg.content)}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center mr-2 flex-shrink-0">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white p-4 rounded-2xl rounded-bl-md shadow-sm border border-gray-100">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {messages.length <= 2 && (
            <div className="px-4 py-3 bg-white border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-2 font-medium">Try asking:</p>
              <div className="flex flex-wrap gap-2">
                {quickActions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickAction(action.action)}
                    className="text-xs px-3 py-1.5 bg-gradient-to-r from-purple-50 to-blue-50 text-purple-700 rounded-full hover:from-purple-100 hover:to-blue-100 transition-colors border border-purple-100"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 bg-white border-t border-gray-100">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                placeholder="Ask about real estate marketing, listings, strategies..."
                className="flex-1 px-4 py-3 bg-gray-100 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-white transition-all"
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || isLoading}
                className="p-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">AI can make mistakes. Verify important information.</p>
          </div>
        </div>
      )}
    </>
  );
};

// Sidebar Component - Wrapped with React.memo
const Sidebar = React.memo(({ activeSection, onSectionChange, userPlan, onUpgrade, isAdmin, onFeedback, isMobileOpen, onCloseMobile }) => {
  // Helper to check if feature is unlocked
  const isPaidUser = userPlan === 'basic' || userPlan === 'premium' || userPlan === 'brokerage';
  const isPremiumUser = userPlan === 'premium' || userPlan === 'brokerage';
  
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, locked: false },
    { id: 'generate', label: 'Generate Listing', icon: Sparkles, locked: false },
    { id: 'my-listings', label: 'My Listings', icon: FileText, locked: !isPaidUser }, // Basic+ can save listings
    { id: 'ai-tools', label: 'AI Marketing Tools', icon: Zap, locked: false },
    { id: 'feature-sheet', label: 'Feature Sheet', icon: FileText, locked: !isPremiumUser }, // Premium only
    { id: 'settings', label: 'Settings', icon: Settings, locked: false },
    // Admin Dashboard - only visible to admins
    ...(isAdmin ? [{ id: 'admin', label: 'Admin Dashboard', icon: BarChart2, locked: false, isAdmin: true }] : []),
  ];

  // AI Tools submenu items - Basic gets social media, Premium gets everything
  const aiToolsSubMenu = [
    { id: 'social', label: 'Social Media', icon: Share, locked: !isPaidUser }, // Basic+ (included in Basic plan)
    { id: 'hashtags', label: 'Hashtags', icon: Hash, locked: !isPaidUser }, // Basic+ (part of social media)
    { id: 'variations', label: 'A/B Variations', icon: Copy, locked: !isPremiumUser }, // Premium only
    { id: 'qrcode', label: 'QR Code', icon: QrCode, locked: !isPremiumUser }, // Premium only
    { id: 'highlight', label: 'Highlight Cards', icon: ImageIcon, locked: !isPremiumUser }, // Premium only
    { id: 'video', label: 'Video Scripts', icon: Video, locked: !isPremiumUser }, // Premium only
    { id: 'clientcomm', label: 'Client Emails', icon: Mail, locked: !isPremiumUser }, // Premium only
    { id: 'compare', label: 'Compare Areas', icon: MapPin, locked: !isPremiumUser }, // Premium only
    { id: 'openhouse', label: 'Open House', icon: Home, locked: !isPremiumUser }, // Premium only
    { id: 'pricing', label: 'Pricing', icon: DollarSign, locked: !isPremiumUser }, // Premium only
    { id: 'seo', label: 'SEO Analyzer', icon: Search, locked: !isPremiumUser }, // Premium only
    { id: 'translate', label: 'Translate', icon: Globe, locked: !isPremiumUser }, // Premium only
    { id: 'persona', label: 'Buyer Persona', icon: Users, locked: !isPremiumUser }, // Premium only
    { id: 'staging', label: 'Virtual Staging', icon: Sofa, locked: !isPremiumUser }, // Premium only
  ];

  // Check if current section is an AI tool - auto expand submenu
  const aiToolIds = aiToolsSubMenu.map(t => t.id);
  const isAIToolActive = aiToolIds.includes(activeSection);
  
  const [showAISubMenu, setShowAISubMenu] = useState(isAIToolActive);
  
  // Auto-expand submenu when an AI tool is active
  React.useEffect(() => {
    if (isAIToolActive && !showAISubMenu) {
      setShowAISubMenu(true);
    }
  }, [activeSection, isAIToolActive]);

  // Handle menu item click - close mobile sidebar after selection
  const handleMenuClick = (id) => {
    onSectionChange(id);
    if (onCloseMobile) onCloseMobile();
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onCloseMobile}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed left-0 top-0 h-screen w-64 text-white z-50 flex flex-col
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
      `} style={{ background: 'linear-gradient(180deg, #5B21B6 0%, #4C1D95 40%, #3730A3 70%, #1E3A8A 100%)', paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        {/* Close button for mobile */}
        <button 
          onClick={onCloseMobile}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/10 lg:hidden"
          style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="GeniusList" className="w-14 h-14 rounded-2xl object-contain" />
          <div>
            <h1 className="text-xl font-bold">GeniusList</h1>
            <p className="text-xs text-white/70">AI Real Estate</p>
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isAITools = item.id === 'ai-tools';
          // AI Tools header is "active" when any sub-tool is selected
          const isActive = isAITools ? isAIToolActive : activeSection === item.id;
          
          return (
            <div key={item.id}>
              <button
                onClick={() => {
                  if (isAITools) {
                    // Just toggle the submenu, don't navigate to a page
                    setShowAISubMenu(!showAISubMenu);
                  } else if (!item.locked) {
                    handleMenuClick(item.id);
                  }
                }}
                disabled={item.locked && !isAITools}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-2xl transition-all text-left relative overflow-hidden
                  ${isActive 
                    ? 'text-white font-semibold' 
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }
                  ${item.locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
                style={isActive ? {
                  background: 'linear-gradient(90deg, rgba(34, 211, 238, 0.4) 0%, rgba(59, 130, 246, 0.3) 40%, transparent 100%)',
                  boxShadow: '0 0 25px rgba(34, 211, 238, 0.5), inset 0 0 15px rgba(34, 211, 238, 0.15)'
                } : {}}
              >
                {/* Cyan glow effect for active item */}
                {isActive && (
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-1.5 rounded-r-full"
                    style={{ background: 'linear-gradient(180deg, #22D3EE 0%, #06B6D4 100%)', boxShadow: '0 0 15px #22D3EE, 0 0 30px #22D3EE' }}
                  />
                )}
                <Icon className={`w-5 h-5 ${isActive ? 'text-cyan-300' : ''}`} />
                <span className="font-medium text-sm">{item.label}</span>
                {item.locked && <Lock className="w-4 h-4 ml-auto" />}
                {isAITools && (
                  <svg className={`w-4 h-4 ml-auto transition-transform ${showAISubMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>
              
              {/* AI Tools Submenu */}
              {isAITools && showAISubMenu && (
                <div className="ml-4 mt-1 space-y-1">
                  {aiToolsSubMenu.map((subItem) => {
                    const SubIcon = subItem.icon;
                    const isSubActive = activeSection === subItem.id;
                    return (
                      <button
                        key={subItem.id}
                        onClick={() => !subItem.locked && handleMenuClick(subItem.id)}
                        disabled={subItem.locked}
                        className={`
                          w-full flex items-center gap-3 px-4 py-2 rounded-xl transition-all text-left text-sm relative
                          ${isSubActive 
                            ? 'text-white font-medium' 
                            : 'text-white/60 hover:bg-white/10 hover:text-white'
                          }
                          ${subItem.locked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                        style={isSubActive ? {
                          background: 'linear-gradient(90deg, rgba(34, 211, 238, 0.3) 0%, transparent 100%)',
                        } : {}}
                      >
                        <SubIcon className={`w-4 h-4 ${isSubActive ? 'text-cyan-300' : ''}`} />
                        <span>{subItem.label}</span>
                        {subItem.locked && <Lock className="w-3 h-3 ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Feedback Button - Always visible */}
      <div className="px-4 pb-2">
        <button
          onClick={onFeedback}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-white/70 hover:bg-white/10 hover:text-white transition-all text-left"
          data-testid="feedback-button"
        >
          <MessageSquare className="w-5 h-5" />
          <span className="font-medium text-sm">Send Feedback</span>
        </button>
      </div>

      {/* Upgrade CTA at bottom */}
      {userPlan !== 'premium' && (
        <div className="p-4 border-t border-white/10">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
            <Crown className="w-8 h-8 text-yellow-300 mb-2" />
            <h3 className="font-bold text-sm mb-1">Upgrade to Premium</h3>
            <p className="text-xs text-white/70 mb-3">Unlock all features</p>
            <button 
              onClick={onUpgrade}
              className="w-full py-2 bg-white text-purple-700 rounded-xl font-semibold text-sm hover:bg-white/90 transition-colors"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      )}
      </aside>
    </>
  );
});

// Top Bar Component
const TopBar = ({ user, onLogout, onSearch, onToggleMobileMenu }) => {
  return (
    <header className="fixed top-0 left-0 lg:left-64 right-0 z-40 shadow-lg" style={{ background: 'linear-gradient(90deg, #5B21B6 0%, #4C1D95 30%, #3730A3 70%, #1E3A8A 100%)', paddingTop: 'env(safe-area-inset-top, 0px)', height: 'calc(4rem + env(safe-area-inset-top, 0px))' }}>
      <div className="h-16 px-4 lg:px-6 flex items-center justify-between gap-4">
        {/* Mobile menu button */}
        <button 
          onClick={onToggleMobileMenu}
          className="p-2 rounded-lg hover:bg-white/10 lg:hidden"
        >
          <Menu className="w-6 h-6 text-white" />
        </button>
        
        {/* Logo for mobile */}
        <div className="flex items-center gap-2 lg:hidden">
          <Sparkles className="w-6 h-6 text-cyan-300" />
          <span className="font-bold text-white text-lg">GeniusList</span>
        </div>
        
        {/* Search Bar - hidden on mobile */}
        <div className="hidden md:block flex-1 max-w-2xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
            <input
              type="text"
              placeholder="Search properties, listings, or insights..."
              className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-transparent text-white placeholder-white/60"
            />
          </div>
        </div>

        {/* Right Side - Notifications + User */}
        <div className="flex items-center gap-4">
          {/* Notification Bell */}
          <button className="relative p-2 hover:bg-white/10 rounded-xl transition-colors">
            <div className="w-2 h-2 bg-red-400 rounded-full absolute top-1 right-1"></div>
            <Bell className="w-5 h-5 text-white" />
          </button>

          {/* User Menu */}
          {user && (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center text-white font-semibold text-sm border border-white/30">
                {user.name ? user.name[0].toUpperCase() : 'U'}
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-semibold text-white">{user.name || 'User'}</p>
                <p className="text-xs text-white/70">{user.email}</p>
              </div>
              <button onClick={onLogout} className="p-2 hover:bg-white/10 rounded-xl transition-colors" title="Sign out">
                <LogOut className="w-4 h-4 text-white" />
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

// App Header Component (legacy, keeping for reference)
const AppHeader = ({ user, userPlan, onUpgrade, onAuth, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  return (
    <header className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur-xl border-b border-slate-800" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <Home className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-50">GeniusList</h1>
              <p className="text-xs text-slate-400 -mt-0.5">AI Real Estate Marketing</p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {user && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-xl border border-slate-700">
                <div className={`w-2 h-2 rounded-full ${
                  userPlan === 'premium' ? 'bg-rose-500' : 
                  userPlan === 'basic' ? 'bg-blue-500' : 
                  'bg-slate-500'
                }`}></div>
                <span className="text-xs font-semibold text-slate-300 uppercase">
                  {userPlan} Plan
                </span>
              </div>
            )}
          </nav>

          {/* User Actions */}
          <div className="flex items-center gap-3">
            {/* Pricing/Upgrade button - always visible for non-premium users */}
            {(!user || userPlan !== 'premium') && (
              <button 
                onClick={onUpgrade}
                className="hidden md:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-blue-500 hover:to-rose-600 rounded-xl text-white text-sm font-semibold transition-all shadow-lg btn-press"
              >
                <Crown className="w-4 h-4" />
                {user ? 'Upgrade' : 'Pricing'}
              </button>
            )}
            
            {user ? (
              <>
                <div className="flex items-center gap-2">
                  <span className="hidden sm:inline text-sm text-slate-300">{user.email}</span>
                  <button 
                    onClick={onLogout}
                    className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl text-slate-300 hover:text-slate-50 text-sm transition-all"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              </>
            ) : (
              <button 
                onClick={onAuth}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-xl text-slate-200 text-sm font-medium transition-all"
              >
                Sign In
              </button>
            )}
            
            {/* Mobile menu button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-slate-400 hover:text-slate-50"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-800">
            <div className="flex flex-col gap-3">
              {user && (
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-xl">
                  <div className={`w-2 h-2 rounded-full ${
                    userPlan === 'premium' ? 'bg-rose-500' : 
                    userPlan === 'basic' ? 'bg-blue-500' : 
                    'bg-slate-500'
                  }`}></div>
                  <span className="text-sm text-slate-300">{userPlan.charAt(0).toUpperCase() + userPlan.slice(1)} Plan</span>
                </div>
              )}
              {user && userPlan !== 'premium' && (
                <button 
                  onClick={() => { onUpgrade(); setMobileMenuOpen(false); }}
                  className="mx-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl text-white text-sm font-semibold"
                >
                  <Crown className="w-4 h-4 inline mr-2" />
                  Upgrade to Premium
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// Feature Tabs Navigation Component
const FeatureTabs = ({ activeTab, onTabChange, userPlan, onUpgrade }) => {
  // Helper to check if feature is unlocked
  const isPaidUser = userPlan === 'basic' || userPlan === 'premium' || userPlan === 'brokerage';
  const isPremiumUser = userPlan === 'premium' || userPlan === 'brokerage';
  
  const primaryTabs = [
    { id: 'generate', label: 'Generate', icon: Sparkles, locked: false },
    { id: 'social', label: 'Social Media', icon: Share, locked: !isPaidUser }, // Basic+ 
    { id: 'hashtags', label: 'Hashtags', icon: Hash, locked: !isPaidUser }, // Basic+
    { id: 'feature-sheet', label: 'Feature Sheet', icon: FileText, locked: !isPremiumUser }, // Premium
    { id: 'variations', label: 'A/B Variations', icon: Copy, locked: !isPremiumUser }, // Premium
    { id: 'qrcode', label: 'QR Code', icon: QrCode, locked: !isPremiumUser }, // Premium
    { id: 'highlight', label: 'Highlight Cards', icon: ImageIcon, locked: !isPremiumUser }, // Premium
    { id: 'clientcomm', label: 'Client Emails', icon: Mail, locked: !isPremiumUser }, // Premium
  ];

  const moreTabs = [
    { id: 'compare', label: 'Compare Areas', icon: MapPin, locked: !isPremiumUser }, // Premium
    { id: 'openhouse', label: 'Open House', icon: HomeIcon, locked: !isPremiumUser }, // Premium
    { id: 'pricing', label: 'Pricing', icon: DollarSign, locked: !isPremiumUser }, // Premium
    { id: 'video', label: 'Video Scripts', icon: Video, locked: !isPremiumUser }, // Premium
    { id: 'seo', label: 'SEO Analyzer', icon: Search, locked: !isPremiumUser }, // Premium
    { id: 'translate', label: 'Translate', icon: Globe, locked: !isPremiumUser }, // Premium
    { id: 'staging', label: 'Virtual Staging', icon: Sofa, locked: !isPremiumUser }, // Premium
    { id: 'persona', label: 'Buyer Persona', icon: Users, locked: !isPremiumUser }, // Premium
  ];

  const allTabs = [...primaryTabs, ...moreTabs];

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-3 border border-slate-700 mb-6">
      <div className="overflow-x-auto scrollbar-thin">
        <div className="flex flex-wrap gap-2">
          {allTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => tab.locked ? onUpgrade() : onTabChange(tab.id)}
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-xl font-medium text-xs sm:text-sm transition-all whitespace-nowrap
                  ${activeTab === tab.id 
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg' 
                    : 'text-slate-400 hover:text-slate-50 hover:bg-slate-700/50'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                {tab.locked && (
                  <Lock className="w-3 h-3 text-amber-400" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Plan Badge Component
const PlanBadge = ({ plan }) => {
  const configs = {
    free: { bg: 'bg-slate-600/20', border: 'border-slate-500/30', text: 'text-slate-300', dot: 'bg-slate-500' },
    basic: { bg: 'bg-blue-600/20', border: 'border-blue-500/30', text: 'text-blue-300', dot: 'bg-blue-500' },
    premium: { bg: 'bg-rose-600/20', border: 'border-rose-500/30', text: 'text-rose-300', dot: 'bg-rose-500' }
  };
  const config = configs[plan] || configs.free;

  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 ${config.bg} border ${config.border} rounded-xl animate-scale-in`}>
      {plan === 'premium' && <Crown className={`w-4 h-4 ${config.text}`} />}
      {plan === 'basic' && <Zap className={`w-4 h-4 ${config.text}`} />}
      <span className={`text-sm font-bold ${config.text} uppercase tracking-wide`}>
        {plan}
      </span>
    </div>
  );
};

// Loading Spinner Component
const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4 border-2',
    md: 'w-8 h-8 border-3',
    lg: 'w-12 h-12 border-4'
  };

  return (
    <div className={`${sizeClasses[size]} border-blue-500 border-t-transparent rounded-full animate-rotate ${className}`}></div>
  );
};

// Skeleton Loader Component
const SkeletonLoader = ({ lines = 3, className = '' }) => (
  <div className={`space-y-3 ${className}`}>
    {[...Array(lines)].map((_, i) => (
      <div 
        key={i} 
        className={`h-4 bg-slate-700 rounded skeleton`}
        style={{ width: `${100 - (i * 10)}%`, animationDelay: `${i * 0.1}s` }}
      />
    ))}
  </div>
);

// Toast Notification Component
const Toast = ({ message, type = 'success', onClose }) => {
  const typeClasses = {
    success: 'toast-success',
    error: 'toast-error',
    info: 'toast-info',
    warning: 'toast-warning'
  };

  const icons = {
    success: <Check className="w-5 h-5" />,
    error: <X className="w-5 h-5" />,
    info: <Sparkles className="w-5 h-5" />,
    warning: <Zap className="w-5 h-5" />
  };

  return (
    <div className={`toast ${typeClasses[type]}`}>
      {icons[type]}
      <span className="font-medium">{message}</span>
      <button onClick={onClose} className="ml-4 hover:opacity-80 transition-opacity">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// Enhanced Copy Button Component
const CopyButton = ({ text, className = '' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className={`
        flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm transition-all btn-press
        ${copied 
          ? 'bg-green-600 text-white glow-success' 
          : 'bg-slate-700 hover:bg-slate-600 text-slate-200 border border-slate-600'
        }
        ${className}
      `}
    >
      {copied ? (
        <>
          <Check className="w-4 h-4" />
          Copied!
        </>
      ) : (
        <>
          <Copy className="w-4 h-4" />
          Copy
        </>
      )}
    </button>
  );
};

// Progress Bar Component
const ProgressBar = ({ progress, className = '' }) => (
  <div className={`progress-bar ${className}`}>
    <div 
      className="progress-bar-fill"
      style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
    />
  </div>
);

// Animated Card Component
const AnimatedCard = ({ children, className = '', delay = 0 }) => (
  <div 
    className={`animate-slide-in-up ${className}`}
    style={{ animationDelay: `${delay}s` }}
  >
    {children}
  </div>
);

// Hero Section for Non-Logged Users
const HeroSection = ({ onGetStarted }) => {
  const [stats, setStats] = useState({ listings: 0, agents: 0, saved: 0 });
  
  useEffect(() => {
    // Animate counters
    const targets = { listings: 10547, agents: 2834, saved: 45200 };
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      setStats({
        listings: Math.floor(targets.listings * progress),
        agents: Math.floor(targets.agents * progress),
        saved: Math.floor(targets.saved * progress)
      });
      if (step >= steps) clearInterval(timer);
    }, interval);
    
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="hero-section mb-8">
      <div className="relative z-10">
        <h1 className="hero-title mb-4">AI-Powered Real Estate Marketing</h1>
        <p className="text-xl text-slate-300 mb-6 max-w-2xl">
          Generate stunning property descriptions, marketing materials, and social content in seconds. 
          Stand out from the competition with AI that understands real estate.
        </p>
        <button 
          onClick={onGetStarted}
          className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl text-white font-bold text-lg hover:scale-105 transition-transform shadow-xl btn-premium"
        >
          Get Started Free
        </button>
        
        <div className="hero-stats">
          <div className="hero-stat">
            <div className="hero-stat-value">{stats.listings.toLocaleString()}+</div>
            <div className="text-slate-400 text-sm">Listings Generated</div>
          </div>
          <div className="hero-stat">
            <div className="hero-stat-value">{stats.agents.toLocaleString()}+</div>
            <div className="text-slate-400 text-sm">Agents Trust Us</div>
          </div>
          <div className="hero-stat">
            <div className="hero-stat-value">{Math.floor(stats.saved / 1000)}K+</div>
            <div className="text-slate-400 text-sm">Hours Saved</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Floating Action Button
const FloatingActionButton = ({ onClick, isReady, isGenerating }) => {
  const [isVisible, setIsVisible] = useState(true);
  
  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY < 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <button 
      onClick={onClick}
      disabled={!isReady || isGenerating}
      className={`fab ${isReady && !isGenerating ? 'ready' : ''} ${!isReady ? 'opacity-50' : ''}`}
      title="Generate Description"
    >
      {isGenerating ? (
        <LoadingSpinner size="sm" className="border-white border-t-transparent" />
      ) : (
        <Zap className="w-6 h-6" />
      )}
    </button>
  );
};

// Progress Tracker
const ProgressTracker = ({ formData }) => {
  const steps = [
    { id: 'type', label: 'Property Type', completed: !!formData.propertyType },
    { id: 'location', label: 'Location', completed: !!formData.address },
    { id: 'details', label: 'Details', completed: !!(formData.bedrooms && formData.bathrooms) },
    { id: 'features', label: 'Features', completed: !!formData.keyFeatures }
  ];
  
  const completedCount = steps.filter(s => s.completed).length;
  const progress = (completedCount / steps.length) * 100;

  return (
    <div className="progress-tracker">
      {steps.map((step, index) => (
        <React.Fragment key={step.id}>
          <div className="progress-step">
            <div className={`progress-step-circle ${step.completed ? 'completed' : completedCount === index ? 'active' : 'pending'}`}>
              {step.completed ? <Check className="w-4 h-4" /> : index + 1}
            </div>
            <span className={`text-sm hidden sm:inline ${step.completed ? 'text-slate-200' : 'text-slate-500'}`}>
              {step.label}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div className="progress-line">
              <div className="progress-line-fill" style={{ width: step.completed ? '100%' : '0%' }} />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Circular Progress Component
const CircularProgress = ({ value, label }) => {
  const radius = 32;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div className="circular-progress">
      <svg width="80" height="80">
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3B82F6" />
            <stop offset="100%" stopColor="#BE123C" />
          </linearGradient>
        </defs>
        <circle className="circular-progress-bg" cx="40" cy="40" r={radius} />
        <circle 
          className="circular-progress-bar" 
          cx="40" cy="40" r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="circular-progress-text">{value}%</div>
    </div>
  );
};

// Empty State Component
const EmptyState = ({ icon: Icon, title, description, suggestions, onSuggestionClick }) => (
  <div className="empty-state">
    <div className="empty-state-icon">
      <Icon className="w-10 h-10 text-blue-400" />
    </div>
    <h3 className="empty-state-title">{title}</h3>
    <p className="empty-state-description">{description}</p>
    {suggestions && (
      <div className="empty-state-suggestions">
        {suggestions.map((suggestion, idx) => (
          <button 
            key={idx} 
            className="empty-state-suggestion"
            onClick={() => onSuggestionClick && onSuggestionClick(suggestion)}
          >
            {suggestion}
          </button>
        ))}
      </div>
    )}
  </div>
);

// AI Marketing Tools Component - All features in one place
const AIMarketingTools = ({ userPlan, generatedDescription, formData, userId, API }) => {
  const [activeToolTab, setActiveToolTab] = useState('variations');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState({});
  const [copied, setCopied] = useState(false);
  const [savedListings, setSavedListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [localFormData, setLocalFormData] = useState(formData || {});

  // Fetch saved listings on mount
  useEffect(() => {
    if (userId && API) {
      fetchSavedListings();
    }
  }, [userId, API]);

  const fetchSavedListings = async () => {
    try {
      const res = await axios.get(`${API}/my-listings?user_id=${userId}`);
      setSavedListings(res.data.listings || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    }
  };

  const handleListingSelect = (listingId) => {
    if (!listingId) {
      setSelectedListing(null);
      setLocalFormData(formData || {});
      return;
    }
    
    const listing = savedListings.find(l => l.id === listingId);
    if (listing) {
      setSelectedListing(listing);
      setLocalFormData({
        propertyType: listing.property_type || '',
        address: `${listing.address}, ${listing.city}, ${listing.state} ${listing.zip_code}`,
        beds: listing.bedrooms?.toString() || '',
        bedrooms: listing.bedrooms?.toString() || '',
        baths: listing.bathrooms?.toString() || '',
        bathrooms: listing.bathrooms?.toString() || '',
        sqft: listing.square_footage?.toString() || '',
        squareFootage: listing.square_footage?.toString() || '',
        price: listing.price ? `$${listing.price.toLocaleString()}` : '',
        keyFeatures: listing.key_features || [],
        neighborhood: '',
        neighborhoodHighlights: '',
        description: listing.description || ''
      });
    }
  };

  // Helper function to build description from formData when none exists
  const buildDescriptionFromFormData = (data) => {
    if (!data) return 'A beautiful property available for sale.';
    
    const parts = [];
    if (data.propertyType) parts.push(`${data.propertyType}`);
    if (data.address) parts.push(`located at ${data.address}`);
    if (data.beds || data.bedrooms) parts.push(`with ${data.beds || data.bedrooms} bedrooms`);
    if (data.baths || data.bathrooms) parts.push(`and ${data.baths || data.bathrooms} bathrooms`);
    if (data.sqft || data.squareFootage) parts.push(`spanning ${data.sqft || data.squareFootage} square feet`);
    if (data.price) parts.push(`priced at ${data.price}`);
    if (data.keyFeatures) {
      const features = Array.isArray(data.keyFeatures) ? data.keyFeatures.join(', ') : data.keyFeatures;
      if (features) parts.push(`featuring ${features}`);
    }
    if (data.neighborhood || data.neighborhoodHighlights) {
      parts.push(`in a great location ${data.neighborhood || data.neighborhoodHighlights}`);
    }
    
    return parts.length > 0 
      ? `Beautiful ${parts.join(' ')}. This property offers an excellent opportunity for buyers.`
      : 'A beautiful property available for sale with excellent features and great location.';
  };

  // Use localFormData instead of formData for all operations
  const activeFormData = selectedListing ? localFormData : (formData || localFormData);

  const tools = [
    { id: 'variations', label: 'A/B Variations', icon: Hash },
    { id: 'qrcode', label: 'QR Code', icon: QrCode },
    { id: 'hashtags', label: 'Hashtags', icon: Hash },
    { id: 'highlights', label: 'Highlight Cards', icon: Sparkles },
    { id: 'emails', label: 'Client Emails', icon: Mail },
    { id: 'compare', label: 'Compare Areas', icon: MapPin },
    { id: 'video', label: 'Video Scripts', icon: Video },
    { id: 'social', label: 'Social Media', icon: Share },
    { id: 'seo', label: 'SEO Analyzer', icon: Search },
    { id: 'translate', label: 'Translate', icon: Globe },
    { id: 'staging', label: 'Virtual Staging', icon: ImageIcon },
    { id: 'persona', label: 'Buyer Persona', icon: User },
    { id: 'print', label: 'Print Marketing', icon: FileText },
    { id: 'openhouse', label: 'Open House', icon: Home },
  ];

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const generateTool = async (toolId) => {
    // Build description from formData if no generatedDescription exists
    const description = generatedDescription || buildDescriptionFromFormData(activeFormData);
    
    setIsLoading(true);
    try {
      let endpoint = '';
      let payload = { description, ...activeFormData };
      
      switch(toolId) {
        case 'variations':
          endpoint = '/premium/description-variations';
          payload = {
            propertyType: activeFormData?.propertyType || 'House',
            address: activeFormData?.address || '123 Main Street',
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '3',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '2',
            squareFootage: activeFormData?.sqft || activeFormData?.squareFootage || '2000',
            price: activeFormData?.price || '$500,000',
            keyFeatures: Array.isArray(activeFormData?.keyFeatures) ? activeFormData.keyFeatures.join(', ') : (activeFormData?.keyFeatures || 'Modern Kitchen, Hardwood Floors'),
            neighborhoodHighlights: activeFormData?.neighborhood || activeFormData?.neighborhoodHighlights || 'Great location'
          };
          break;
        case 'qrcode':
          endpoint = '/premium/qr-code';
          payload = { 
            listing_url: `https://geniuslist.com/listing/${Date.now()}`,
            property_address: activeFormData?.address || '123 Main Street'
          };
          break;
        case 'hashtags':
          endpoint = '/premium/hashtags';
          payload = { 
            propertyType: activeFormData?.propertyType || 'House',
            location: activeFormData?.address || 'Prime Location',
            features: Array.isArray(activeFormData?.keyFeatures) ? activeFormData.keyFeatures.join(', ') : (activeFormData?.keyFeatures || 'Modern, Spacious'),
            platform: 'instagram'
          };
          break;
        case 'video':
          endpoint = '/premium/video-script';
          payload = { 
            propertyType: activeFormData?.propertyType || 'House',
            address: activeFormData?.address || '123 Main Street',
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '3',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '2',
            squareFootage: activeFormData?.sqft || activeFormData?.squareFootage || '2000',
            price: activeFormData?.price || '$500,000',
            keyFeatures: Array.isArray(activeFormData?.keyFeatures) ? activeFormData.keyFeatures.join(', ') : (activeFormData?.keyFeatures || 'Modern Kitchen'),
            scriptType: 'walkthrough'
          };
          break;
        case 'social':
          endpoint = '/premium/social-media';
          payload = { 
            description,
            address: activeFormData?.address || '123 Main Street',
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '3',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '2',
            squareFootage: activeFormData?.sqft || activeFormData?.squareFootage || '2000',
            price: activeFormData?.price || '$500,000',
            propertyType: activeFormData?.propertyType || 'House',
            platforms: ['instagram', 'facebook', 'linkedin', 'twitter', 'tiktok'],
            imageAnalysis: activeFormData?.keyFeatures ? (Array.isArray(activeFormData.keyFeatures) ? activeFormData.keyFeatures.join(', ') : activeFormData.keyFeatures) : '',
            imageCount: 0
          };
          break;
        case 'seo':
          endpoint = '/premium/seo-analysis';
          payload = { 
            address: activeFormData?.address || '',
            propertyType: activeFormData?.propertyType || 'Home',
            description: description || '',
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '',
            price: activeFormData?.price || '',
            features: Array.isArray(activeFormData?.keyFeatures) ? activeFormData.keyFeatures : [],
            city: activeFormData?.city || ''
          };
          break;
        case 'translate':
          endpoint = '/premium/translate';
          payload = { 
            description: description || '',
            address: activeFormData?.address || '',
            price: activeFormData?.price || '',
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '',
            features: Array.isArray(activeFormData?.keyFeatures) ? activeFormData.keyFeatures : [],
            propertyType: activeFormData?.propertyType || 'Home',
            languages: ['Spanish', 'French', 'Chinese', 'German', 'Portuguese']
          };
          break;
        case 'persona':
          endpoint = '/premium/buyer-persona';
          payload = { 
            description,
            personaType: 'first_time',
            propertyType: activeFormData?.propertyType || 'House',
            price: activeFormData?.price || '$500,000'
          };
          break;
        case 'emails':
          endpoint = '/premium/client-templates';
          payload = { 
            templateType: 'just_listed',
            clientName: 'Valued Client',
            propertyAddress: activeFormData?.address || '123 Main Street',
            agentName: 'Your Agent',
            agentPhone: '(555) 123-4567'
          };
          break;
        case 'highlights':
          endpoint = '/premium/highlight-card';
          payload = { 
            propertyType: activeFormData?.propertyType || 'Home',
            address: activeFormData?.address || '',
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '',
            squareFootage: activeFormData?.sqft || activeFormData?.squareFootage || '',
            price: activeFormData?.price || '',
            topFeatures: Array.isArray(activeFormData?.keyFeatures) ? activeFormData.keyFeatures : 
                        (activeFormData?.keyFeatures ? activeFormData.keyFeatures.split(',').map(f => f.trim()) : []),
            description: description || ''
          };
          break;
        case 'openhouse':
          endpoint = '/premium/open-house';
          payload = { 
            address: activeFormData?.address || '123 Main Street',
            date: new Date().toLocaleDateString(),
            time: '2:00 PM - 4:00 PM',
            propertyType: activeFormData?.propertyType || 'House',
            agentName: 'Your Agent',
            agentPhone: '(555) 123-4567',
            contentType: 'invitation'
          };
          break;
        case 'compare':
          endpoint = '/premium/neighborhood-compare';
          // Send all property data for intelligent comparison
          payload = { 
            primaryArea: activeFormData?.address || '',
            address: activeFormData?.address || '',
            description: description,
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '',
            squareFootage: activeFormData?.sqft || activeFormData?.squareFootage || '',
            price: activeFormData?.price || '',
            propertyType: activeFormData?.propertyType || '',
            neighborhood: activeFormData?.neighborhood || '',
            features: activeFormData?.keyFeatures || [],
            city: activeFormData?.city || '',
            state: activeFormData?.state || '',
            zipCode: activeFormData?.zipCode || '',
            country: activeFormData?.country || 'USA'
          };
          break;
        case 'staging':
          endpoint = '/premium/staging-suggestions';
          payload = { images: [], room_type: 'living_room' };
          break;
        case 'print':
          endpoint = '/premium/print-marketing';
          payload = { 
            description,
            address: activeFormData?.address || '123 Main Street',
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '3',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '2',
            price: activeFormData?.price || '$500,000',
            city: activeFormData?.city || '',
            state: activeFormData?.state || '',
            country: activeFormData?.country || 'USA',
            format: 'flyer'
          };
          break;
        case 'pricing':
          endpoint = '/premium/pricing-suggestions';
          // Send all property data for intelligent pricing analysis
          payload = { 
            address: activeFormData?.address || '',
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '',
            squareFootage: activeFormData?.sqft || activeFormData?.squareFootage || '',
            sqft: activeFormData?.sqft || activeFormData?.squareFootage || '',
            propertyType: activeFormData?.propertyType || '',
            features: activeFormData?.keyFeatures || [],
            description: description,
            price: activeFormData?.price || '',
            neighborhood: activeFormData?.neighborhood || '',
            region: activeFormData?.address || '',
            city: activeFormData?.city || '',
            state: activeFormData?.state || '',
            zipCode: activeFormData?.zipCode || '',
            country: activeFormData?.country || 'USA'
          };
          break;
        default:
          // Mock response for tools without backend endpoints
          setResults(prev => ({
            ...prev,
            [toolId]: { success: true, data: `${toolId} feature generated successfully!` }
          }));
          setIsLoading(false);
          return;
      }
      
      const res = await axios.post(`${API}${endpoint}?user_id=${userId}`, payload);
      setResults(prev => ({ ...prev, [toolId]: res.data }));
    } catch (error) {
      console.error(`Error with ${toolId}:`, error);
      // Provide mock data on error for demo
      setResults(prev => ({
        ...prev,
        [toolId]: { success: true, data: getMockData(toolId) }
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const getMockData = (toolId) => {
    const mockData = {
      variations: {
        variations: [
          { angle: 'Luxury', description: 'Experience unparalleled elegance in this stunning residence featuring premium finishes and sophisticated design elements...' },
          { angle: 'Family', description: 'Perfect for growing families, this spacious home offers plenty of room for everyone with a large backyard and nearby schools...' },
          { angle: 'Investor', description: 'Prime investment opportunity with strong rental potential and excellent location near major employment centers...' }
        ]
      },
      hashtags: {
        hashtags: {
          instagram: ['#DreamHome', '#LuxuryLiving', '#RealEstate', '#HomeForSale', '#JustListed', '#PropertyGoals', '#HouseHunting', '#NewListing'],
          twitter: ['#RealEstate', '#HomesForSale', '#Property', '#Investment'],
          linkedin: ['#RealEstate', '#PropertyInvestment', '#LuxuryHomes']
        }
      },
      video: {
        script: "OPENING SHOT: Aerial view of the property\n\nNARRATOR: Welcome to your dream home...\n\nCUT TO: Front entrance\n\nNARRATOR: Step inside and discover...\n\n[Continue with interior shots]\n\nCLOSING: Contact information overlay"
      },
      social: {
        posts: {
          instagram: "✨ Just Listed! ✨\n\nThis stunning property won't last long! Features include modern kitchen, spacious living areas, and a beautiful backyard perfect for entertaining.\n\n📍 Prime location\n🏠 Move-in ready\n💎 Premium finishes\n\nDM for details! 🏡",
          facebook: "🏠 NEW LISTING ALERT! 🏠\n\nCheck out this amazing property that just hit the market! Perfect for families looking for their forever home...",
          linkedin: "Excited to present this exceptional property to the market. A perfect blend of luxury and functionality...",
          twitter: "🏡 Just Listed! Beautiful home with stunning features. Perfect location, move-in ready. Contact me for a private showing! #RealEstate #JustListed"
        }
      },
      seo: {
        analysis: {
          score: 85,
          keywords: ['luxury home', 'modern kitchen', 'spacious', 'prime location'],
          suggestions: ['Add more location-specific keywords', 'Include neighborhood amenities', 'Mention nearby schools']
        }
      },
      translate: {
        translations: {
          es: 'Bienvenido a su hogar soñado. Esta impresionante propiedad ofrece...',
          fr: 'Bienvenue dans votre maison de rêve. Cette propriété exceptionnelle offre...',
          zh: '欢迎来到您的梦想之家。这座令人惊叹的房产提供...'
        }
      },
      persona: {
        persona: {
          name: 'The Young Professional',
          age: '28-35',
          income: '$100,000-$150,000',
          interests: ['Career growth', 'Urban living', 'Modern design'],
          buyingMotivation: 'First-time homebuyer looking for a property that reflects their success'
        }
      },
      emails: {
        emails: {
          subject: 'Just Listed: Your Dream Home Awaits!',
          body: 'Dear [Client Name],\n\nI\'m excited to share this exclusive new listing that perfectly matches what you\'ve been looking for...'
        }
      },
      highlights: {
        cards: [
          { title: 'Modern Kitchen', description: 'Gourmet kitchen with premium appliances' },
          { title: 'Prime Location', description: 'Minutes from downtown and top-rated schools' },
          { title: 'Outdoor Living', description: 'Beautifully landscaped backyard' }
        ]
      },
      openhouse: {
        materials: {
          signInSheet: 'Digital sign-in ready',
          brochure: 'Property brochure generated',
          followUp: 'Follow-up email template ready'
        }
      },
      compare: {
        comparison: {
          areas: ['Property Area', 'Nearby Area 1', 'Nearby Area 2'],
          metrics: { avgPrice: ['$XXX,XXX', '$XXX,XXX', '$XXX,XXX'], schools: ['X/10', 'X/10', 'X/10'], commute: ['XX min', 'XX min', 'XX min'], lifestyle: ['Add property details', 'For real data', 'Fill address'] }
        }
      },
      pricing: {
        analysis: {
          suggestedPrice: '$XXX,XXX',
          priceRange: '$XXX,XXX - $XXX,XXX',
          confidence: 'Pending',
          marketPosition: 'Fill property details'
        },
        factors: [
          { factor: 'Property Details', impact: 'Pending', details: 'Add property info for analysis' }
        ],
        recommendations: ['Fill property details in Generate Listing section']
      },
      qrcode: { url: 'QR Code generated for property link' },
      staging: { message: 'Virtual staging suggestions generated' },
      print: { message: 'Print materials template ready' },
      agent: { message: 'Agent toolkit generated' },
      mls: { message: 'MLS export format ready' }
    };
    return mockData[toolId] || { message: 'Feature content generated' };
  };

  const renderToolContent = () => {
    const result = results[activeToolTab];
    
    return (
      <div className="space-y-4">
        {/* Tool Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              {tools.find(t => t.id === activeToolTab)?.label}
            </h3>
            <p className="text-sm text-gray-500">Generate AI-powered content for your listing</p>
          </div>
          <button
            onClick={() => generateTool(activeToolTab)}
            disabled={isLoading}
            className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium rounded-xl transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Generate
              </>
            )}
          </button>
        </div>

        {/* Results Display */}
        {result ? (
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            {activeToolTab === 'variations' && result.variations && (
              <div className="space-y-4">
                {result.variations.map((v, i) => (
                  <div key={i} className="bg-white p-4 rounded-xl border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-purple-600">{v.angle} Version</span>
                      <button onClick={() => copyToClipboard(v.description)} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1">
                        <Copy className="w-4 h-4" /> Copy
                      </button>
                    </div>
                    <p className="text-gray-700 text-sm">{v.description}</p>
                  </div>
                ))}
              </div>
            )}

            {activeToolTab === 'hashtags' && result.hashtags && (
              <div className="space-y-4">
                {Object.entries(result.hashtags).map(([platform, tags]) => (
                  <div key={platform} className="bg-white p-4 rounded-xl border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold capitalize text-gray-800">{platform}</span>
                      <button onClick={() => copyToClipboard(tags.join(' '))} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1">
                        <Copy className="w-4 h-4" /> Copy All
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag, i) => (
                        <span key={i} className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm">{tag}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeToolTab === 'video' && result.script && (
              <div className="bg-white p-4 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-gray-800">Video Script</span>
                  <button onClick={() => copyToClipboard(result.script)} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1">
                    <Copy className="w-4 h-4" /> Copy
                  </button>
                </div>
                <pre className="text-gray-700 text-sm whitespace-pre-wrap font-sans">{result.script}</pre>
              </div>
            )}

            {activeToolTab === 'social' && (result.posts || result.instagram || result.facebook) && (
              <div className="space-y-4">
                {/* Handle both formats: result.posts object OR direct platform properties */}
                {result.posts ? (
                  Object.entries(result.posts).map(([platform, post]) => (
                    <div key={platform} className="bg-white p-4 rounded-xl border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold capitalize text-gray-800">{platform}</span>
                        <button onClick={() => copyToClipboard(post)} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1">
                          <Copy className="w-4 h-4" /> Copy
                        </button>
                      </div>
                      <p className="text-gray-700 text-sm whitespace-pre-wrap">{post}</p>
                    </div>
                  ))
                ) : (
                  ['instagram', 'facebook', 'twitter', 'linkedin'].map((platform) => (
                    result[platform] && (
                      <div key={platform} className="bg-white p-4 rounded-xl border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold capitalize text-gray-800 flex items-center gap-2">
                            {platform === 'instagram' && '📸'}
                            {platform === 'facebook' && '📘'}
                            {platform === 'twitter' && '🐦'}
                            {platform === 'linkedin' && '💼'}
                            {platform}
                          </span>
                          <button onClick={() => copyToClipboard(result[platform])} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1">
                            <Copy className="w-4 h-4" /> Copy
                          </button>
                        </div>
                        <p className="text-gray-700 text-sm whitespace-pre-wrap">{result[platform]}</p>
                      </div>
                    )
                  ))
                )}
                {/* Hashtags section */}
                {result.hashtags && result.hashtags.length > 0 && (
                  <div className="bg-white p-4 rounded-xl border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-800">Hashtags</span>
                      <button onClick={() => copyToClipboard(result.hashtags.join(' '))} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1">
                        <Copy className="w-4 h-4" /> Copy All
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {result.hashtags.map((tag, i) => (
                        <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeToolTab === 'seo' && result.analysis && (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full border-4 border-green-500 flex items-center justify-center">
                    <span className="text-2xl font-bold text-green-600">{result.analysis.score}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">SEO Score</p>
                    <p className="text-sm text-gray-500">Your listing is well optimized</p>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-200">
                  <p className="font-semibold text-gray-800 mb-2">Top Keywords</p>
                  <div className="flex flex-wrap gap-2">
                    {result.analysis.keywords.map((kw, i) => (
                      <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">{kw}</span>
                    ))}
                  </div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-200">
                  <p className="font-semibold text-gray-800 mb-2">Suggestions</p>
                  <ul className="space-y-1">
                    {result.analysis.suggestions.map((s, i) => (
                      <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-500 mt-0.5" /> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {activeToolTab === 'translate' && result.translations && (
              <div className="space-y-4">
                {Object.entries(result.translations).map(([lang, text]) => (
                  <div key={lang} className="bg-white p-4 rounded-xl border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold uppercase text-gray-800">{lang}</span>
                      <button onClick={() => copyToClipboard(text)} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1">
                        <Copy className="w-4 h-4" /> Copy
                      </button>
                    </div>
                    <p className="text-gray-700 text-sm">{text}</p>
                  </div>
                ))}
              </div>
            )}

            {activeToolTab === 'persona' && result.persona && (
              <div className="bg-white p-4 rounded-xl border border-gray-200">
                <h4 className="font-semibold text-purple-600 text-lg mb-3">{result.persona.name}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div><span className="text-gray-500 text-sm">Age Range:</span><p className="font-medium">{result.persona.age}</p></div>
                  <div><span className="text-gray-500 text-sm">Income:</span><p className="font-medium">{result.persona.income}</p></div>
                </div>
                <div className="mt-4">
                  <span className="text-gray-500 text-sm">Interests:</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {result.persona.interests.map((i, idx) => (
                      <span key={idx} className="px-2 py-1 bg-purple-50 text-purple-700 rounded-xl text-sm">{i}</span>
                    ))}
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-gray-500 text-sm">Buying Motivation:</span>
                  <p className="text-gray-700">{result.persona.buyingMotivation}</p>
                </div>
              </div>
            )}

            {activeToolTab === 'emails' && result.emails && (
              <div className="bg-white p-4 rounded-xl border border-gray-200 space-y-3">
                <div>
                  <span className="text-gray-500 text-sm">Subject Line:</span>
                  <p className="font-semibold text-gray-800">{result.emails.subject}</p>
                </div>
                <div>
                  <span className="text-gray-500 text-sm">Email Body:</span>
                  <p className="text-gray-700 whitespace-pre-wrap text-sm mt-1">{result.emails.body}</p>
                </div>
                <button onClick={() => copyToClipboard(`Subject: ${result.emails.subject}\n\n${result.emails.body}`)} className="px-4 py-2 bg-purple-100 text-purple-700 rounded-xl text-sm font-medium hover:bg-purple-200 transition-colors flex items-center gap-2">
                  <Copy className="w-4 h-4" /> Copy Email
                </button>
              </div>
            )}

            {activeToolTab === 'highlights' && result.cards && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {result.cards.map((card, i) => (
                  <div key={i} className="bg-white p-4 rounded-xl border border-gray-200 text-center">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Star className="w-6 h-6 text-purple-600" />
                    </div>
                    <h4 className="font-semibold text-gray-800">{card.title}</h4>
                    <p className="text-sm text-gray-500 mt-1">{card.description}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Generic result display for other tools */}
            {!['variations', 'hashtags', 'video', 'social', 'seo', 'translate', 'persona', 'emails', 'highlights'].includes(activeToolTab) && (
              <div className="bg-white p-4 rounded-xl border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">Content Generated Successfully</p>
                    <p className="text-sm text-gray-500">{result.message || result.data || 'Your content is ready'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-gray-50 rounded-xl p-12 border border-gray-200 text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {(() => {
                const Tool = tools.find(t => t.id === activeToolTab);
                const Icon = Tool?.icon || Sparkles;
                return <Icon className="w-8 h-8 text-purple-600" />;
              })()}
            </div>
            <p className="text-gray-600">Click "Generate" to create {tools.find(t => t.id === activeToolTab)?.label.toLowerCase()} content</p>
            <p className="text-sm text-gray-400 mt-2">Make sure you have a listing description generated first</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Marketing Tools</h1>
        <p className="text-gray-600">All your marketing tools in one place</p>
      </div>

      {/* Listing Selector */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 border border-purple-200">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-purple-700 mb-1">
              <FileText className="w-4 h-4 inline mr-1" />
              Select a Listing to Auto-Fill
            </label>
            <select
              value={selectedListing?.id || ''}
              onChange={(e) => handleListingSelect(e.target.value)}
              className="w-full px-3 py-2 border border-purple-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">-- Choose from My Listings --</option>
              {savedListings.map((listing) => (
                <option key={listing.id} value={listing.id}>
                  {listing.address}, {listing.city} - ${listing.price?.toLocaleString()}
                </option>
              ))}
            </select>
          </div>
          {selectedListing && (
            <div className="flex-shrink-0 bg-white rounded-xl p-3 border border-purple-200">
              <p className="text-sm font-medium text-gray-800">{selectedListing.address}</p>
              <p className="text-xs text-gray-500">
                {selectedListing.bedrooms} bed • {selectedListing.bathrooms} bath • {selectedListing.square_footage?.toLocaleString()} sqft
              </p>
            </div>
          )}
        </div>
        {savedListings.length === 0 && (
          <p className="text-sm text-purple-600 mt-2">
            💡 Add listings in "My Listings" tab to quickly use them here!
          </p>
        )}
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Left Sidebar - Tool Navigation */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm sticky top-24">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Tools</h3>
            <nav className="space-y-1">
              {tools.map((tool) => {
                const Icon = tool.icon;
                const isActive = activeToolTab === tool.id;
                return (
                  <button
                    key={tool.id}
                    onClick={() => setActiveToolTab(tool.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all ${
                      isActive
                        ? 'bg-purple-50 text-purple-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{tool.label}</span>
                    {results[tool.id] && (
                      <div className="ml-auto w-2 h-2 bg-green-500 rounded-full"></div>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            {renderToolContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Buyer Persona Component with Full Customization
const BuyerPersonaEnhanced = ({ formData, generatedDescription, API, userId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState({});
  const [activeResultTab, setActiveResultTab] = useState('profile');
  const [savedListings, setSavedListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  
  // Persona configuration state
  const [personaConfig, setPersonaConfig] = useState({
    personaType: 'first_time',
    ageRange: '',
    incomeLevel: '',
    familyStatus: '',
    occupation: '',
    priorities: [],
    lifestyleInterests: [],
    buyingMotivation: '',
    painPoints: []
  });

  // Fetch saved listings
  useEffect(() => {
    if (userId && API) {
      fetchSavedListings();
    }
  }, [userId, API]);

  const fetchSavedListings = async () => {
    try {
      const res = await axios.get(`${API}/my-listings?user_id=${userId}`);
      setSavedListings(res.data.listings || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    }
  };

  const handleListingSelect = (listingId) => {
    if (!listingId) {
      setSelectedListing(null);
      return;
    }
    const listing = savedListings.find(l => l.id === listingId);
    setSelectedListing(listing);
  };

  const personaTypes = [
    { id: 'first_time', label: 'First-Time Buyer', icon: '🏠', desc: 'New to homeownership' },
    { id: 'luxury', label: 'Luxury Buyer', icon: '💎', desc: 'Premium properties' },
    { id: 'investor', label: 'Investor', icon: '📈', desc: 'ROI focused' },
    { id: 'downsizer', label: 'Downsizer', icon: '🏡', desc: 'Empty nesters' },
    { id: 'family', label: 'Family', icon: '👨‍👩‍👧‍👦', desc: 'Growing families' },
    { id: 'international', label: 'International', icon: '🌍', desc: 'Foreign buyers' },
    { id: 'custom', label: 'Custom', icon: '⚙️', desc: 'Build your own' }
  ];

  const ageRanges = [
    { id: '20-30', label: '20-30', desc: 'Young professionals' },
    { id: '30-40', label: '30-40', desc: 'Established careers' },
    { id: '40-50', label: '40-50', desc: 'Mid-career' },
    { id: '50-60', label: '50-60', desc: 'Pre-retirement' },
    { id: '60+', label: '60+', desc: 'Retirees' }
  ];

  const incomeLevels = [
    { id: 'under_75k', label: 'Under $75K', desc: 'Budget-focused' },
    { id: '75k_150k', label: '$75K-$150K', desc: 'Middle income' },
    { id: '150k_300k', label: '$150K-$300K', desc: 'Affluent' },
    { id: '300k_plus', label: '$300K+', desc: 'High net worth' }
  ];

  const familyStatuses = [
    { id: 'single', label: 'Single', icon: '👤' },
    { id: 'couple', label: 'Couple', icon: '👫' },
    { id: 'young_family', label: 'Young Family', icon: '👶' },
    { id: 'growing_family', label: 'Growing Family', icon: '👨‍👩‍👧‍👦' },
    { id: 'empty_nester', label: 'Empty Nester', icon: '🏠' }
  ];

  const occupations = [
    { id: 'professional', label: 'Professional' },
    { id: 'remote_worker', label: 'Remote Worker' },
    { id: 'business_owner', label: 'Business Owner' },
    { id: 'retiree', label: 'Retiree' },
    { id: 'healthcare', label: 'Healthcare' },
    { id: 'tech', label: 'Tech Industry' },
    { id: 'education', label: 'Education' }
  ];

  const priorityOptions = [
    { id: 'price', label: 'Price/Value', icon: '💰' },
    { id: 'location', label: 'Location', icon: '📍' },
    { id: 'size', label: 'Size/Space', icon: '📐' },
    { id: 'schools', label: 'Schools', icon: '🎓' },
    { id: 'commute', label: 'Commute', icon: '🚗' },
    { id: 'amenities', label: 'Amenities', icon: '✨' },
    { id: 'investment', label: 'Investment', icon: '📈' },
    { id: 'outdoor_space', label: 'Outdoor Space', icon: '🌳' }
  ];

  const lifestyleOptions = [
    { id: 'outdoor', label: 'Outdoor Activities', icon: '🏕️' },
    { id: 'entertainment', label: 'Entertainment', icon: '🎭' },
    { id: 'work_from_home', label: 'Work from Home', icon: '💻' },
    { id: 'fitness', label: 'Fitness', icon: '🏋️' },
    { id: 'pets', label: 'Pet Owner', icon: '🐕' },
    { id: 'gardening', label: 'Gardening', icon: '🌱' },
    { id: 'cooking', label: 'Cooking/Entertaining', icon: '👨‍🍳' }
  ];

  const motivationOptions = [
    { id: 'first_home', label: 'First Home', icon: '🎯' },
    { id: 'upgrade', label: 'Upgrading', icon: '⬆️' },
    { id: 'investment', label: 'Investment', icon: '💵' },
    { id: 'relocation', label: 'Relocation', icon: '✈️' },
    { id: 'retirement', label: 'Retirement', icon: '🌴' },
    { id: 'divorce', label: 'Fresh Start', icon: '🔄' },
    { id: 'growing_family', label: 'Growing Family', icon: '👶' }
  ];

  const painPointOptions = [
    { id: 'budget', label: 'Budget Constraints', icon: '💸' },
    { id: 'neighborhood_safety', label: 'Safety Concerns', icon: '🛡️' },
    { id: 'future_value', label: 'Future Value', icon: '📊' },
    { id: 'maintenance', label: 'Maintenance Worries', icon: '🔧' },
    { id: 'commute', label: 'Commute Time', icon: '⏰' },
    { id: 'space', label: 'Space Requirements', icon: '📦' }
  ];

  const toggleArrayItem = (array, item, setter, field) => {
    const newArray = array.includes(item) 
      ? array.filter(i => i !== item)
      : [...array, item];
    setter(prev => ({ ...prev, [field]: newArray }));
  };

  const handleGenerate = async () => {
    setIsLoading(true);
    setResult(null);
    
    try {
      // Build the request payload
      const listingData = selectedListing || formData || {};
      const description = selectedListing?.description || generatedDescription || 
        `${listingData.propertyType || 'Home'} with ${listingData.bedrooms || listingData.beds || '3'} bedrooms`;
      
      const payload = {
        description,
        personaType: personaConfig.personaType,
        propertyType: listingData.propertyType || listingData.property_type || 'Home',
        price: listingData.price ? (typeof listingData.price === 'number' ? `$${listingData.price.toLocaleString()}` : listingData.price) : '',
        ageRange: personaConfig.ageRange,
        incomeLevel: personaConfig.incomeLevel,
        familyStatus: personaConfig.familyStatus,
        occupation: personaConfig.occupation,
        priorities: personaConfig.priorities,
        lifestyleInterests: personaConfig.lifestyleInterests,
        buyingMotivation: personaConfig.buyingMotivation,
        painPoints: personaConfig.painPoints,
        bedrooms: listingData.bedrooms?.toString() || listingData.beds || '',
        bathrooms: listingData.bathrooms?.toString() || listingData.baths || '',
        squareFootage: listingData.square_footage?.toString() || listingData.squareFootage || listingData.sqft || '',
        address: listingData.address || '',
        keyFeatures: listingData.key_features || listingData.keyFeatures || [],
        neighborhood: listingData.neighborhood || listingData.neighborhoodHighlights || ''
      };

      const response = await axios.post(`${API}/premium/buyer-persona?user_id=${userId}`, payload);
      
      if (response.data.success) {
        setResult(response.data);
        setActiveResultTab('profile');
      } else {
        setResult({ error: response.data.error || 'Failed to generate persona analysis' });
      }
    } catch (error) {
      console.error('Error generating persona:', error);
      setResult({ error: error.response?.data?.detail || error.message || 'Failed to generate persona analysis' });
    }
    
    setIsLoading(false);
  };

  const copyToClipboard = async (text, key) => {
    await navigator.clipboard.writeText(text);
    setCopied(prev => ({ ...prev, [key]: true }));
    setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 2000);
  };

  const resultTabs = [
    { id: 'profile', label: 'Persona Profile', icon: '👤' },
    { id: 'description', label: 'Custom Listing', icon: '📝' },
    { id: 'selling', label: 'Selling Points', icon: '🎯' },
    { id: 'objections', label: 'Objection Handlers', icon: '🛡️' },
    { id: 'channels', label: 'Multi-Channel', icon: '📱' }
  ];

  return (
    <div className="space-y-6">
      {/* Listing Selection */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 border border-purple-100">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Select Property</label>
        <select
          value={selectedListing?.id || ''}
          onChange={(e) => handleListingSelect(e.target.value)}
          className="w-full p-3 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="">Use current form data</option>
          {savedListings.map(listing => (
            <option key={listing.id} value={listing.id}>
              {listing.address}, {listing.city} - ${listing.price?.toLocaleString()}
            </option>
          ))}
        </select>
        {selectedListing && (
          <div className="mt-2 text-sm text-gray-600">
            📍 {selectedListing.bedrooms}bd/{selectedListing.bathrooms}ba | {selectedListing.square_footage?.toLocaleString()} sqft
          </div>
        )}
      </div>

      {/* Persona Type Selection */}
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-purple-600" />
          Buyer Type
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {personaTypes.map(type => (
            <button
              key={type.id}
              onClick={() => setPersonaConfig(prev => ({ ...prev, personaType: type.id }))}
              className={`p-3 rounded-xl border-2 transition-all text-center ${
                personaConfig.personaType === type.id
                  ? 'border-purple-500 bg-purple-50 shadow-md'
                  : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
              }`}
            >
              <div className="text-2xl mb-1">{type.icon}</div>
              <div className="text-sm font-semibold text-gray-800">{type.label}</div>
              <div className="text-xs text-gray-500">{type.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Demographics Section */}
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          Demographics (Optional)
        </h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Age Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Age Range</label>
            <div className="flex flex-wrap gap-2">
              {ageRanges.map(age => (
                <button
                  key={age.id}
                  onClick={() => setPersonaConfig(prev => ({ 
                    ...prev, 
                    ageRange: prev.ageRange === age.id ? '' : age.id 
                  }))}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    personaConfig.ageRange === age.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {age.label}
                </button>
              ))}
            </div>
          </div>

          {/* Income Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Income Level</label>
            <div className="flex flex-wrap gap-2">
              {incomeLevels.map(income => (
                <button
                  key={income.id}
                  onClick={() => setPersonaConfig(prev => ({ 
                    ...prev, 
                    incomeLevel: prev.incomeLevel === income.id ? '' : income.id 
                  }))}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    personaConfig.incomeLevel === income.id
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {income.label}
                </button>
              ))}
            </div>
          </div>

          {/* Family Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Family Status</label>
            <div className="flex flex-wrap gap-2">
              {familyStatuses.map(status => (
                <button
                  key={status.id}
                  onClick={() => setPersonaConfig(prev => ({ 
                    ...prev, 
                    familyStatus: prev.familyStatus === status.id ? '' : status.id 
                  }))}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                    personaConfig.familyStatus === status.id
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{status.icon}</span>
                  {status.label}
                </button>
              ))}
            </div>
          </div>

          {/* Occupation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Occupation</label>
            <select
              value={personaConfig.occupation}
              onChange={(e) => setPersonaConfig(prev => ({ ...prev, occupation: e.target.value }))}
              className="w-full p-2.5 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select occupation...</option>
              {occupations.map(occ => (
                <option key={occ.id} value={occ.id}>{occ.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Priorities & Lifestyle */}
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Heart className="w-5 h-5 text-red-500" />
          Priorities & Lifestyle
        </h3>
        
        <div className="space-y-5">
          {/* Priorities */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What matters most? (Select multiple)
            </label>
            <div className="flex flex-wrap gap-2">
              {priorityOptions.map(priority => (
                <button
                  key={priority.id}
                  onClick={() => toggleArrayItem(personaConfig.priorities, priority.id, setPersonaConfig, 'priorities')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                    personaConfig.priorities.includes(priority.id)
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{priority.icon}</span>
                  {priority.label}
                </button>
              ))}
            </div>
          </div>

          {/* Lifestyle Interests */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lifestyle Interests (Select multiple)
            </label>
            <div className="flex flex-wrap gap-2">
              {lifestyleOptions.map(lifestyle => (
                <button
                  key={lifestyle.id}
                  onClick={() => toggleArrayItem(personaConfig.lifestyleInterests, lifestyle.id, setPersonaConfig, 'lifestyleInterests')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                    personaConfig.lifestyleInterests.includes(lifestyle.id)
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{lifestyle.icon}</span>
                  {lifestyle.label}
                </button>
              ))}
            </div>
          </div>

          {/* Buying Motivation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buying Motivation
            </label>
            <div className="flex flex-wrap gap-2">
              {motivationOptions.map(motivation => (
                <button
                  key={motivation.id}
                  onClick={() => setPersonaConfig(prev => ({ 
                    ...prev, 
                    buyingMotivation: prev.buyingMotivation === motivation.id ? '' : motivation.id 
                  }))}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                    personaConfig.buyingMotivation === motivation.id
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{motivation.icon}</span>
                  {motivation.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Pain Points */}
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-500" />
          Pain Points to Address
        </h3>
        <p className="text-sm text-gray-500 mb-3">Select concerns this buyer might have - we'll generate responses for each</p>
        <div className="flex flex-wrap gap-2">
          {painPointOptions.map(point => (
            <button
              key={point.id}
              onClick={() => toggleArrayItem(personaConfig.painPoints, point.id, setPersonaConfig, 'painPoints')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1 ${
                personaConfig.painPoints.includes(point.id)
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span>{point.icon}</span>
              {point.label}
            </button>
          ))}
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={isLoading}
        className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-bold text-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Analyzing Buyer Persona...
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5" />
            Generate Persona Analysis
          </>
        )}
      </button>

      {/* Results Section */}
      {result && !result.error && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Result Tabs */}
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {resultTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveResultTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-all ${
                  activeResultTab === tab.id
                    ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-600'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className="mr-1">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Persona Profile Tab */}
            {activeResultTab === 'profile' && result.persona_profile && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-xl p-5">
                  <h4 className="text-xl font-bold text-gray-800 mb-1">
                    {result.persona_profile.name || 'Target Buyer'}
                  </h4>
                  <p className="text-gray-600 text-sm">{result.persona_profile.lifestyle}</p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <span className="text-xs font-semibold text-gray-500 uppercase">Age Range</span>
                    <p className="text-gray-800 font-medium">{result.persona_profile.age_range || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <span className="text-xs font-semibold text-gray-500 uppercase">Income</span>
                    <p className="text-gray-800 font-medium">{result.persona_profile.income_bracket || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <span className="text-xs font-semibold text-gray-500 uppercase">Occupation</span>
                    <p className="text-gray-800 font-medium">{result.persona_profile.occupation || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <span className="text-xs font-semibold text-gray-500 uppercase">Family</span>
                    <p className="text-gray-800 font-medium">{result.persona_profile.family_situation || 'N/A'}</p>
                  </div>
                </div>

                {result.persona_profile.values && result.persona_profile.values.length > 0 && (
                  <div>
                    <h5 className="font-semibold text-gray-700 mb-2">Core Values</h5>
                    <div className="flex flex-wrap gap-2">
                      {result.persona_profile.values.map((value, i) => (
                        <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                          {value}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {result.emotional_triggers && result.emotional_triggers.length > 0 && (
                  <div>
                    <h5 className="font-semibold text-gray-700 mb-2">🎯 Emotional Triggers</h5>
                    <div className="space-y-2">
                      {result.emotional_triggers.map((trigger, i) => (
                        <div key={i} className="flex items-start gap-2 text-gray-700">
                          <span className="text-red-500">❤️</span>
                          <span>{trigger}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {result.marketing_angle && (
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <h5 className="font-semibold text-yellow-800 mb-1">💡 Marketing Angle</h5>
                    <p className="text-yellow-900">{result.marketing_angle}</p>
                  </div>
                )}

                {result.recommended_tone && (
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h5 className="font-semibold text-blue-800 mb-1">🎨 Recommended Tone</h5>
                    <p className="text-blue-900">{result.recommended_tone}</p>
                  </div>
                )}
              </div>
            )}

            {/* Custom Listing Tab */}
            {activeResultTab === 'description' && (
              <div className="space-y-4">
                {result.headline_options && result.headline_options.length > 0 && (
                  <div>
                    <h5 className="font-semibold text-gray-700 mb-2">📰 Headline Options</h5>
                    <div className="space-y-2">
                      {result.headline_options.map((headline, i) => (
                        <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                          <span className="font-medium text-gray-800">{headline}</span>
                          <button
                            onClick={() => copyToClipboard(headline, `headline-${i}`)}
                            className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            {copied[`headline-${i}`] ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold text-gray-700">📝 Customized Description</h5>
                    <button
                      onClick={() => copyToClipboard(result.customized_description, 'description')}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        copied.description
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {copied.description ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy</>}
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-gray-700">
                    {result.customized_description}
                  </div>
                </div>
              </div>
            )}

            {/* Selling Points Tab */}
            {activeResultTab === 'selling' && (
              <div className="space-y-4">
                {result.key_selling_points && result.key_selling_points.length > 0 && (
                  <div>
                    <h5 className="font-semibold text-gray-700 mb-3">🎯 Key Selling Points</h5>
                    <div className="space-y-2">
                      {result.key_selling_points.map((point, i) => (
                        <div key={i} className="flex items-start gap-3 bg-green-50 rounded-lg p-3 border border-green-100">
                          <span className="text-green-600 font-bold">{i + 1}</span>
                          <span className="text-gray-700">{point}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {result.talking_points && result.talking_points.length > 0 && (
                  <div>
                    <h5 className="font-semibold text-gray-700 mb-3">🗣️ Talking Points for Showings</h5>
                    <div className="space-y-2">
                      {result.talking_points.map((point, i) => (
                        <div key={i} className="flex items-start gap-3 bg-blue-50 rounded-lg p-3 border border-blue-100">
                          <span className="text-blue-600">💬</span>
                          <span className="text-gray-700">{point}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Objection Handlers Tab */}
            {activeResultTab === 'objections' && result.objection_handlers && (
              <div className="space-y-4">
                <h5 className="font-semibold text-gray-700 mb-3">🛡️ Objection Handlers</h5>
                <div className="space-y-3">
                  {result.objection_handlers.map((item, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="flex items-start gap-2 mb-2">
                        <span className="text-red-500">❓</span>
                        <span className="font-medium text-gray-800">{item.objection}</span>
                      </div>
                      <div className="flex items-start gap-2 ml-6">
                        <span className="text-green-500">✓</span>
                        <span className="text-gray-700">{item.response}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Multi-Channel Tab */}
            {activeResultTab === 'channels' && result.channel_content && (
              <div className="space-y-4">
                <h5 className="font-semibold text-gray-700 mb-3">📱 Multi-Channel Content</h5>
                
                {result.channel_content.listing && (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-800 flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Listing Teaser
                      </span>
                      <button
                        onClick={() => copyToClipboard(result.channel_content.listing, 'listing')}
                        className="p-1.5 hover:bg-gray-200 rounded-lg"
                      >
                        {copied.listing ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                      </button>
                    </div>
                    <p className="text-gray-700">{result.channel_content.listing}</p>
                  </div>
                )}

                {result.channel_content.social_media && (
                  <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl p-4 border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-800 flex items-center gap-2">
                        <Share className="w-4 h-4" /> Social Media Post
                      </span>
                      <button
                        onClick={() => copyToClipboard(result.channel_content.social_media, 'social')}
                        className="p-1.5 hover:bg-purple-100 rounded-lg"
                      >
                        {copied.social ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                      </button>
                    </div>
                    <p className="text-gray-700">{result.channel_content.social_media}</p>
                  </div>
                )}

                {result.channel_content.email_subject && (
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-800 flex items-center gap-2">
                        <Mail className="w-4 h-4" /> Email Subject Line
                      </span>
                      <button
                        onClick={() => copyToClipboard(result.channel_content.email_subject, 'email')}
                        className="p-1.5 hover:bg-blue-100 rounded-lg"
                      >
                        {copied.email ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                      </button>
                    </div>
                    <p className="text-gray-700 font-medium">{result.channel_content.email_subject}</p>
                  </div>
                )}

                {result.channel_content.open_house_pitch && (
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-800 flex items-center gap-2">
                        <Home className="w-4 h-4" /> Open House Pitch (30 sec)
                      </span>
                      <button
                        onClick={() => copyToClipboard(result.channel_content.open_house_pitch, 'pitch')}
                        className="p-1.5 hover:bg-green-100 rounded-lg"
                      >
                        {copied.pitch ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                      </button>
                    </div>
                    <p className="text-gray-700">{result.channel_content.open_house_pitch}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error State */}
      {result && result.error && (
        <div className="bg-red-50 rounded-xl p-4 border border-red-200">
          <p className="text-red-700 flex items-center gap-2">
            <X className="w-5 h-5" />
            {result.error}
          </p>
        </div>
      )}
    </div>
  );
};

// Single Tool Renderer - Phase 1
// Wrapped with React.memo to prevent unnecessary re-renders when parent state changes
const AIMarketingToolsSingle = React.memo(({ toolId, userPlan, generatedDescription, formData, userId, API }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [savedListings, setSavedListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  // Use useRef to maintain stable formData reference and prevent re-renders
  const formDataRef = React.useRef(formData);
  formDataRef.current = formData;
  const [localFormData, setLocalFormData] = useState(formData || {});

  // Fetch saved listings on mount
  useEffect(() => {
    if (userId && API) {
      fetchSavedListings();
    }
  }, [userId, API]);

  const fetchSavedListings = async () => {
    try {
      const res = await axios.get(`${API}/my-listings?user_id=${userId}`);
      setSavedListings(res.data.listings || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    }
  };

  const handleListingSelect = (listingId) => {
    // Clear previous results when switching listings
    setResult(null);
    
    if (!listingId) {
      setSelectedListing(null);
      setLocalFormData(formData || {});
      return;
    }
    
    const listing = savedListings.find(l => l.id === listingId);
    if (listing) {
      setSelectedListing(listing);
      setLocalFormData({
        propertyType: listing.property_type || '',
        address: `${listing.address}, ${listing.city}, ${listing.state} ${listing.zip_code}`,
        beds: listing.bedrooms?.toString() || '',
        bedrooms: listing.bedrooms?.toString() || '',
        baths: listing.bathrooms?.toString() || '',
        bathrooms: listing.bathrooms?.toString() || '',
        sqft: listing.square_footage?.toString() || '',
        squareFootage: listing.square_footage?.toString() || '',
        price: listing.price ? `$${listing.price.toLocaleString()}` : '',
        keyFeatures: listing.key_features || [],
        neighborhood: '',
        neighborhoodHighlights: '',
        description: listing.description || ''
      });
    }
  };

  // Use activeFormData consistently
  const activeFormData = selectedListing ? localFormData : (formData || localFormData);

  // Helper function to build description from formData when none exists
  const buildDescriptionFromFormData = (data) => {
    if (!data) return 'A beautiful property available for sale.';
    
    const parts = [];
    if (data.propertyType) parts.push(`${data.propertyType}`);
    if (data.address) parts.push(`located at ${data.address}`);
    if (data.beds || data.bedrooms) parts.push(`with ${data.beds || data.bedrooms} bedrooms`);
    if (data.baths || data.bathrooms) parts.push(`and ${data.baths || data.bathrooms} bathrooms`);
    if (data.sqft || data.squareFootage) parts.push(`spanning ${data.sqft || data.squareFootage} square feet`);
    if (data.price) parts.push(`priced at ${data.price}`);
    if (data.keyFeatures) {
      const features = Array.isArray(data.keyFeatures) ? data.keyFeatures.join(', ') : data.keyFeatures;
      if (features) parts.push(`featuring ${features}`);
    }
    if (data.neighborhood || data.neighborhoodHighlights) {
      parts.push(`in a great location ${data.neighborhood || data.neighborhoodHighlights}`);
    }
    
    return parts.length > 0 
      ? `Beautiful ${parts.join(' ')}. This property offers an excellent opportunity for buyers.`
      : 'A beautiful property available for sale with excellent features and great location.';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const generateTool = async () => {
    // QR Code requires a valid URL
    if (toolId === 'qrcode') {
      if (!qrUrl.trim()) {
        alert('Please enter a URL to generate a QR code.');
        return;
      }
      if (!isValidUrl(qrUrl)) {
        alert('Please enter a valid URL (e.g., https://example.com)');
        return;
      }
    }
    
    // Build description from formData if no generatedDescription exists
    const description = generatedDescription || buildDescriptionFromFormData(activeFormData);
    
    setIsLoading(true);
    try {
      let endpoint = '';
      let payload = { description, ...activeFormData };
      
      switch(toolId) {
        case 'variations':
          endpoint = '/premium/description-variations';
          payload = {
            propertyType: activeFormData?.propertyType || 'House',
            address: activeFormData?.address || '123 Main Street',
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '3',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '2',
            squareFootage: activeFormData?.sqft || activeFormData?.squareFootage || '2000',
            price: activeFormData?.price || '$500,000',
            keyFeatures: Array.isArray(activeFormData?.keyFeatures) ? activeFormData.keyFeatures.join(', ') : (activeFormData?.keyFeatures || 'Modern Kitchen, Hardwood Floors'),
            neighborhoodHighlights: activeFormData?.neighborhood || activeFormData?.neighborhoodHighlights || 'Great location'
          };
          break;
        case 'hashtags':
          endpoint = '/premium/hashtags';
          payload = { 
            propertyType: activeFormData?.propertyType || 'House',
            location: activeFormData?.address || 'Prime Location',
            features: Array.isArray(activeFormData?.keyFeatures) ? activeFormData.keyFeatures.join(', ') : (activeFormData?.keyFeatures || 'Modern, Spacious'),
            platform: 'instagram'
          };
          break;
        case 'video':
          endpoint = '/premium/video-script';
          payload = { 
            propertyType: activeFormData?.propertyType || 'House',
            address: activeFormData?.address || '123 Main Street',
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '3',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '2',
            squareFootage: activeFormData?.sqft || activeFormData?.squareFootage || '2000',
            price: activeFormData?.price || '$500,000',
            keyFeatures: Array.isArray(activeFormData?.keyFeatures) ? activeFormData.keyFeatures.join(', ') : (activeFormData?.keyFeatures || 'Modern Kitchen'),
            scriptType: 'walkthrough'
          };
          break;
        case 'highlights':
          endpoint = '/premium/highlight-card';
          payload = { 
            propertyType: activeFormData?.propertyType || 'Home',
            address: activeFormData?.address || '',
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '',
            squareFootage: activeFormData?.sqft || activeFormData?.squareFootage || '',
            price: activeFormData?.price || '',
            topFeatures: Array.isArray(activeFormData?.keyFeatures) ? activeFormData.keyFeatures : 
                        (activeFormData?.keyFeatures ? activeFormData.keyFeatures.split(',').map(f => f.trim()) : []),
            description: description || ''
          };
          break;
        case 'qrcode':
          // Generate QR code with the provided URL
          setResult({
            success: true,
            url: qrUrl,
            qrDataUrl: `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}`,
            message: 'QR Code generated successfully!'
          });
          setIsLoading(false);
          return;
        case 'social':
          endpoint = '/premium/social-media';
          payload = { 
            description,
            address: activeFormData?.address || '123 Main Street',
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '3',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '2',
            squareFootage: activeFormData?.sqft || activeFormData?.squareFootage || '2000',
            price: activeFormData?.price || '$500,000',
            propertyType: activeFormData?.propertyType || 'House',
            platforms: ['instagram', 'facebook', 'linkedin', 'twitter', 'tiktok'],
            imageAnalysis: activeFormData?.keyFeatures ? (Array.isArray(activeFormData.keyFeatures) ? activeFormData.keyFeatures.join(', ') : activeFormData.keyFeatures) : '',
            imageCount: 0
          };
          break;
        case 'emails':
        case 'clientcomm':
          endpoint = '/premium/client-templates';
          payload = { 
            description, 
            templateType: 'new_listing',
            city: activeFormData?.city || '',
            state: activeFormData?.state || '',
            country: activeFormData?.country || 'USA'
          };
          break;
        case 'compare':
          endpoint = '/premium/neighborhood-compare';
          // Send all property data for intelligent comparison
          payload = { 
            primaryArea: activeFormData?.address || '',
            address: activeFormData?.address || '',
            description: description,
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '',
            squareFootage: activeFormData?.sqft || activeFormData?.squareFootage || '',
            price: activeFormData?.price || '',
            propertyType: activeFormData?.propertyType || '',
            neighborhood: activeFormData?.neighborhood || '',
            features: activeFormData?.keyFeatures || [],
            city: activeFormData?.city || '',
            state: activeFormData?.state || '',
            zipCode: activeFormData?.zipCode || '',
            country: activeFormData?.country || 'USA'
          };
          break;
        case 'seo':
          endpoint = '/premium/seo-analysis';
          payload = { 
            address: activeFormData?.address || '',
            propertyType: activeFormData?.propertyType || 'Home',
            description: description || '',
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '',
            price: activeFormData?.price || '',
            features: Array.isArray(activeFormData?.keyFeatures) ? activeFormData.keyFeatures : [],
            city: activeFormData?.city || '',
            state: activeFormData?.state || '',
            zipCode: activeFormData?.zipCode || '',
            country: activeFormData?.country || 'USA'
          };
          break;
        case 'translate':
          endpoint = '/premium/translate';
          payload = { 
            description: description || '',
            address: activeFormData?.address || '',
            price: activeFormData?.price || '',
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '',
            features: Array.isArray(activeFormData?.keyFeatures) ? activeFormData.keyFeatures : [],
            propertyType: activeFormData?.propertyType || 'Home',
            city: activeFormData?.city || '',
            state: activeFormData?.state || '',
            country: activeFormData?.country || 'USA',
            languages: ['Spanish', 'French', 'Chinese', 'German', 'Portuguese']
          };
          break;
        case 'persona':
          endpoint = '/premium/buyer-persona';
          payload = { 
            description,
            city: activeFormData?.city || '',
            state: activeFormData?.state || '',
            country: activeFormData?.country || 'USA'
          };
          break;
        case 'staging':
          endpoint = '/premium/staging-suggestions';
          payload = { description };
          break;
        case 'print':
          endpoint = '/premium/print-marketing';
          payload = { 
            description,
            city: activeFormData?.city || '',
            state: activeFormData?.state || '',
            country: activeFormData?.country || 'USA'
          };
          break;
        case 'openhouse':
          endpoint = '/premium/open-house';
          payload = { 
            description, 
            date: new Date().toISOString(),
            city: activeFormData?.city || '',
            state: activeFormData?.state || '',
            country: activeFormData?.country || 'USA'
          };
          break;
        case 'agent':
          endpoint = '/premium/agent-tools';
          payload = { 
            description,
            city: activeFormData?.city || '',
            state: activeFormData?.state || '',
            country: activeFormData?.country || 'USA'
          };
          break;
        case 'mls':
          endpoint = '/premium/mls-export';
          payload = { description };
          break;
        case 'pricing':
          endpoint = '/premium/pricing-suggestions';
          // Send all property data for intelligent pricing analysis
          payload = { 
            address: activeFormData?.address || '',
            bedrooms: activeFormData?.beds || activeFormData?.bedrooms || '',
            bathrooms: activeFormData?.baths || activeFormData?.bathrooms || '',
            squareFootage: activeFormData?.sqft || activeFormData?.squareFootage || '',
            sqft: activeFormData?.sqft || activeFormData?.squareFootage || '',
            propertyType: activeFormData?.propertyType || '',
            features: activeFormData?.keyFeatures || [],
            description: description,
            price: activeFormData?.price || '',
            neighborhood: activeFormData?.neighborhood || '',
            region: activeFormData?.address || ''
          };
          break;
        case 'highlight':
          endpoint = '/premium/highlight-card';
          payload = { description };
          break;
        default:
          setResult(getMockData(toolId));
          setIsLoading(false);
          return;
      }
      
      const res = await axios.post(`${API}${endpoint}?user_id=${userId}`, payload);
      setResult(res.data);
    } catch (error) {
      console.error(`Error with ${toolId}:`, error);
      setResult(getMockData(toolId));
    } finally {
      setIsLoading(false);
    }
  };

  const getMockData = (toolId) => {
    const mockData = {
      variations: {
        variations: [
          { angle: 'Luxury', description: 'Experience unparalleled elegance in this stunning residence featuring premium finishes and sophisticated design elements throughout.' },
          { angle: 'Family', description: 'Perfect for growing families, this spacious home offers plenty of room for everyone with generous bedrooms and a large backyard.' },
          { angle: 'Investor', description: 'Prime investment opportunity with strong rental potential and excellent location near major employment centers.' }
        ]
      },
      hashtags: {
        hashtags: {
          instagram: ['#DreamHome', '#LuxuryLiving', '#RealEstate', '#HomeForSale', '#JustListed', '#PropertyGoals', '#HouseHunting', '#NewListing'],
          twitter: ['#RealEstate', '#HomesForSale', '#Property', '#Investment', '#JustListed'],
          linkedin: ['#RealEstate', '#PropertyInvestment', '#LuxuryHomes', '#CommercialRealEstate'],
          facebook: ['#HomeForSale', '#RealEstate', '#DreamHome', '#PropertyListing']
        }
      },
      video: {
        script: "OPENING SHOT: Aerial view of the property\n\nNARRATOR: Welcome to your dream home in [Location]...\n\nCUT TO: Front entrance\n\nNARRATOR: Step inside and discover elegance...\n\nSCENE: Living Room\nNARRATOR: Spacious living areas perfect for entertaining...\n\nSCENE: Kitchen\nNARRATOR: Gourmet kitchen with premium appliances...\n\nCLOSING: Sunset view\nNARRATOR: Schedule your showing today.\n\n[Contact information]"
      },
      highlights: {
        cards: [
          { title: 'Gourmet Kitchen', description: 'Modern kitchen with premium stainless steel appliances and quartz countertops' },
          { title: 'Prime Location', description: 'Minutes from downtown, schools, shopping, and dining' },
          { title: 'Outdoor Living', description: 'Beautifully landscaped backyard perfect for entertaining' },
          { title: 'Spacious Layout', description: 'Open floor plan with abundant natural light' }
        ]
      },
      qrcode: {
        message: 'QR Code generated successfully!',
        url: 'https://yourlisting.com/property/12345',
        instructions: 'Download and print for your marketing materials'
      },
      social: {
        posts: {
          instagram: "✨ Just Listed! ✨\n\nThis stunning property won't last long!\n\n🏠 Modern kitchen with premium appliances\n🌳 Beautifully landscaped yard\n📍 Prime location\n💎 Move-in ready\n\nDM for details! 🏡\n\n#JustListed #DreamHome #RealEstate",
          facebook: "🏠 NEW LISTING ALERT! 🏠\n\nCheck out this amazing property that just hit the market! Perfect for families looking for their forever home.\n\n✅ Updated throughout\n✅ Great neighborhood\n✅ Priced to sell!\n\nMessage me to schedule a showing!",
          linkedin: "Excited to present this exceptional property to the market. A perfect blend of luxury and functionality in a sought-after neighborhood.\n\nKey features:\n• Contemporary design\n• Energy-efficient\n• Prime location\n\nContact me for details.",
          twitter: "🏡 Just Listed! Beautiful home with stunning features. Perfect location, move-in ready. Contact me for a private showing! #RealEstate #JustListed"
        }
      },
      emails: {
        emails: {
          subject: 'Just Listed: Your Dream Home Awaits! 🏡',
          body: "Dear [Client Name],\n\nI'm excited to share this exclusive new listing that matches what you've been looking for.\n\n[Property Address]\n$[Price]\n\nKey features:\n• Spacious open floor plan\n• Updated kitchen with premium appliances\n• Beautiful landscaped backyard\n\nThis property won't last long. Schedule a private showing this week?\n\nBest regards,\n[Your Name]\n[Contact Information]"
        }
      },
      compare: {
        comparison: {
          areas: ['Property Neighborhood', 'Nearby Area 1', 'Nearby Area 2'],
          metrics: {
            avgPrice: ['$XXX,XXX', '$XXX,XXX', '$XXX,XXX'],
            schools: ['X/10', 'X/10', 'X/10'],
            commute: ['XX min', 'XX min', 'XX min'],
            lifestyle: ['Fill property details for analysis', 'Generate listing first', 'Add address for real data']
          }
        }
      },
      seo: {
        analysis: {
          score: 85,
          keywords: ['luxury home', 'modern kitchen', 'spacious living', 'prime location', 'move-in ready'],
          suggestions: [
            'Add more location-specific keywords (neighborhood names, nearby landmarks)',
            'Include school district information',
            'Mention proximity to major highways',
            'Add unique property features',
            'Include year built and recent renovations'
          ],
          improvements: [
            'Current keyword density: Good',
            'Meta description: Could be more compelling',
            'Header structure: Well organized'
          ]
        }
      },
      translate: {
        translations: {
          es: 'Bienvenido a su hogar soñado. Esta impresionante propiedad ofrece acabados de lujo y diseño sofisticado...',
          fr: 'Bienvenue dans votre maison de rêve. Cette propriété exceptionnelle offre des finitions de luxe...',
          zh: '欢迎来到您的梦想之家。这座令人惊叹的房产提供豪华装修...',
          de: 'Willkommen in Ihrem Traumhaus. Diese atemberaubende Immobilie bietet luxuriöse Ausstattung...'
        }
      },
      persona: {
        persona: {
          name: 'The Young Professional',
          age: '28-35',
          income: '$100,000-$150,000',
          occupation: 'Tech professional, manager, or entrepreneur',
          interests: ['Career growth', 'Urban living', 'Modern design', 'Work-life balance'],
          buyingMotivation: 'First-time homebuyer looking for a property that reflects their success',
          painPoints: ['Limited time for house hunting', 'Wants move-in ready', 'Concerned about commute'],
          marketingAngle: 'Emphasize modern features, smart home technology, and low maintenance'
        }
      },
      staging: {
        suggestions: [
          'Add neutral-colored throw pillows to sofas',
          'Place fresh flowers on dining table',
          'Remove personal photos and items',
          'Add mirror to entry hallway to create depth',
          'Stage home office with desk and chair',
          'Ensure all light fixtures have working bulbs',
          'Add area rug to define living space'
        ],
        rooms: {
          livingRoom: 'Arrange furniture to showcase space, add accent pieces',
          kitchen: 'Clear counters, add bowl of fresh fruit',
          bedroom: 'Fresh linens, minimal decor, reading lamp',
          bathroom: 'Fluffy towels, remove personal items'
        }
      },
      print: {
        materials: [
          { type: 'Property Flyer', status: '✅ Template ready', description: 'Full-page flyer with photos and details' },
          { type: 'Feature Sheet', status: '✅ Template ready', description: 'Detailed feature list with specs' },
          { type: 'Door Hanger', status: '✅ Design ready', description: 'Eye-catching door hanger' },
          { type: 'Postcard', status: '✅ Layout ready', description: 'Direct mail postcard' }
        ],
        notes: 'All templates include your branding and contact information'
      },
      openhouse: {
        materials: {
          signInSheet: '✅ Digital sign-in sheet ready',
          brochure: '✅ Property brochure generated',
          followUp: '✅ Follow-up email template ready',
          schedule: 'Suggested time: Saturday 2-4 PM'
        },
        checklist: [
          'Clean and stage property',
          'Prepare refreshments',
          'Print directions and signage',
          'Set up guest sign-in',
          'Prepare property information sheets',
          'Test all lights and fixtures'
        ]
      },
      agent: {
        tools: [
          { name: 'Commission Calculator', description: 'Calculate your commission instantly' },
          { name: 'Market Analysis Template', description: 'CMA template ready to customize' },
          { name: 'Buyer Questionnaire', description: 'Qualify buyers efficiently' },
          { name: 'Offer Presentation Guide', description: 'Present offers professionally' },
          { name: 'Closing Checklist', description: 'Never miss a detail' }
        ]
      },
      mls: {
        export: {
          format: 'MLS-compatible format',
          status: 'Ready for upload',
          fields: 'All required fields populated',
          validation: '✓ Passed all validation checks'
        },
        included: [
          'Property description',
          'Key features and amenities',
          'Room dimensions',
          'Lot information',
          'School district data',
          'Tax and HOA information'
        ]
      },
      pricing: {
        analysis: {
          suggestedPrice: '$XXX,XXX',
          priceRange: '$XXX,XXX - $XXX,XXX',
          confidence: 'Pending',
          marketPosition: 'Analysis Required'
        },
        factors: [
          { factor: 'Property Size and Layout', impact: 'Pending', details: 'Fill in property details (beds, baths, sqft) for analysis' },
          { factor: 'Location and Neighborhood', impact: 'Pending', details: 'Add property address for location-based pricing' },
          { factor: 'Property Features', impact: 'Pending', details: 'Add key features for accurate valuation' },
          { factor: 'Market Conditions', impact: 'Pending', details: 'Generate listing to analyze market positioning' }
        ],
        recommendations: [
          'Fill out property details in Generate Listing section',
          'Add property address for location-specific pricing',
          'Include key features for more accurate analysis'
        ]
      }
    };
    return mockData[toolId] || { message: 'Feature generated successfully!' };
  };

  const renderResults = () => {
    if (!result) return null;

    return (
      <div className="mt-6 bg-gray-50 rounded-xl p-6 border border-gray-200">
        {/* A/B Variations */}
        {toolId === 'variations' && result.variations && (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 mb-4">Generated Variations:</h3>
            {/* Handle variations as string or array */}
            {typeof result.variations === 'string' ? (
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-purple-600 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    AI Generated Variations
                  </span>
                  <button 
                    onClick={() => copyToClipboard(result.variations)} 
                    className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1 px-3 py-1 rounded-xl hover:bg-purple-50 transition-colors"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy All'}
                  </button>
                </div>
                <pre className="text-gray-700 whitespace-pre-wrap leading-relaxed text-sm">{result.variations}</pre>
              </div>
            ) : Array.isArray(result.variations) ? (
              result.variations.map((v, i) => (
                <div key={i} className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-purple-600 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      {v.angle || `Variation ${i + 1}`} Version
                    </span>
                    <button 
                      onClick={() => copyToClipboard(v.description || v)} 
                      className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1 px-3 py-1 rounded-xl hover:bg-purple-50 transition-colors"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{v.description || v}</p>
                </div>
              ))
            ) : null}
          </div>
        )}

        {/* Hashtags */}
        {toolId === 'hashtags' && result.hashtags && (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 mb-4">Generated Hashtags:</h3>
            {/* Handle hashtags as string or object */}
            {typeof result.hashtags === 'string' ? (
              <div className="bg-white p-5 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-purple-600 capitalize flex items-center gap-2">
                    <Hash className="w-4 h-4" />
                    {result.platform || 'Hashtags'}
                  </span>
                  <button 
                    onClick={() => copyToClipboard(result.hashtags)} 
                    className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1"
                  >
                    <Copy className="w-4 h-4" /> Copy All
                  </button>
                </div>
                <pre className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">{result.hashtags}</pre>
              </div>
            ) : typeof result.hashtags === 'object' ? (
              Object.entries(result.hashtags).map(([platform, tags]) => (
                <div key={platform} className="bg-white p-5 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-purple-600 capitalize flex items-center gap-2">
                      <Hash className="w-4 h-4" />
                      {platform}
                    </span>
                    <button 
                      onClick={() => copyToClipboard(Array.isArray(tags) ? tags.join(' ') : tags)} 
                      className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1"
                    >
                      <Copy className="w-4 h-4" /> Copy All
                    </button>
                  </div>
                  {Array.isArray(tags) ? (
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag, i) => (
                        <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <pre className="text-gray-700 whitespace-pre-wrap text-sm">{tags}</pre>
                  )}
                </div>
              ))
            ) : null}
          </div>
        )}

        {/* Video Script */}
        {toolId === 'video' && result.script && (
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Video className="w-5 h-5 text-purple-600" />
                Video Script
              </h3>
              <button 
                onClick={() => copyToClipboard(result.script)} 
                className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1"
              >
                <Copy className="w-4 h-4" /> Copy
              </button>
            </div>
            <pre className="text-gray-700 whitespace-pre-wrap font-mono text-sm leading-relaxed">
              {result.script}
            </pre>
          </div>
        )}

        {/* Highlight Cards */}
        {/* Highlight Cards - ENHANCED */}
        {toolId === 'highlights' && result.cards && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">Property Highlight Cards</h3>
              <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                Social Media Ready
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {result.cards.map((card, i) => (
                <div 
                  key={i} 
                  className="group relative bg-gradient-to-br from-purple-50 to-blue-50 p-6 rounded-xl border-2 border-purple-200 hover:border-purple-400 hover:shadow-lg transition-all"
                >
                  {/* Card Number Badge */}
                  <div className="absolute top-3 right-3 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {i + 1}
                  </div>
                  
                  {/* Icon */}
                  <div className="text-4xl mb-3">{card.icon || '✨'}</div>
                  
                  {/* Title */}
                  <h4 className="font-bold text-gray-800 text-lg mb-2 group-hover:text-purple-600 transition-colors pr-8">
                    {card.title}
                  </h4>
                  
                  {/* Description */}
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {card.description}
                  </p>
                  
                  {/* Copy Button */}
                  <button
                    onClick={() => copyToClipboard(`${card.icon || '✨'} ${card.title}\n${card.description}`)}
                    className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
                  >
                    <Copy className="w-4 h-4" /> Copy Card
                  </button>
                </div>
              ))}
            </div>
            
            {/* Copy All Button */}
            <div className="flex justify-center pt-4">
              <button
                onClick={() => {
                  const allCards = result.cards.map((c, i) => 
                    `${i + 1}. ${c.icon || '✨'} ${c.title}\n${c.description}`
                  ).join('\n\n');
                  copyToClipboard(allCards);
                }}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all flex items-center gap-2 shadow-lg"
              >
                <Copy className="w-5 h-5" />
                Copy All Cards
              </button>
            </div>
            
            {/* Usage Tip */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-2">
              <p className="text-sm text-blue-800">
                💡 <strong>Pro Tip:</strong> Use these cards on Instagram Stories, Facebook posts, or property flyers!
              </p>
            </div>
          </div>
        )}

        {/* QR Code */}
        {toolId === 'qrcode' && result.success && (
          <div className="bg-white p-6 rounded-xl border border-gray-200 text-center">
            <div className="w-52 h-52 mx-auto bg-white rounded-xl flex items-center justify-center mb-4 border border-gray-100 shadow-sm">
              <img 
                src={result.qrDataUrl} 
                alt="QR Code" 
                className="w-48 h-48"
              />
            </div>
            <p className="text-green-600 font-medium mb-2 flex items-center justify-center gap-2">
              <Check className="w-5 h-5" />
              {result.message}
            </p>
            {result.url && (
              <div className="mt-3 p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Linked URL:</p>
                <p className="text-sm text-purple-600 font-medium break-all">{result.url}</p>
              </div>
            )}
            <div className="mt-4 flex justify-center gap-3">
              <a 
                href={result.qrDataUrl} 
                download="qrcode.png"
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download PNG
              </a>
              <button 
                onClick={() => copyToClipboard(result.url)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Copy URL
              </button>
            </div>
          </div>
        )}

        {/* Social Media Posts */}
        {toolId === 'social' && result.posts && (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 mb-4">Platform-Specific Posts:</h3>
            
            {/* Facebook */}
            {result.posts.facebook && (
              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
                      <span className="text-white font-bold text-sm">f</span>
                    </div>
                    <span className="font-semibold text-gray-800">Facebook</span>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {result.posts.facebook.length} chars
                    </span>
                  </div>
                  <button 
                    onClick={() => copyToClipboard(result.posts.facebook)} 
                    className="text-sm text-gray-500 hover:text-blue-600 flex items-center gap-1 px-3 py-1 rounded hover:bg-blue-50 transition-colors"
                  >
                    <Copy className="w-4 h-4" /> Copy
                  </button>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{result.posts.facebook}</p>
              </div>
            )}

            {/* Instagram */}
            {result.posts.instagram && (
              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded flex items-center justify-center">
                      <span className="text-white text-sm">📷</span>
                    </div>
                    <span className="font-semibold text-gray-800">Instagram</span>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {result.posts.instagram.length} chars
                    </span>
                  </div>
                  <button 
                    onClick={() => copyToClipboard(result.posts.instagram)} 
                    className="text-sm text-gray-500 hover:text-pink-600 flex items-center gap-1 px-3 py-1 rounded hover:bg-pink-50 transition-colors"
                  >
                    <Copy className="w-4 h-4" /> Copy
                  </button>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{result.posts.instagram}</p>
              </div>
            )}

            {/* TikTok */}
            {result.posts.tiktok && (
              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-black rounded flex items-center justify-center">
                      <span className="text-white text-sm">🎵</span>
                    </div>
                    <span className="font-semibold text-gray-800">TikTok</span>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {result.posts.tiktok.length} chars
                    </span>
                  </div>
                  <button 
                    onClick={() => copyToClipboard(result.posts.tiktok)} 
                    className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1 px-3 py-1 rounded hover:bg-gray-100 transition-colors"
                  >
                    <Copy className="w-4 h-4" /> Copy
                  </button>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{result.posts.tiktok}</p>
              </div>
            )}

            {/* LinkedIn */}
            {result.posts.linkedin && (
              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-700 rounded flex items-center justify-center">
                      <span className="text-white font-bold text-sm">in</span>
                    </div>
                    <span className="font-semibold text-gray-800">LinkedIn</span>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {result.posts.linkedin.length} chars
                    </span>
                  </div>
                  <button 
                    onClick={() => copyToClipboard(result.posts.linkedin)} 
                    className="text-sm text-gray-500 hover:text-blue-700 flex items-center gap-1 px-3 py-1 rounded hover:bg-blue-50 transition-colors"
                  >
                    <Copy className="w-4 h-4" /> Copy
                  </button>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{result.posts.linkedin}</p>
              </div>
            )}

            {/* Twitter/X */}
            {result.posts.twitter && (
              <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-black rounded flex items-center justify-center">
                      <span className="text-white font-bold text-sm">𝕏</span>
                    </div>
                    <span className="font-semibold text-gray-800">X (Twitter)</span>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {result.posts.twitter.length} chars
                    </span>
                  </div>
                  <button 
                    onClick={() => copyToClipboard(result.posts.twitter)} 
                    className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1 px-3 py-1 rounded hover:bg-gray-100 transition-colors"
                  >
                    <Copy className="w-4 h-4" /> Copy
                  </button>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{result.posts.twitter}</p>
              </div>
            )}

            {/* Platform Tips */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-sm text-blue-800">
                <strong>💡 Pro Tip:</strong> Each post is optimized for its platform's best practices. 
                Copy and paste directly, or customize further based on your brand voice.
              </p>
            </div>
          </div>
        )}

        {/* Email Templates */}
        {toolId === 'emails' && result.emails && (
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Mail className="w-5 h-5 text-purple-600" />
                Email Campaign
              </h3>
              <button 
                onClick={() => copyToClipboard(`Subject: ${result.emails.subject}\n\n${result.emails.body}`)} 
                className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1"
              >
                <Copy className="w-4 h-4" /> Copy All
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Subject:</label>
                <p className="text-gray-800 bg-gray-50 p-3 rounded">{result.emails.subject}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-1">Body:</label>
                <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded-xl text-sm">{result.emails.body}</p>
              </div>
            </div>
          </div>
        )}

        {/* Area Comparison */}
        {toolId === 'compare' && result.comparison && (
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-purple-600" />
              Neighborhood Comparison
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Metric</th>
                    {result.comparison.areas && result.comparison.areas.map((area, i) => (
                      <th key={i} className="text-center py-3 px-4 font-semibold text-purple-700">{area}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.comparison.metrics && Object.entries(result.comparison.metrics).map(([metric, values]) => (
                    <tr key={metric} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-gray-600">
                        {metric === 'avgPrice' ? '💰 Average Price' : 
                         metric === 'schools' ? '🎓 School Rating' :
                         metric === 'commute' ? '🚗 Commute Time' :
                         metric === 'lifestyle' ? '🏡 Lifestyle' :
                         metric.replace(/([A-Z])/g, ' $1').trim()}
                      </td>
                      {Array.isArray(values) && values.map((value, i) => (
                        <td key={i} className="text-center py-3 px-4 text-gray-700">{value}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SEO Analysis - ENHANCED */}
        {toolId === 'seo' && (result.seoScore !== undefined || result.analysis) && (
          <div className="space-y-6">
            {/* SEO Score */}
            <div className="bg-gradient-to-br from-green-50 to-blue-50 p-6 rounded-xl border-2 border-green-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800 text-lg">Overall SEO Score</h3>
                <span className={`text-4xl font-bold ${
                  (result.seoScore || result.analysis?.score || 0) >= 80 ? 'text-green-600' :
                  (result.seoScore || result.analysis?.score || 0) >= 60 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {result.seoScore || result.analysis?.score || 0}/100
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                <div 
                  className={`h-4 rounded-full transition-all ${
                    (result.seoScore || result.analysis?.score || 0) >= 80 ? 'bg-green-500' :
                    (result.seoScore || result.analysis?.score || 0) >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${result.seoScore || result.analysis?.score || 0}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600">{result.scoreLabel || 'SEO Optimization Level'}</p>
            </div>

            {/* Before/After Comparison */}
            {result.beforeAfter && (
              <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                  Title Optimization: Before vs After
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-red-600 font-semibold">❌ BEFORE (Weak):</label>
                    <p className="text-gray-600 bg-red-50 p-3 rounded border border-red-200 mt-1">
                      {result.beforeAfter.before}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-green-600 font-semibold">✅ AFTER (Optimized):</label>
                    <p className="text-gray-800 bg-green-50 p-3 rounded border border-green-200 mt-1 font-medium">
                      {result.beforeAfter.after}
                    </p>
                    <button 
                      onClick={() => copyToClipboard(result.beforeAfter.after)}
                      className="mt-2 text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                    >
                      <Copy className="w-4 h-4" /> Copy Optimized Title
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {/* Target Keywords */}
              {(result.targetKeywords || result.analysis?.keywords) && (
                <div className="bg-white p-5 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-3">🎯 Target Keywords</h4>
                  <div className="flex flex-wrap gap-2">
                    {(result.targetKeywords || result.analysis?.keywords || []).map((keyword, i) => (
                      <span key={i} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Readability */}
              {(result.readabilityScore !== undefined) && (
                <div className="bg-white p-5 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-3">📖 Readability</h4>
                  <div className="flex items-center gap-3">
                    <div className="text-3xl font-bold text-blue-600">{result.readabilityScore}/100</div>
                    <div className="flex-1">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${result.readabilityScore}%` }}></div>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{result.readabilityLabel || 'Readability Score'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Platform Optimization */}
            {result.platformOptimization && (
              <div className="bg-white p-5 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-4">🌐 Platform-Specific Tips</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {Object.entries(result.platformOptimization).map(([platform, tip]) => (
                    <div key={platform} className="bg-gray-50 p-4 rounded-lg">
                      <div className="font-semibold text-gray-700 mb-2 capitalize flex items-center gap-2">
                        {platform === 'zillow' && '🏠'}
                        {platform === 'realtor' && '🏘️'}
                        {platform === 'mls' && '📋'}
                        {platform === 'google' && '🔍'}
                        {platform}
                      </div>
                      <p className="text-sm text-gray-600">{tip}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Local SEO */}
            {result.localSEO && (
              <div className="bg-white p-5 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-4">📍 Local SEO Optimization</h4>
                <div className="space-y-3">
                  {result.localSEO.neighborhoodKeywords && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Neighborhood Keywords:</label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {result.localSEO.neighborhoodKeywords.map((kw, i) => (
                          <span key={i} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                            {kw}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {result.localSEO.tips && (
                    <div>
                      <label className="text-sm font-medium text-gray-700">Local SEO Tips:</label>
                      <ul className="mt-2 space-y-1">
                        {result.localSEO.tips.map((tip, i) => (
                          <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                            <span className="text-green-600">•</span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Meta Tags */}
            <div className="space-y-3">
              {result.titleTag && (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-700">📝 Title Tag:</label>
                    <button onClick={() => copyToClipboard(result.titleTag)} className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1">
                      <Copy className="w-4 h-4" /> Copy
                    </button>
                  </div>
                  <p className="text-gray-800 font-medium">{result.titleTag}</p>
                </div>
              )}
              
              {result.metaDescription && (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-700">📄 Meta Description:</label>
                    <button onClick={() => copyToClipboard(result.metaDescription)} className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1">
                      <Copy className="w-4 h-4" /> Copy
                    </button>
                  </div>
                  <p className="text-gray-600 text-sm">{result.metaDescription}</p>
                  <p className="text-xs text-gray-400 mt-1">{result.metaDescription.length}/155 characters</p>
                </div>
              )}

              {result.urlSlug && (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-700">🔗 SEO-Friendly URL:</label>
                    <button onClick={() => copyToClipboard(result.urlSlug)} className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1">
                      <Copy className="w-4 h-4" /> Copy
                    </button>
                  </div>
                  <p className="text-gray-600 text-sm font-mono bg-gray-50 p-2 rounded">
                    yoursite.com/listings/{result.urlSlug}
                  </p>
                </div>
              )}
            </div>

            {/* Image Alt Texts */}
            {result.imageAltTexts && result.imageAltTexts.length > 0 && (
              <div className="bg-white p-5 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-800 mb-3">🖼️ Image Alt Text Suggestions</h4>
                <div className="space-y-2">
                  {result.imageAltTexts.map((alt, i) => (
                    <div key={i} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                      <span className="text-sm text-gray-700">Photo {i + 1}: {alt}</span>
                      <button onClick={() => copyToClipboard(alt)} className="text-purple-600 hover:text-purple-700">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Improvements */}
            {(result.improvements || result.analysis?.suggestions) && (result.improvements || result.analysis?.suggestions).length > 0 && (
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-5">
                <h4 className="font-semibold text-gray-800 mb-3">💡 Improvement Suggestions</h4>
                <ul className="space-y-2">
                  {(result.improvements || result.analysis?.suggestions || []).map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-yellow-600 mt-0.5">→</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Competitor Insights */}
            {result.competitorInsights && result.competitorInsights.length > 0 && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-5">
                <h4 className="font-semibold text-gray-800 mb-3">🔍 Competitor Keyword Insights</h4>
                <ul className="space-y-2">
                  {result.competitorInsights.map((insight, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-blue-600 mt-0.5">📊</span>
                      <span>{insight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Translations */}
        {/* Translations - ENHANCED MULTI-LANGUAGE */}
        {toolId === 'translate' && result.translations && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-800">
                🌍 <strong>All languages translated simultaneously!</strong> Each translation includes cultural adaptation tips for that market.
              </p>
            </div>

            {Object.entries(result.translations).map(([language, translation]) => {
              const flags = {
                'Spanish': '🇪🇸',
                'French': '🇫🇷',
                'Chinese': '🇨🇳',
                'German': '🇩🇪',
                'Portuguese': '🇵🇹'
              };
              
              return (
                <div key={language} className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                  {/* Language Header */}
                  <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{flags[language] || '🌐'}</span>
                      <div>
                        <h3 className="font-bold text-lg">{language}</h3>
                        <p className="text-sm text-purple-100">{translation.preferredContact || 'Contact info'}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const fullText = `${translation.title}\n\n${translation.description}\n\n${translation.features?.join('\n') || ''}\n\n${translation.callToAction}`;
                        copyToClipboard(fullText);
                      }}
                      className="px-4 py-2 bg-white text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-colors flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      Copy All
                    </button>
                  </div>

                  <div className="p-6 space-y-4">
                    {/* Title */}
                    {translation.title && (
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs font-bold text-purple-700 uppercase">Listing Title</label>
                          <button onClick={() => copyToClipboard(translation.title)} className="text-purple-600 hover:text-purple-700">
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-gray-800 font-semibold text-lg">{translation.title}</p>
                      </div>
                    )}

                    {/* Description */}
                    {translation.description && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs font-bold text-gray-700 uppercase">Description</label>
                          <button onClick={() => copyToClipboard(translation.description)} className="text-purple-600 hover:text-purple-700">
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{translation.description}</p>
                      </div>
                    )}

                    {/* Features */}
                    {translation.features && translation.features.length > 0 && (
                      <div>
                        <label className="text-xs font-bold text-gray-700 uppercase mb-2 block">Key Features</label>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <ul className="space-y-2">
                            {translation.features.map((feature, i) => (
                              <li key={i} className="flex items-start gap-2 text-gray-700">
                                <span className="text-purple-600 mt-1">•</span>
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}

                    {/* Call to Action */}
                    {translation.callToAction && (
                      <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                        <label className="text-xs font-bold text-green-700 uppercase mb-2 block">Call to Action</label>
                        <p className="text-gray-800 font-semibold">{translation.callToAction}</p>
                      </div>
                    )}

                    {/* Price Format */}
                    {translation.priceFormat && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-semibold text-gray-700">💰 Market Price:</span>
                        <span className="text-purple-600 font-bold">{translation.priceFormat}</span>
                      </div>
                    )}

                    {/* Expandable Sections */}
                    <div className="border-t pt-4 space-y-3">
                      {/* Cultural Tips */}
                      {translation.culturalTips && translation.culturalTips.length > 0 && (
                        <details className="group">
                          <summary className="cursor-pointer font-semibold text-gray-800 hover:text-purple-600 flex items-center gap-2">
                            🎯 Cultural Adaptation Tips
                            <span className="text-xs text-gray-500">(click to expand)</span>
                          </summary>
                          <div className="mt-3 pl-4 space-y-2">
                            {translation.culturalTips.map((tip, i) => (
                              <p key={i} className="text-sm text-gray-600 flex items-start gap-2">
                                <span className="text-purple-600">→</span>
                                {tip}
                              </p>
                            ))}
                          </div>
                        </details>
                      )}

                      {/* Market Insights */}
                      {translation.marketInsights && (
                        <details className="group">
                          <summary className="cursor-pointer font-semibold text-gray-800 hover:text-purple-600 flex items-center gap-2">
                            📊 Market Insights
                            <span className="text-xs text-gray-500">(click to expand)</span>
                          </summary>
                          <div className="mt-3 pl-4">
                            <p className="text-sm text-gray-600">{translation.marketInsights}</p>
                          </div>
                        </details>
                      )}

                      {/* Pronunciation Guide */}
                      {translation.pronunciation && translation.pronunciation.length > 0 && (
                        <details className="group">
                          <summary className="cursor-pointer font-semibold text-gray-800 hover:text-purple-600 flex items-center gap-2">
                            🗣️ Key Phrases Pronunciation
                            <span className="text-xs text-gray-500">(click to expand)</span>
                          </summary>
                          <div className="mt-3 bg-blue-50 rounded p-3">
                            <ul className="space-y-1">
                              {translation.pronunciation.map((phrase, i) => (
                                <li key={i} className="text-sm text-gray-700 font-mono">{phrase}</li>
                              ))}
                            </ul>
                          </div>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Export All Button */}
            <div className="flex justify-center">
              <button
                onClick={() => {
                  let allTranslations = '';
                  Object.entries(result.translations).forEach(([lang, trans]) => {
                    allTranslations += `\n========== ${lang} ==========\n\n`;
                    allTranslations += `${trans.title}\n\n${trans.description}\n\n`;
                    if (trans.features) allTranslations += trans.features.join('\n') + '\n\n';
                    allTranslations += trans.callToAction + '\n';
                  });
                  copyToClipboard(allTranslations);
                }}
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all flex items-center gap-2 shadow-lg"
              >
                <Download className="w-5 h-5" />
                Export All Translations
              </button>
            </div>

            {/* Usage Guide */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-5">
              <h4 className="font-bold text-gray-800 mb-3">💡 How to Use These Translations</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span><strong>Spanish:</strong> Share on WhatsApp, Hispanic real estate groups, Latin American property sites</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span><strong>Chinese:</strong> Post on WeChat, Chinese real estate forums, international buyer platforms</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span><strong>French:</strong> Quebec buyer networks, French Canadian real estate sites, bilingual listings</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>Each translation includes cultural tips specific to that market - use these to better connect with buyers!</span>
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Buyer Persona */}
        {toolId === 'persona' && result.persona && (
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Target Buyer Persona
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-purple-50 rounded-xl">
                  <label className="text-xs text-gray-600 font-medium">Persona Name</label>
                  <p className="text-gray-800 font-semibold">{result.persona.name}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl">
                  <label className="text-xs text-gray-600 font-medium">Age Range</label>
                  <p className="text-gray-800 font-semibold">{result.persona.age}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-xl">
                  <label className="text-xs text-gray-600 font-medium">Income Level</label>
                  <p className="text-gray-800 font-semibold">{result.persona.income}</p>
                </div>
                <div className="p-3 bg-yellow-50 rounded-xl">
                  <label className="text-xs text-gray-600 font-medium">Occupation</label>
                  <p className="text-gray-800 font-semibold">{result.persona.occupation}</p>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Interests:</label>
                <div className="flex flex-wrap gap-2">
                  {result.persona.interests.map((interest, i) => (
                    <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                      {interest}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Buying Motivation:</label>
                <p className="text-gray-600 text-sm">{result.persona.buyingMotivation}</p>
              </div>
              
              {result.persona.painPoints && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Pain Points:</label>
                  <ul className="space-y-1">
                    {result.persona.painPoints.map((point, i) => (
                      <li key={i} className="text-gray-600 text-sm flex gap-2">
                        <span className="text-red-500">•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {result.persona.marketingAngle && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Marketing Angle:</label>
                  <p className="text-gray-600 text-sm">{result.persona.marketingAngle}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Staging Suggestions */}
        {toolId === 'staging' && result.suggestions && (
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Sofa className="w-5 h-5 text-purple-600" />
              Virtual Staging Suggestions
            </h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Quick Wins:</h4>
                <ul className="space-y-2">
                  {result.suggestions.map((suggestion, i) => (
                    <li key={i} className="text-gray-600 text-sm flex gap-2">
                      <span className="text-green-500">✓</span>
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>
              
              {result.rooms && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Room-by-Room:</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(result.rooms).map(([room, advice]) => (
                      <div key={room} className="p-3 bg-gray-50 rounded-xl">
                        <h5 className="font-medium text-gray-700 text-sm mb-1 capitalize">
                          {room.replace(/([A-Z])/g, ' $1').trim()}
                        </h5>
                        <p className="text-gray-600 text-xs">{advice}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Print Marketing */}
        {toolId === 'print' && result.materials && (
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Printer className="w-5 h-5 text-purple-600" />
              Print Marketing Materials
            </h3>
            <div className="space-y-3">
              {result.materials.map((material, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-800">{material.type}</h4>
                      <span className="text-sm text-green-600">{material.status}</span>
                    </div>
                    <p className="text-sm text-gray-600">{material.description}</p>
                  </div>
                  <button className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm">
                    Download
                  </button>
                </div>
              ))}
            </div>
            {result.notes && (
              <p className="text-sm text-gray-500 mt-4 italic">{result.notes}</p>
            )}
          </div>
        )}

        {/* Open House */}
        {toolId === 'openhouse' && result.materials && (
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Home className="w-5 h-5 text-purple-600" />
              Open House Toolkit
            </h3>
            <div className="space-y-4">
              {/* Materials */}
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Materials Ready:</h4>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(result.materials).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2 text-sm">
                      <span className="text-green-500">✓</span>
                      <span className="text-gray-700">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Checklist */}
              {result.checklist && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Preparation Checklist:</h4>
                  <ul className="space-y-2">
                    {result.checklist.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <input type="checkbox" className="mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Agent Tools */}
        {toolId === 'agent' && result.tools && (
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-purple-600" />
              Professional Agent Tools
            </h3>
            <div className="space-y-3">
              {result.tools.map((tool, i) => (
                <div key={i} className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-100">
                  <h4 className="font-semibold text-purple-700 mb-1">{tool.name}</h4>
                  <p className="text-sm text-gray-600">{tool.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MLS Export */}
        {toolId === 'mls' && result.export && (
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              MLS Export Ready
            </h3>
            <div className="space-y-4">
              {/* Status */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-green-50 rounded-xl">
                  <label className="text-xs text-gray-600 font-medium">Format</label>
                  <p className="text-gray-800 font-semibold">{result.export.format}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl">
                  <label className="text-xs text-gray-600 font-medium">Status</label>
                  <p className="text-gray-800 font-semibold">{result.export.status}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-xl">
                  <label className="text-xs text-gray-600 font-medium">Fields</label>
                  <p className="text-gray-800 font-semibold">{result.export.fields}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-xl">
                  <label className="text-xs text-gray-600 font-medium">Validation</label>
                  <p className="text-gray-800 font-semibold">{result.export.validation}</p>
                </div>
              </div>
              
              {/* Included Fields */}
              {result.included && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Included Information:</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {result.included.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="text-green-500">✓</span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <button className="w-full py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium">
                Download MLS File
              </button>
            </div>
          </div>
        )}

        {/* Pricing Suggestions */}
        {toolId === 'pricing' && result.analysis && (
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-purple-600" />
                AI Pricing Analysis
              </h3>
              <button
                onClick={() => {
                  // Store pricing data for PDF modal
                  window.pricingPdfData = {
                    analysis: result.analysis,
                    marketData: result.marketData,
                    factors: result.factors,
                    recommendations: result.recommendations,
                    formData: formData
                  };
                  window.showPricingPdfModal = true;
                  window.dispatchEvent(new Event('showPricingPdfModal'));
                }}
                className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg text-sm font-medium hover:from-purple-700 hover:to-blue-700 transition-all flex items-center gap-1.5"
                data-testid="pricing-download-pdf-btn"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>
            </div>
            <div className="space-y-4">
              {/* Suggested Price */}
              <div className="text-center p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200">
                <label className="text-sm text-gray-600 font-medium">Suggested List Price</label>
                <p className="text-4xl font-bold text-purple-600 mt-2">{result.analysis.suggestedPrice}</p>
                <p className="text-sm text-gray-600 mt-2">
                  Range: {result.analysis.priceRange}
                </p>
                {result.analysis.pricePerSqft && (
                  <p className="text-sm text-gray-500 mt-1">
                    Price per sqft: {result.analysis.pricePerSqft}
                  </p>
                )}
                <div className="flex items-center justify-center gap-4 mt-3 flex-wrap">
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                    {result.analysis.confidence} Confidence
                  </span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                    {result.analysis.marketPosition}
                  </span>
                  {result.analysis.comparedToMarket && (
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      result.analysis.comparedToMarket.includes('+') ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {result.analysis.comparedToMarket}
                    </span>
                  )}
                </div>
              </div>

              {/* Real Market Data from Redfin */}
              {result.marketData && (
                <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 border border-red-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-red-800 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Real-Time Market Data
                    </h4>
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
                      {result.marketData.source || 'Redfin'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">Avg Area Price</p>
                      <p className="text-lg font-bold text-gray-800">{result.marketData.avgAreaPrice || 'N/A'}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">Median Price</p>
                      <p className="text-lg font-bold text-gray-800">{result.marketData.medianAreaPrice || 'N/A'}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">Price/Sqft</p>
                      <p className="text-lg font-bold text-gray-800">{result.marketData.pricePerSqft || 'N/A'}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <p className="text-xs text-gray-500">Homes Sold (90d)</p>
                      <p className="text-lg font-bold text-gray-800">{result.marketData.homesSold || 'N/A'}</p>
                    </div>
                  </div>
                  <p className="text-xs text-red-600 mt-3 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Data sourced from Redfin. Prices may vary based on specific property conditions.
                  </p>
                </div>
              )}
              
              {/* Factors */}
              {result.factors && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Key Pricing Factors:</h4>
                  <div className="space-y-2">
                    {result.factors.map((item, i) => (
                      <div key={i} className="p-3 bg-gray-50 rounded-xl">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-800">{item.factor}</span>
                          <span className={`text-sm px-2 py-1 rounded ${
                            item.impact === 'Strong' || item.impact === 'Positive' ? 'bg-green-100 text-green-700' :
                            item.impact === 'Neutral' ? 'bg-gray-100 text-gray-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {item.impact}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{item.details}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Recommendations */}
              {result.recommendations && (
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Recommendations:</h4>
                  <ul className="space-y-2">
                    {result.recommendations.map((rec, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="text-purple-600 font-bold">→</span>
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Disclaimer */}
              <div className="mt-4 p-3 bg-yellow-50 rounded-xl border border-yellow-200">
                <p className="text-xs text-yellow-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Disclaimer:</strong> Pricing analysis uses real-time data from Redfin and AI algorithms. 
                    This is for informational purposes only and should not replace professional appraisal. 
                    Actual market value may vary based on property condition, local factors, and market timing.
                  </span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Generic Results */}
        {result.message && toolId !== 'qrcode' && !result.variations && !result.hashtags && !result.script && !result.cards && !result.posts && !result.emails && !result.comparison && !result.analysis && !result.translations && !result.persona && !result.suggestions && !result.materials && !result.tools && !result.export && (
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <p className="text-gray-700">{result.message}</p>
          </div>
        )}

        {/* Success with Data */}
        {result.success && result.data && typeof result.data === 'string' && (
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-purple-600 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Generated Content
              </span>
              <button 
                onClick={() => copyToClipboard(result.data)} 
                className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1"
              >
                <Copy className="w-4 h-4" /> Copy
              </button>
            </div>
            <pre className="text-gray-700 whitespace-pre-wrap leading-relaxed text-sm">{result.data}</pre>
          </div>
        )}

        {/* Error handling */}
        {result.success === false && result.error && (
          <div className="bg-red-50 border border-red-200 p-5 rounded-xl">
            <p className="text-red-700 flex items-center gap-2">
              <X className="w-5 h-5" />
              {result.error}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Listing Selector */}
      {savedListings.length > 0 && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 border border-purple-200 mb-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-purple-700 mb-1">
                <FileText className="w-4 h-4 inline mr-1" />
                Select a Listing to Auto-Fill
              </label>
              <select
                value={selectedListing?.id || ''}
                onChange={(e) => { e.stopPropagation(); handleListingSelect(e.target.value); }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-3 py-2 border border-purple-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">-- Choose from My Listings --</option>
                {savedListings.map((listing) => (
                  <option key={listing.id} value={listing.id}>
                    {listing.address}, {listing.city} - ${listing.price?.toLocaleString()}
                  </option>
                ))}
              </select>
            </div>
            {selectedListing && (
              <div className="flex-shrink-0 bg-white rounded-xl p-3 border border-purple-200">
                <p className="text-sm font-medium text-gray-800">{selectedListing.address}</p>
                <p className="text-xs text-gray-500">
                  {selectedListing.bedrooms} bed • {selectedListing.bathrooms} bath • {selectedListing.square_footage?.toLocaleString()} sqft
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* QR Code URL Input */}
      {toolId === 'qrcode' && (
        <div className="max-w-md mx-auto">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Enter URL for QR Code
          </label>
          <div className="relative">
            <input
              type="url"
              value={qrUrl}
              onChange={(e) => setQrUrl(e.target.value)}
              placeholder="https://your-listing-url.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-700 placeholder-gray-400"
            />
            {qrUrl && isValidUrl(qrUrl) && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Check className="w-5 h-5 text-green-500" />
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Paste your property listing URL to generate a scannable QR code
          </p>
        </div>
      )}

      {/* Generate Button */}
      <div className="flex justify-center">
        <button
          type="button"
          onClick={generateTool}
          disabled={isLoading || (toolId === 'qrcode' && !qrUrl.trim())}
          className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg"
        >
          {isLoading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              Generate
            </>
          )}
        </button>
      </div>

      {/* Results */}
      {renderResults()}

      {/* Empty State */}
      {!result && !isLoading && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mb-4">
            <Sparkles className="w-10 h-10 text-purple-600" />
          </div>
          <p className="text-gray-600 mb-2">Click "Generate" to create content</p>
          <p className="text-sm text-gray-400">AI will create content based on your property details</p>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent re-renders when only formData changes slightly
  return prevProps.toolId === nextProps.toolId &&
         prevProps.userPlan === nextProps.userPlan &&
         prevProps.userId === nextProps.userId &&
         prevProps.API === nextProps.API;
});

// Virtual Staging Enhanced Component with AI Photo Staging
const VirtualStagingEnhanced = ({ userPlan, generatedDescription, formData, userId, API }) => {
  const [activeSubTab, setActiveSubTab] = useState('staging-plan'); // 'staging-plan' or 'suggestions'
  const [stagingImage, setStagingImage] = useState(null);
  const [stagedPhoto, setStagedPhoto] = useState(null);
  const [roomType, setRoomType] = useState('living_room');
  const [stagingStyle, setStagingStyle] = useState('modern');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [stagingResult, setStagingResult] = useState(null);
  const [suggestionsResult, setSuggestionsResult] = useState(null);
  const [error, setError] = useState(null);
  
  const roomTypes = [
    { id: 'living_room', label: 'Living Room', icon: '🛋️' },
    { id: 'bedroom', label: 'Bedroom', icon: '🛏️' },
    { id: 'kitchen', label: 'Kitchen', icon: '🍳' },
    { id: 'bathroom', label: 'Bathroom', icon: '🚿' },
    { id: 'dining_room', label: 'Dining Room', icon: '🍽️' },
    { id: 'office', label: 'Home Office', icon: '💼' },
    { id: 'nursery', label: 'Nursery', icon: '👶' },
    { id: 'outdoor', label: 'Outdoor/Patio', icon: '🌿' }
  ];
  
  const stagingStyles = [
    { id: 'modern', label: 'Modern', desc: 'Clean lines, minimalist' },
    { id: 'traditional', label: 'Traditional', desc: 'Classic elegance' },
    { id: 'scandinavian', label: 'Scandinavian', desc: 'Light & cozy' },
    { id: 'industrial', label: 'Industrial', desc: 'Urban & raw' },
    { id: 'coastal', label: 'Coastal', desc: 'Beach vibes' },
    { id: 'farmhouse', label: 'Farmhouse', desc: 'Rustic charm' },
    { id: 'luxury', label: 'Luxury', desc: 'High-end appeal' },
    { id: 'bohemian', label: 'Bohemian', desc: 'Eclectic & warm' }
  ];

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setStagingImage(reader.result);
        setStagingResult(null);
        setStagedPhoto(null);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  // Generate actual AI staged image using Pollinations.AI (FREE - no API key needed!)
  const generateStagedImage = async () => {
    setIsGeneratingImage(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('room_type', roomType);
      formData.append('style', stagingStyle);
      formData.append('additional_instructions', additionalNotes);

      const response = await axios.post(
        `${API}/premium/virtual-staging?user_id=${userId}`,
        formData,
        { 
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000 // 2 minute timeout for image generation
        }
      );

      if (response.data.success) {
        setStagedPhoto(`data:image/jpeg;base64,${response.data.staged_image_base64}`);
      } else {
        setError(response.data.error || 'Failed to generate staged image');
      }
    } catch (err) {
      console.error('Image generation error:', err);
      if (err.code === 'ECONNABORTED') {
        setError('Image generation timed out. Please try again.');
      } else {
        setError(err.response?.data?.error || 'Failed to generate image. Please try again.');
      }
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleAIStaging = async () => {
    if (!stagingImage) {
      setError('Please upload a room photo first');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API}/premium/ai-photo-staging`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image: stagingImage,
          room_type: roomType,
          style: stagingStyle,
          additional_instructions: additionalNotes
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setStagingResult(data);
      } else {
        setError(data.error || 'Failed to generate staging suggestions');
      }
    } catch (err) {
      setError('Failed to connect to staging service');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionsGenerate = async (images) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API}/premium/staging-suggestions?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          images: images,
          room_type: roomType
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuggestionsResult(data);
      } else {
        setError(data.error || 'Failed to generate suggestions');
      }
    } catch (err) {
      setError('Failed to connect to staging service');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation - 2 Tabs */}
      <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
        <button
          onClick={() => setActiveSubTab('staging-plan')}
          className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
            activeSubTab === 'staging-plan'
              ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          <FileText className="w-4 h-4" />
          Staging Plan
        </button>
        <button
          onClick={() => setActiveSubTab('suggestions')}
          className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
            activeSubTab === 'suggestions'
              ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          Quick Suggestions
        </button>
      </div>

      {/* Staging Suggestions Tab */}
      {activeSubTab === 'suggestions' && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-xl border border-purple-200">
            <p className="text-sm text-purple-800">
              📸 Upload room photos to get AI-powered staging recommendations including furniture placement, color schemes, and decor suggestions.
            </p>
          </div>
          
          <AIMarketingToolsSingle 
            toolId="staging"
            userPlan={userPlan}
            generatedDescription={generatedDescription}
            formData={formData}
            userId={userId}
            API={API}
          />
        </div>
      )}

      {/* STAGING PLAN Tab - Detailed AI Recommendations */}
      {activeSubTab === 'staging-plan' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-xl border border-purple-200">
            <p className="text-sm text-purple-800">
              🪄 <strong>AI Photo Staging:</strong> Upload a vacant room photo and get a detailed visualization plan with furniture recommendations, color schemes, and buyer appeal points.
            </p>
          </div>

          {/* Image Upload */}
          <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-6">
            <div className="text-center">
              {!stagingImage ? (
                <label className="cursor-pointer">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                      <Upload className="w-8 h-8 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-gray-800 font-semibold">Upload Room Photo</p>
                      <p className="text-gray-500 text-sm">PNG, JPG up to 10MB</p>
                    </div>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
              ) : (
                <div className="space-y-4">
                  <div className="relative inline-block">
                    <img
                      src={stagingImage}
                      alt="Room to stage"
                      className="max-h-64 rounded-xl shadow-lg"
                    />
                    <button
                      onClick={() => {
                        setStagingImage(null);
                        setStagingResult(null);
                      }}
                      className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-green-600 text-sm font-medium">✓ Photo uploaded successfully</p>
                </div>
              )}
            </div>
          </div>

          {/* Room Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">Room Type</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {roomTypes.map((room) => (
                <button
                  key={room.id}
                  onClick={() => setRoomType(room.id)}
                  className={`p-3 rounded-xl border-2 transition-all text-center ${
                    roomType === room.id
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  <span className="text-2xl block mb-1">{room.icon}</span>
                  <span className="text-xs font-medium">{room.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Style Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">Staging Style</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {stagingStyles.map((style) => (
                <button
                  key={style.id}
                  onClick={() => setStagingStyle(style.id)}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    stagingStyle === style.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className={`text-sm font-semibold block ${stagingStyle === style.id ? 'text-purple-700' : 'text-gray-700'}`}>
                    {style.label}
                  </span>
                  <span className="text-xs text-gray-500">{style.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Additional Notes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Additional Instructions (Optional)
            </label>
            <textarea
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              placeholder="E.g., 'Focus on making the space feel larger', 'Target young professionals', 'Highlight the fireplace'"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              rows={2}
            />
          </div>

          {/* Generate Button */}
          <button
            onClick={handleAIStaging}
            disabled={!stagingImage || isLoading}
            className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-3 ${
              !stagingImage || isLoading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl'
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Analyzing Room... (30-60 seconds)
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Stage This Room with AI
              </>
            )}
          </button>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Results */}
          {stagingResult && (
            <div className="space-y-6">
              {/* Visualization Description */}
              {stagingResult.visualization && (
                <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold flex items-center gap-2">
                      <Eye className="w-6 h-6" />
                      Staged Room Visualization
                    </h3>
                    <button
                      onClick={() => copyToClipboard(stagingResult.visualization)}
                      className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm flex items-center gap-1"
                    >
                      <Copy className="w-4 h-4" /> Copy
                    </button>
                  </div>
                  <p className="text-white/90 leading-relaxed whitespace-pre-wrap">
                    {stagingResult.visualization}
                  </p>
                </div>
              )}

              {/* Room Analysis */}
              {stagingResult.room_analysis && (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Search className="w-5 h-5 text-purple-600" />
                    Room Analysis
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(stagingResult.room_analysis).map(([key, value]) => (
                      <div key={key} className="bg-gray-50 p-3 rounded-lg">
                        <span className="text-xs text-gray-500 uppercase block mb-1">
                          {key.replace(/_/g, ' ')}
                        </span>
                        <span className="text-gray-800 font-medium">
                          {Array.isArray(value) ? value.join(', ') : value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Staging Plan */}
              {stagingResult.staging_details?.staging_plan && (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Sofa className="w-5 h-5 text-purple-600" />
                    Detailed Staging Plan
                  </h3>
                  
                  {/* Primary Furniture */}
                  {stagingResult.staging_details.staging_plan.primary_furniture && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-700 mb-3">Primary Furniture</h4>
                      <div className="space-y-3">
                        {stagingResult.staging_details.staging_plan.primary_furniture.map((item, i) => (
                          <div key={i} className="bg-purple-50 p-4 rounded-lg">
                            <div className="flex items-start justify-between">
                              <div>
                                <span className="font-semibold text-purple-800">{item.item}</span>
                                <p className="text-sm text-gray-600 mt-1">
                                  <strong>Placement:</strong> {item.placement}
                                </p>
                                {item.dimensions && (
                                  <p className="text-sm text-gray-600">
                                    <strong>Size:</strong> {item.dimensions}
                                  </p>
                                )}
                                {item.color && (
                                  <p className="text-sm text-gray-600">
                                    <strong>Color:</strong> {item.color}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Color Scheme */}
                  {stagingResult.staging_details.staging_plan.color_scheme && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-700 mb-3">Color Scheme</h4>
                      <div className="flex flex-wrap gap-3">
                        {Object.entries(stagingResult.staging_details.staging_plan.color_scheme).map(([key, value]) => (
                          <div key={key} className="bg-gray-100 px-4 py-2 rounded-full">
                            <span className="text-xs text-gray-500 uppercase">{key.replace(/_/g, ' ')}: </span>
                            <span className="text-gray-800 font-medium">
                              {Array.isArray(value) ? value.join(', ') : value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Lighting Plan */}
                  {stagingResult.staging_details.staging_plan.lighting_plan && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-700 mb-3">Lighting Plan</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {Object.entries(stagingResult.staging_details.staging_plan.lighting_plan).map(([key, value]) => (
                          <div key={key} className="bg-yellow-50 p-3 rounded-lg">
                            <span className="text-xs text-yellow-700 uppercase block mb-1">{key}</span>
                            <span className="text-gray-800 text-sm">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Decor Accents */}
                  {stagingResult.staging_details.staging_plan.decor_accents && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-700 mb-3">Decor Accents</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {stagingResult.staging_details.staging_plan.decor_accents.map((item, i) => (
                          <div key={i} className="bg-blue-50 p-3 rounded-lg">
                            <span className="font-medium text-blue-800">{item.item}</span>
                            <p className="text-xs text-gray-600 mt-1">{item.location}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Buyer Appeal */}
              {stagingResult.recommendations && stagingResult.recommendations.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                  <h3 className="font-bold text-green-800 mb-4 flex items-center gap-2">
                    <Heart className="w-5 h-5" />
                    Why Buyers Will Love This
                  </h3>
                  <ul className="space-y-2">
                    {stagingResult.recommendations.map((rec, i) => (
                      <li key={i} className="flex items-start gap-2 text-green-700">
                        <span className="text-green-500 mt-0.5">✓</span>
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Budget Options */}
              {stagingResult.budget_options && Object.keys(stagingResult.budget_options).length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-purple-600" />
                    Budget Options
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {Object.entries(stagingResult.budget_options).map(([level, desc]) => (
                      <div key={level} className={`p-4 rounded-lg ${
                        level === 'budget_friendly' ? 'bg-green-50 border border-green-200' :
                        level === 'mid_range' ? 'bg-blue-50 border border-blue-200' :
                        'bg-purple-50 border border-purple-200'
                      }`}>
                        <h4 className="font-semibold capitalize mb-2">
                          {level.replace(/_/g, ' ')}
                        </h4>
                        <p className="text-sm text-gray-600">{desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* DIY Tips & Photography Tips */}
              <div className="grid grid-cols-2 gap-4">
                {stagingResult.diy_tips && stagingResult.diy_tips.length > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-5">
                    <h3 className="font-bold text-orange-800 mb-3 flex items-center gap-2">
                      <Wrench className="w-5 h-5" />
                      DIY Staging Tips
                    </h3>
                    <ul className="space-y-2">
                      {stagingResult.diy_tips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-orange-700">
                          <span className="text-orange-500">•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {stagingResult.photography_tips && stagingResult.photography_tips.length > 0 && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
                    <h3 className="font-bold text-indigo-800 mb-3 flex items-center gap-2">
                      <Camera className="w-5 h-5" />
                      Photography Tips
                    </h3>
                    <ul className="space-y-2">
                      {stagingResult.photography_tips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-indigo-700">
                          <span className="text-indigo-500">•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Enhanced Open House Toolkit Component
const OpenHouseToolkit = ({ formData, generatedDescription, API, userId }) => {
  const [activeSection, setActiveSection] = useState('invitation');
  const [isLoading, setIsLoading] = useState({});
  const [results, setResults] = useState({});
  const [openHouseDate, setOpenHouseDate] = useState('');
  const [openHouseTime, setOpenHouseTime] = useState('2:00 PM - 4:00 PM');
  const [agentName, setAgentName] = useState('');
  const [agentPhone, setAgentPhone] = useState('');
  const [agentEmail, setAgentEmail] = useState('');
  const [agentWebsite, setAgentWebsite] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyTagline, setCompanyTagline] = useState('YOUR REAL ESTATE PARTNERS');
  const [colorTheme, setColorTheme] = useState('navy');
  
  // Listing selection state
  const [savedListings, setSavedListings] = useState([]);
  const [selectedListingId, setSelectedListingId] = useState('');
  const [selectedListing, setSelectedListing] = useState(null);
  const [loadingListings, setLoadingListings] = useState(false);
  
  // Image upload state
  const [uploadedImages, setUploadedImages] = useState([]);

  const colorThemes = [
    { id: 'navy', name: 'Navy Blue', color: '#244D67' },
    { id: 'burgundy', name: 'Burgundy', color: '#722F37' },
    { id: 'forest', name: 'Forest Green', color: '#1B4332' },
    { id: 'slate', name: 'Slate Gray', color: '#334155' },
    { id: 'purple', name: 'Royal Purple', color: '#5B21B6' },
  ];

  // Fetch saved listings on mount
  useEffect(() => {
    const fetchListings = async () => {
      if (!userId) return;
      setLoadingListings(true);
      try {
        const response = await fetch(`${API}/my-listings?user_id=${userId}`);
        const data = await response.json();
        if (data.listings) {
          setSavedListings(data.listings);
        }
      } catch (error) {
        console.error('Error fetching listings:', error);
      } finally {
        setLoadingListings(false);
      }
    };
    fetchListings();
  }, [API, userId]);

  // Handle listing selection
  const handleListingSelect = (listingId) => {
    setSelectedListingId(listingId);
    if (!listingId) {
      setSelectedListing(null);
      setUploadedImages([]);
      return;
    }
    const listing = savedListings.find(l => l.id === listingId);
    setSelectedListing(listing);
    // Load images from listing if available
    if (listing?.images && listing.images.length > 0) {
      setUploadedImages(listing.images);
    } else {
      setUploadedImages([]);
    }
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const maxImages = 3;
    const remainingSlots = maxImages - uploadedImages.length;
    
    if (remainingSlots <= 0) {
      alert('Maximum 3 images allowed. Remove some images first.');
      return;
    }
    
    const filesToProcess = files.slice(0, remainingSlots);
    
    filesToProcess.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImages(prev => [...prev, reader.result].slice(0, maxImages));
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove uploaded image
  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  // Get active property data - prioritize selected listing, then formData
  const getPropertyData = () => {
    // Use uploadedImages which may come from listing or manual upload
    const images = uploadedImages.length > 0 ? uploadedImages : (selectedListing?.images || formData?.images || []);
    
    if (selectedListing) {
      return {
        address: `${selectedListing.address}${selectedListing.city ? ', ' + selectedListing.city : ''}${selectedListing.state ? ', ' + selectedListing.state : ''} ${selectedListing.zip_code || ''}`.trim(),
        price: selectedListing.price ? `$${selectedListing.price.toLocaleString()}` : '$XXX,XXX',
        bedrooms: selectedListing.bedrooms?.toString() || '3',
        bathrooms: selectedListing.bathrooms?.toString() || '2',
        squareFootage: selectedListing.square_footage?.toLocaleString() || '1,500',
        propertyType: selectedListing.property_type || 'Home',
        description: selectedListing.description || generatedDescription || '',
        keyFeatures: selectedListing.key_features || [],
        images: images
      };
    }
    return {
      address: formData?.address || 'Property Address',
      price: formData?.price || '$XXX,XXX',
      bedrooms: formData?.beds || formData?.bedrooms || '3',
      bathrooms: formData?.baths || formData?.bathrooms || '2',
      squareFootage: formData?.sqft || formData?.squareFootage || '1,500',
      propertyType: formData?.propertyType || 'Home',
      description: generatedDescription || '',
      keyFeatures: formData?.keyFeatures || [],
      images: images
    };
  };

  const propertyData = getPropertyData();
  const hasPropertyData = selectedListing || formData?.address;

  const generateContent = async (contentType) => {
    setIsLoading(prev => ({ ...prev, [contentType]: true }));
    
    try {
      const response = await fetch(`${API}/premium/open-house/toolkit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: propertyData.address,
          price: propertyData.price,
          bedrooms: propertyData.bedrooms,
          bathrooms: propertyData.bathrooms,
          squareFootage: propertyData.squareFootage,
          propertyType: propertyData.propertyType,
          description: propertyData.description,
          features: propertyData.keyFeatures,
          openHouseDate: openHouseDate || 'Saturday, January 15th',
          openHouseTime: openHouseTime,
          agentName: agentName || 'Your Name',
          agentPhone: agentPhone || '(555) 123-4567',
          agentEmail: agentEmail || 'agent@email.com',
          contentType: contentType
        })
      });

      const data = await response.json();
      if (data.success) {
        setResults(prev => ({ ...prev, ...data.results }));
      }
    } catch (error) {
      console.error('Error generating content:', error);
    } finally {
      setIsLoading(prev => ({ ...prev, [contentType]: false }));
    }
  };

  const downloadPDF = async (templateType) => {
    setIsLoading(prev => ({ ...prev, [`pdf_${templateType}`]: true }));
    
    try {
      const response = await fetch(`${API}/premium/open-house/generate-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateType,
          address: propertyData.address,
          price: propertyData.price,
          bedrooms: propertyData.bedrooms,
          bathrooms: propertyData.bathrooms,
          squareFootage: propertyData.squareFootage,
          propertyType: propertyData.propertyType,
          features: propertyData.keyFeatures,
          description: propertyData.description,
          images: propertyData.images || [],  // Include images
          openHouseDate: openHouseDate || 'Saturday',
          openHouseTime: openHouseTime,
          agentName: agentName || 'Your Name',
          agentPhone: agentPhone || '(555) 123-4567',
          agentEmail: agentEmail || 'agent@email.com',
          agentWebsite: agentWebsite || '',
          companyName: companyName || 'Your Real Estate Company',
          companyTagline: companyTagline || 'YOUR REAL ESTATE PARTNERS',
          colorTheme: colorTheme
        })
      });

      const data = await response.json();
      if (data.success) {
        const link = document.createElement('a');
        link.href = data.pdfData;
        link.download = data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsLoading(prev => ({ ...prev, [`pdf_${templateType}`]: false }));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="space-y-6">
      {/* Listing Selector */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Home className="w-5 h-5 text-purple-600" />
          Select Property
        </h3>
        <div className="space-y-3">
          <select
            value={selectedListingId}
            onChange={(e) => handleListingSelect(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 bg-white"
          >
            <option value="">-- Select from My Listings or use current form data --</option>
            {loadingListings && <option disabled>Loading listings...</option>}
            {savedListings.map((listing) => (
              <option key={listing.id} value={listing.id}>
                {listing.address}{listing.city ? `, ${listing.city}` : ''} - ${listing.price ? `$${listing.price.toLocaleString()}` : 'Price TBD'} ({listing.bedrooms || '?'}BD/{listing.bathrooms || '?'}BA)
              </option>
            ))}
          </select>
          {savedListings.length === 0 && !loadingListings && (
            <p className="text-sm text-gray-500">No saved listings found. You can create listings in "My Listings" or use the form data from "Generate Listing".</p>
          )}
        </div>
      </div>

      {/* Property Context Banner */}
      {hasPropertyData ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-sm text-green-800">
            ✓ <strong>Using listing data:</strong> {propertyData.address} | {propertyData.price} | {propertyData.bedrooms} BD / {propertyData.bathrooms} BA | {propertyData.squareFootage} sqft
          </p>
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <p className="text-sm text-yellow-800">
            💡 <strong>Tip:</strong> Select a listing above, or fill out property details in "Generate Listing" for personalized open house materials!
          </p>
        </div>
      )}

      {/* Open House Setup Form */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-purple-600" />
          Open House Details
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
            <input
              type="text"
              value={openHouseDate}
              onChange={(e) => setOpenHouseDate(e.target.value)}
              placeholder="Saturday, January 15th"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Time *</label>
            <input
              type="text"
              value={openHouseTime}
              onChange={(e) => setOpenHouseTime(e.target.value)}
              placeholder="2:00 PM - 4:00 PM"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-400"
            />
          </div>
        </div>
      </div>

      {/* Agent & Company Info */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-purple-600" />
          Agent & Company Information
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
            <input
              type="text"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              placeholder="Your Name"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="text"
              value={agentPhone}
              onChange={(e) => setAgentPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={agentEmail}
              onChange={(e) => setAgentEmail(e.target.value)}
              placeholder="agent@email.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            <input
              type="text"
              value={agentWebsite}
              onChange={(e) => setAgentWebsite(e.target.value)}
              placeholder="www.youragency.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Your Real Estate Company"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Tagline</label>
            <input
              type="text"
              value={companyTagline}
              onChange={(e) => setCompanyTagline(e.target.value)}
              placeholder="YOUR REAL ESTATE PARTNERS"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-400"
            />
          </div>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'invitation', label: '✉️ Invitation', icon: Mail },
          { id: 'followUp', label: '📧 Follow-Up Emails', icon: Send },
          { id: 'checklist', label: '✅ Checklist', icon: CheckSquare },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeSection === tab.id
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Invitation Section */}
      {activeSection === 'invitation' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-gray-800">AI-Generated Invitation Content</h3>
            <button
              onClick={() => generateContent('invitation')}
              disabled={isLoading.invitation}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading.invitation ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Generate Invitation
            </button>
          </div>
          
          {results.invitation && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <label className="text-xs font-bold text-purple-700 uppercase">Headline</label>
                  <button onClick={() => copyToClipboard(results.invitation.headline)} className="text-purple-600 hover:text-purple-700">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xl font-bold text-gray-800">{results.invitation.headline}</p>
              </div>
              
              {results.invitation.subheadline && (
                <p className="text-gray-600 italic">{results.invitation.subheadline}</p>
              )}
              
              <div>
                <label className="text-xs font-bold text-gray-700 uppercase mb-2 block">Body</label>
                <p className="text-gray-700">{results.invitation.body}</p>
              </div>
              
              {results.invitation.highlights && (
                <div>
                  <label className="text-xs font-bold text-gray-700 uppercase mb-2 block">Highlights</label>
                  <ul className="space-y-1">
                    {results.invitation.highlights.map((h, i) => (
                      <li key={i} className="text-gray-700 flex items-start gap-2">
                        <span className="text-purple-600">•</span> {h}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {results.invitation.socialPost && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <label className="text-xs font-bold text-blue-700 uppercase">Social Media Post</label>
                    <button onClick={() => copyToClipboard(results.invitation.socialPost)} className="text-blue-600 hover:text-blue-700">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-gray-700">{results.invitation.socialPost}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Sign-In Questions Section */}
      {activeSection === 'signIn' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-gray-800">Sign-In Sheet Questions</h3>
            <button
              onClick={() => generateContent('signIn')}
              disabled={isLoading.signIn}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading.signIn ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Generate Questions
            </button>
          </div>
          
          {results.signIn && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
              {results.signIn.welcomeMessage && (
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-green-800">{results.signIn.welcomeMessage}</p>
                </div>
              )}
              
              {results.signIn.questions && (
                <div className="space-y-3">
                  {results.signIn.questions.map((q, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-start gap-2">
                        <span className="bg-purple-600 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">{i + 1}</span>
                        <div className="flex-1">
                          <p className="font-medium text-gray-800">{q.question}</p>
                          <p className="text-xs text-gray-500 mt-1">Type: {q.type} {q.required ? '• Required' : ''}</p>
                          {q.options && q.options.length > 0 && (
                            <p className="text-xs text-gray-500">Options: {q.options.join(', ')}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {results.signIn.privacyNote && (
                <p className="text-xs text-gray-500 italic">{results.signIn.privacyNote}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Follow-Up Emails Section */}
      {activeSection === 'followUp' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-gray-800">Follow-Up Email Templates</h3>
            <button
              onClick={() => generateContent('followUp')}
              disabled={isLoading.followUp}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading.followUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Generate Emails
            </button>
          </div>
          
          {results.followUp && (
            <div className="space-y-4">
              {[
                { key: 'immediateFollowUp', title: '📧 Immediate Follow-Up', color: 'green' },
                { key: 'secondFollowUp', title: '📨 3-Day Follow-Up', color: 'blue' },
                { key: 'notInterestedFollowUp', title: '📩 Alternative Properties', color: 'purple' },
              ].map((email) => results.followUp[email.key] && (
                <div key={email.key} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className={`bg-${email.color}-50 p-3 border-b border-${email.color}-100`}>
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-gray-800">{email.title}</h4>
                      <span className="text-xs text-gray-500">{results.followUp[email.key].sendTiming}</span>
                    </div>
                  </div>
                  <div className="p-4 space-y-3">
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase">Subject</label>
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-gray-800">{results.followUp[email.key].subject}</p>
                        <button onClick={() => copyToClipboard(results.followUp[email.key].subject)} className="text-gray-400 hover:text-purple-600">
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase">Body</label>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-gray-700 text-sm whitespace-pre-wrap">{results.followUp[email.key].body}</p>
                        <button onClick={() => copyToClipboard(results.followUp[email.key].body)} className="text-gray-400 hover:text-purple-600 flex-shrink-0">
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Checklist Section */}
      {activeSection === 'checklist' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-gray-800">Open House Preparation Checklist</h3>
            <button
              onClick={() => generateContent('checklist')}
              disabled={isLoading.checklist}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading.checklist ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Generate Checklist
            </button>
          </div>
          
          {results.checklist && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
              {results.checklist.beforeOpenHouse && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">📅 Before Open House</h4>
                  {Object.entries(results.checklist.beforeOpenHouse).map(([timing, tasks]) => (
                    <div key={timing} className="ml-4 mb-3">
                      <p className="text-sm font-semibold text-purple-600 capitalize">{timing.replace(/([A-Z])/g, ' $1')}</p>
                      <ul className="space-y-1">
                        {Array.isArray(tasks) && tasks.map((task, i) => (
                          <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-gray-400">☐</span> {task}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
              
              {results.checklist.duringOpenHouse && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">🏠 During Open House</h4>
                  <ul className="ml-4 space-y-1">
                    {results.checklist.duringOpenHouse.map((task, i) => (
                      <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                        <span className="text-gray-400">☐</span> {task}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {results.checklist.talkingPoints && (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h4 className="font-bold text-yellow-800 mb-2">💬 Key Talking Points</h4>
                  <ul className="space-y-1">
                    {results.checklist.talkingPoints.map((point, i) => (
                      <li key={i} className="text-sm text-yellow-700 flex items-start gap-2">
                        <span className="text-yellow-600">★</span> {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {results.checklist.suppliesNeeded && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-bold text-blue-800 mb-2">📦 Supplies Needed</h4>
                  <div className="flex flex-wrap gap-2">
                    {results.checklist.suppliesNeeded.map((supply, i) => (
                      <span key={i} className="bg-white px-3 py-1 rounded-full text-sm text-blue-700 border border-blue-200">
                        {supply}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Print Marketing Templates Component with PDF Download
const PrintMarketingTemplates = ({ formData, generatedDescription, API }) => {
  const [isGenerating, setIsGenerating] = useState({});

  const generateAndDownload = async (templateType, templateName) => {
    setIsGenerating(prev => ({ ...prev, [templateType]: true }));
    
    try {
      const response = await fetch(`${API}/premium/print-marketing/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateType: templateType,
          address: formData?.address || 'Property Address',
          price: formData?.price || '$XXX,XXX',
          bedrooms: formData?.beds || formData?.bedrooms || '3',
          bathrooms: formData?.baths || formData?.bathrooms || '2',
          squareFootage: formData?.sqft || formData?.squareFootage || '1,500',
          propertyType: formData?.propertyType || 'Home',
          description: generatedDescription || 'Beautiful property for sale',
          features: formData?.keyFeatures || ['Modern kitchen', 'Hardwood floors', 'Updated bathrooms'],
          agentName: 'Your Name',
          agentPhone: '(555) 123-4567',
          agentEmail: 'agent@example.com'
        })
      });

      const data = await response.json();

      if (data.success) {
        // Create download link
        const link = document.createElement('a');
        link.href = data.pdfData;
        link.download = data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Failed to generate template: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Template generation error:', error);
      alert('Failed to generate template. Check console for details.');
    } finally {
      setIsGenerating(prev => ({ ...prev, [templateType]: false }));
    }
  };

  const templates = [
    {
      id: 'flyer',
      name: 'Property Flyer',
      status: 'PDF Ready',
      statusColor: 'text-green-600',
      description: 'Full-page flyer with property details and features',
      icon: '📄',
      size: '8.5" x 11"'
    },
    {
      id: 'feature_sheet',
      name: 'Feature Sheet',
      status: 'PDF Ready',
      statusColor: 'text-green-600',
      description: 'Detailed feature list with specs',
      icon: '📋',
      size: '8.5" x 11"'
    },
    {
      id: 'door_hanger',
      name: 'Door Hanger',
      status: 'PDF Ready',
      statusColor: 'text-blue-600',
      description: 'Eye-catching door hanger for neighborhoods',
      icon: '🚪',
      size: '4.25" x 11"'
    },
    {
      id: 'postcard',
      name: 'Postcard',
      status: 'PDF Ready',
      statusColor: 'text-purple-600',
      description: 'Direct mail postcard for marketing',
      icon: '📮',
      size: '6" x 4"'
    }
  ];

  return (
    <div className="space-y-4">
      {!formData?.address && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-yellow-800">
            💡 <strong>Tip:</strong> Fill out property details in Generate Listing section first for personalized templates!
          </p>
        </div>
      )}

      <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-xl border border-purple-200 mb-4">
        <p className="text-sm text-purple-800">
          📄 <strong>Download professional PDFs</strong> with your listing details. Print-ready templates for marketing your properties!
        </p>
      </div>

      {templates.map((template) => (
        <div key={template.id} className="flex items-center justify-between p-5 bg-gray-50 rounded-xl border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all">
          <div className="flex items-center gap-4">
            <div className="text-4xl">{template.icon}</div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-gray-800">{template.name}</h3>
                <span className={`text-xs font-semibold ${template.statusColor} flex items-center gap-1`}>
                  <Check className="w-3 h-3" />
                  {template.status}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">{template.description}</p>
              <p className="text-xs text-gray-400 mt-1">Size: {template.size}</p>
            </div>
          </div>
          <button
            onClick={() => generateAndDownload(template.id, template.name)}
            disabled={isGenerating[template.id]}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg"
          >
            {isGenerating[template.id] ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Download PDF
              </>
            )}
          </button>
        </div>
      ))}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <h4 className="font-semibold text-blue-800 mb-2">📌 What's Included in Each PDF:</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Property address, price, and key details</li>
          <li>• Beds, baths, square footage, property type</li>
          <li>• Property description and key features</li>
          <li>• Agent contact information</li>
          <li>• Professional, branded design ready to print</li>
        </ul>
      </div>
    </div>
  );
};

// Premium Lock Component
const PremiumLock = ({ onUpgrade }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mb-4">
      <Lock className="w-10 h-10 text-purple-600" />
    </div>
    <h3 className="text-xl font-bold text-gray-800 mb-2">Premium Feature</h3>
    <p className="text-gray-600 mb-6">Upgrade to Premium to unlock this feature</p>
    <button
      onClick={onUpgrade}
      className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
    >
      <Crown className="w-5 h-5" />
      Upgrade to Premium
    </button>
  </div>
);

// Client Emails Component with Email Type Variations
const ClientEmailsComponent = ({ userId, API, formData }) => {
  const [savedListings, setSavedListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedEmailType, setSelectedEmailType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [generatedEmails, setGeneratedEmails] = useState([]);
  const [copied, setCopied] = useState(null);
  const [clientName, setClientName] = useState('');
  const [showingDate, setShowingDate] = useState('');
  const [showingTime, setShowingTime] = useState('');
  const [newPrice, setNewPrice] = useState('');

  const emailTypes = [
    { 
      id: 'just_listed', 
      label: 'Just Listed Announcement', 
      icon: '🏠',
      description: 'Announce a new property listing to your client database',
      color: 'from-green-500 to-emerald-600'
    },
    { 
      id: 'open_house_invite', 
      label: 'Open House Invitation', 
      icon: '🎉',
      description: 'Invite clients to attend an upcoming open house',
      color: 'from-blue-500 to-cyan-600'
    },
    { 
      id: 'pre_showing', 
      label: 'Pre-Showing Preparation', 
      icon: '📋',
      description: 'Prepare your client for an upcoming property showing',
      color: 'from-purple-500 to-violet-600'
    },
    { 
      id: 'post_showing', 
      label: 'Post-Showing Follow-Up', 
      icon: '💬',
      description: 'Follow up after a showing to gather feedback',
      color: 'from-orange-500 to-amber-600'
    },
    { 
      id: 'price_reduction', 
      label: 'Price Reduction Alert', 
      icon: '💰',
      description: 'Notify clients about a price drop on a property',
      color: 'from-red-500 to-rose-600'
    },
    { 
      id: 'under_contract', 
      label: 'Under Contract Congratulations', 
      icon: '🎊',
      description: 'Congratulate a client whose offer was accepted',
      color: 'from-yellow-500 to-orange-500'
    },
    { 
      id: 'buyer_match', 
      label: 'New Listing Match', 
      icon: '🎯',
      description: 'Alert a buyer about a property matching their criteria',
      color: 'from-indigo-500 to-purple-600'
    },
    { 
      id: 'market_update', 
      label: 'Market Update Newsletter', 
      icon: '📊',
      description: 'Share local market trends and insights',
      color: 'from-teal-500 to-cyan-600'
    }
  ];

  useEffect(() => {
    if (userId && API) {
      fetchSavedListings();
    }
  }, [userId, API]);

  const fetchSavedListings = async () => {
    try {
      const res = await axios.get(`${API}/my-listings?user_id=${userId}`);
      setSavedListings(res.data.listings || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    }
  };

  const handleListingSelect = (listingId) => {
    const listing = savedListings.find(l => l.id === listingId);
    setSelectedListing(listing || null);
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopied(index);
    setTimeout(() => setCopied(null), 2000);
  };

  const generateEmails = async () => {
    if (!selectedEmailType) {
      alert('Please select an email type first');
      return;
    }

    setIsLoading(true);
    setGeneratedEmails([]);

    try {
      const propertyAddress = selectedListing 
        ? `${selectedListing.address}, ${selectedListing.city}, ${selectedListing.state}`
        : formData?.address || '123 Main Street, Seattle, WA';
      
      const price = selectedListing?.price 
        ? `$${selectedListing.price.toLocaleString()}`
        : formData?.price || '$500,000';

      const payload = {
        templateType: selectedEmailType,
        clientName: clientName || '[Client Name]',
        propertyAddress,
        agentName: '[Your Name]',
        agentPhone: '[Your Phone]',
        agentEmail: '[Your Email]',
        additionalDetails: selectedListing?.description || formData?.keyFeatures?.join(', ') || '',
        showingDate: showingDate || '[Date]',
        showingTime: showingTime || '[Time]',
        price: price,
        newPrice: newPrice || ''
      };

      // Generate 3 variations
      const variations = [];
      const tones = ['professional', 'friendly', 'urgent'];
      
      for (let i = 0; i < 3; i++) {
        const res = await axios.post(`${API}/premium/client-email-variations?user_id=${userId}`, {
          ...payload,
          tone: tones[i],
          variationNumber: i + 1
        });
        
        if (res.data.success) {
          variations.push({
            tone: tones[i],
            label: i === 0 ? 'Professional' : i === 1 ? 'Friendly & Warm' : 'Urgent & Direct',
            email: res.data.template
          });
        }
      }

      if (variations.length === 0) {
        // Fallback - generate single and create variations client-side
        const res = await axios.post(`${API}/premium/client-templates?user_id=${userId}`, payload);
        if (res.data.success) {
          variations.push({
            tone: 'professional',
            label: 'Professional',
            email: res.data.template
          });
        }
      }

      setGeneratedEmails(variations);
    } catch (error) {
      console.error('Error generating emails:', error);
      alert('Error generating emails. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Listing Selector */}
      {savedListings.length > 0 && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 border border-purple-200">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-purple-700 mb-1">
                <FileText className="w-4 h-4 inline mr-1" />
                Select a Listing (Optional)
              </label>
              <select
                value={selectedListing?.id || ''}
                onChange={(e) => handleListingSelect(e.target.value)}
                className="w-full px-3 py-2 border border-purple-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-purple-500"
              >
                <option value="">-- Choose from My Listings --</option>
                {savedListings.map((listing) => (
                  <option key={listing.id} value={listing.id}>
                    {listing.address}, {listing.city} - ${listing.price?.toLocaleString()}
                  </option>
                ))}
              </select>
            </div>
            {selectedListing && (
              <div className="flex-shrink-0 bg-white rounded-xl p-3 border border-purple-200">
                <p className="text-sm font-medium text-gray-800">{selectedListing.address}</p>
                <p className="text-xs text-gray-500">
                  {selectedListing.bedrooms} bed • {selectedListing.bathrooms} bath • ${selectedListing.price?.toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email Type Selection */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Choose Email Type</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {emailTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setSelectedEmailType(type.id)}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                selectedEmailType === type.id
                  ? 'border-purple-500 bg-purple-50 shadow-lg'
                  : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow'
              }`}
            >
              <span className="text-2xl mb-2 block">{type.icon}</span>
              <h4 className="font-semibold text-gray-800 text-sm">{type.label}</h4>
              <p className="text-xs text-gray-500 mt-1">{type.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Additional Fields Based on Email Type */}
      {selectedEmailType && (
        <div className="bg-gray-50 rounded-xl p-4 space-y-4">
          <h3 className="font-semibold text-gray-800">Email Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="John Smith"
                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500"
              />
            </div>
            
            {['pre_showing', 'open_house_invite'].includes(selectedEmailType) && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Showing Date</label>
                  <input
                    type="date"
                    value={showingDate}
                    onChange={(e) => setShowingDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Showing Time</label>
                  <input
                    type="time"
                    value={showingTime}
                    onChange={(e) => setShowingTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </>
            )}
            
            {selectedEmailType === 'price_reduction' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Price</label>
                <input
                  type="text"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  placeholder="$450,000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Generate Button */}
      <div className="flex justify-center">
        <button
          onClick={generateEmails}
          disabled={isLoading || !selectedEmailType}
          className={`px-8 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
            isLoading || !selectedEmailType
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl'
          }`}
        >
          {isLoading ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Generating Variations...
            </>
          ) : (
            <>
              <Mail className="w-5 h-5" />
              Generate Email Variations
            </>
          )}
        </button>
      </div>

      {/* Generated Email Variations */}
      {generatedEmails.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">Email Variations</h3>
          <p className="text-sm text-gray-500">Choose the tone that best fits your relationship with the client</p>
          
          <div className="grid grid-cols-1 gap-4">
            {generatedEmails.map((variation, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                <div className={`px-4 py-2 bg-gradient-to-r ${
                  index === 0 ? 'from-blue-500 to-indigo-600' : 
                  index === 1 ? 'from-green-500 to-teal-600' : 
                  'from-orange-500 to-red-600'
                } text-white flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">
                      {index === 0 ? '👔' : index === 1 ? '😊' : '⚡'}
                    </span>
                    <span className="font-semibold">{variation.label}</span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(variation.email, index)}
                    className="flex items-center gap-1 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm transition-colors"
                  >
                    {copied === index ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <div className="p-4">
                  <pre className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed font-sans">
                    {variation.email}
                  </pre>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {generatedEmails.length === 0 && !isLoading && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-purple-600" />
          </div>
          <p className="text-gray-600 mb-2">Select an email type and click Generate</p>
          <p className="text-sm text-gray-400">You'll get 3 variations with different tones to choose from</p>
        </div>
      )}
    </div>
  );
};

// Enhanced Video Script Generator Component
const VideoScriptEnhanced = ({ formData, generatedDescription, images, userId, API }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState({});
  const [activeTab, setActiveTab] = useState('script');
  
  // Configuration state
  const [config, setConfig] = useState({
    scriptType: 'walkthrough',
    targetAudience: '',
    tone: 'professional',
    musicStyle: '',
    includeCallToAction: true
  });
  
  // Saved listings
  const [savedListings, setSavedListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);

  useEffect(() => {
    if (userId && API) {
      fetchSavedListings();
    }
  }, [userId, API]);

  const fetchSavedListings = async () => {
    try {
      const res = await axios.get(`${API}/my-listings?user_id=${userId}`);
      setSavedListings(res.data.listings || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    }
  };

  const handleListingSelect = (listingId) => {
    if (!listingId) {
      setSelectedListing(null);
      return;
    }
    const listing = savedListings.find(l => l.id === listingId);
    setSelectedListing(listing);
  };

  const videoTypes = [
    { id: 'walkthrough', label: 'Property Walkthrough', icon: '🏠', duration: '2-3 min', desc: 'Room-by-room tour' },
    { id: 'tiktok', label: 'TikTok', icon: '📱', duration: '15-30 sec', desc: 'Viral-style short' },
    { id: 'instagram_reel', label: 'Instagram Reel', icon: '📸', duration: '30-60 sec', desc: 'Aesthetic showcase' },
    { id: 'youtube', label: 'YouTube Tour', icon: '▶️', duration: '5-8 min', desc: 'Detailed walkthrough' },
    { id: 'drone', label: 'Drone Footage', icon: '🚁', duration: '1-2 min', desc: 'Aerial shots script' },
    { id: 'virtual_tour', label: 'Virtual Tour', icon: '🔄', duration: '3-5 min', desc: '360° tour narration' },
    { id: 'open_house_invite', label: 'Open House Invite', icon: '🎉', duration: '30-45 sec', desc: 'Event teaser' },
    { id: 'agent_intro', label: 'Agent Introduction', icon: '👋', duration: '45-90 sec', desc: 'Personal property pitch' },
  ];

  const audiences = [
    { id: '', label: 'General Audience' },
    { id: 'first_time_buyers', label: 'First-Time Buyers' },
    { id: 'luxury', label: 'Luxury Buyers' },
    { id: 'investors', label: 'Investors' },
    { id: 'families', label: 'Families' },
    { id: 'downsizers', label: 'Downsizers' },
  ];

  const tones = [
    { id: 'professional', label: 'Professional' },
    { id: 'friendly', label: 'Friendly & Warm' },
    { id: 'luxury', label: 'Luxury & Elegant' },
    { id: 'energetic', label: 'Energetic & Exciting' },
    { id: 'casual', label: 'Casual & Relatable' },
  ];

  const musicStyles = [
    { id: '', label: 'No preference' },
    { id: 'upbeat', label: 'Upbeat & Modern' },
    { id: 'elegant', label: 'Elegant & Sophisticated' },
    { id: 'cinematic', label: 'Cinematic & Epic' },
    { id: 'acoustic', label: 'Acoustic & Warm' },
    { id: 'electronic', label: 'Electronic & Trendy' },
  ];

  const handleGenerate = async () => {
    setIsGenerating(true);
    setResult(null);
    
    try {
      const listingData = selectedListing || formData || {};
      
      const payload = {
        propertyType: String(listingData.propertyType || listingData.property_type || 'Single Family Home'),
        address: String(listingData.address || '123 Main Street'),
        bedrooms: String(listingData.bedrooms?.toString() || listingData.beds || '3'),
        bathrooms: String(listingData.bathrooms?.toString() || listingData.baths || '2'),
        squareFootage: String(listingData.square_footage?.toString() || listingData.squareFootage || listingData.sqft || '2000'),
        price: String(listingData.price ? (typeof listingData.price === 'number' ? `$${listingData.price.toLocaleString()}` : listingData.price) : '$500,000'),
        keyFeatures: String(Array.isArray(listingData.key_features) ? listingData.key_features.join(', ') : (listingData.keyFeatures || 'Modern kitchen, hardwood floors, spacious backyard')),
        description: String(listingData.description || generatedDescription || 'Beautiful property with modern amenities'),
        neighborhood: String(listingData.neighborhood || listingData.neighborhoodHighlights || 'Great neighborhood'),
        scriptType: String(config?.scriptType || 'walkthrough'),
        targetAudience: String(config?.targetAudience || ''),
        tone: String(config?.tone || 'professional'),
        musicStyle: String(config?.musicStyle || ''),
        includeCallToAction: config?.includeCallToAction !== undefined ? config.includeCallToAction : true,
        imageCount: images?.length || 0,
        imageAnalysis: ''
      };

      const response = await axios.post(`${API}/premium/video-script?user_id=${userId}`, payload);
      
      if (response.data.success) {
        setResult(response.data);
        setActiveTab('script');
      } else {
        setResult({ error: response.data.error || 'Failed to generate video script' });
      }
    } catch (error) {
      console.error('Error generating video script:', error);
      // Handle FastAPI validation errors which can be arrays of objects
      let errorMessage = 'Failed to generate script';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(d => d.msg || JSON.stringify(d)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      setResult({ error: errorMessage });
    }
    
    setIsGenerating(false);
  };

  const copyToClipboard = async (text, key) => {
    await navigator.clipboard.writeText(text);
    setCopied(prev => ({ ...prev, [key]: true }));
    setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 2000);
  };

  const selectedVideoType = videoTypes.find(t => t.id === config.scriptType);

  return (
    <div className="space-y-6">
      {/* Property Selection */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 border border-purple-100">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Select Property</label>
        <select
          value={selectedListing?.id || ''}
          onChange={(e) => handleListingSelect(e.target.value)}
          className="w-full p-3 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="">Use current form data</option>
          {savedListings.map(listing => (
            <option key={listing.id} value={listing.id}>
              {listing.address}, {listing.city} - ${listing.price?.toLocaleString()}
            </option>
          ))}
        </select>
        {selectedListing && (
          <div className="mt-2 text-sm text-gray-600">
            📍 {selectedListing.bedrooms}bd/{selectedListing.bathrooms}ba | {selectedListing.square_footage?.toLocaleString()} sqft
          </div>
        )}
      </div>

      {/* Video Type Selection */}
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Video className="w-5 h-5 text-purple-600" />
          Video Type
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {videoTypes.map(type => (
            <button
              key={type.id}
              onClick={() => setConfig(prev => ({ ...prev, scriptType: type.id }))}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                config.scriptType === type.id
                  ? 'border-purple-500 bg-purple-50 shadow-md'
                  : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
              }`}
            >
              <div className="text-2xl mb-1">{type.icon}</div>
              <div className="text-sm font-semibold text-gray-800">{type.label}</div>
              <div className="text-xs text-purple-600 font-medium">{type.duration}</div>
              <div className="text-xs text-gray-500 mt-1">{type.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Configuration Options */}
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5 text-blue-600" />
          Customize Your Script
        </h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Target Audience */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience</label>
            <select
              value={config.targetAudience}
              onChange={(e) => setConfig(prev => ({ ...prev, targetAudience: e.target.value }))}
              className="w-full p-2.5 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-purple-500"
            >
              {audiences.map(a => (
                <option key={a.id} value={a.id}>{a.label}</option>
              ))}
            </select>
          </div>

          {/* Tone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tone & Style</label>
            <select
              value={config.tone}
              onChange={(e) => setConfig(prev => ({ ...prev, tone: e.target.value }))}
              className="w-full p-2.5 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-purple-500"
            >
              {tones.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Music Style */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Music Style</label>
            <select
              value={config.musicStyle}
              onChange={(e) => setConfig(prev => ({ ...prev, musicStyle: e.target.value }))}
              className="w-full p-2.5 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-purple-500"
            >
              {musicStyles.map(m => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
          </div>

          {/* Call to Action Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <label className="text-sm font-medium text-gray-700">Include Call-to-Action</label>
              <p className="text-xs text-gray-500">Add contact/booking prompt at end</p>
            </div>
            <button
              onClick={() => setConfig(prev => ({ ...prev, includeCallToAction: !prev.includeCallToAction }))}
              className={`w-12 h-6 rounded-full transition-colors ${
                config.includeCallToAction ? 'bg-purple-600' : 'bg-gray-300'
              }`}
            >
              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                config.includeCallToAction ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={isGenerating}
        className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-bold text-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg"
      >
        {isGenerating ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Generating {selectedVideoType?.label} Script...
          </>
        ) : (
          <>
            <Sparkles className="w-5 h-5" />
            Generate {selectedVideoType?.label} Script
          </>
        )}
      </button>

      {/* Results Section */}
      {result && !result.error && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Result Tabs */}
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {[
              { id: 'script', label: '📝 Full Script', show: true },
              { id: 'scenes', label: '🎬 Scene Breakdown', show: result.scene_breakdown?.length > 0 },
              { id: 'shots', label: '📷 Shot List', show: result.shot_list?.length > 0 },
              { id: 'extras', label: '✨ Extras', show: true },
            ].filter(t => t.show).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-600'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Hook Banner */}
            {result.hook && activeTab === 'script' && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 border border-yellow-200 mb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-xs font-bold text-yellow-700 uppercase">🎯 Opening Hook (First 3 Seconds)</span>
                    <p className="text-gray-800 font-medium mt-1 text-lg">"{result.hook}"</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(result.hook, 'hook')}
                    className="p-2 hover:bg-yellow-100 rounded-lg"
                  >
                    {copied.hook ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                  </button>
                </div>
              </div>
            )}

            {/* Full Script Tab */}
            {activeTab === 'script' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <h4 className="font-semibold text-gray-800">Full Narration Script</h4>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                      ⏱️ {result.duration_estimate}
                    </span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(result.script, 'script')}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      copied.script ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {copied.script ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy Script</>}
                  </button>
                </div>
                <div className="bg-gray-50 rounded-xl p-5 whitespace-pre-wrap text-gray-700 leading-relaxed border border-gray-200 max-h-96 overflow-y-auto">
                  {result.script}
                </div>

                {/* Call to Action */}
                {result.call_to_action && (
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="text-xs font-bold text-green-700 uppercase">📣 Call to Action</span>
                        <p className="text-gray-800 font-medium mt-1">"{result.call_to_action}"</p>
                      </div>
                      <button
                        onClick={() => copyToClipboard(result.call_to_action, 'cta')}
                        className="p-2 hover:bg-green-100 rounded-lg"
                      >
                        {copied.cta ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Scene Breakdown Tab */}
            {activeTab === 'scenes' && result.scene_breakdown && (
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-800 mb-3">Scene-by-Scene Breakdown</h4>
                <div className="space-y-3">
                  {result.scene_breakdown.map((scene, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                          {scene.scene_number || i + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h5 className="font-semibold text-gray-800">{scene.scene_name || `Scene ${i + 1}`}</h5>
                            {scene.duration && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                {scene.duration}
                              </span>
                            )}
                          </div>
                          {scene.narration && (
                            <div className="mb-2">
                              <span className="text-xs font-medium text-purple-600">🎙️ Narration:</span>
                              <p className="text-gray-700 text-sm mt-1">"{scene.narration}"</p>
                            </div>
                          )}
                          {scene.visual && (
                            <div className="mb-2">
                              <span className="text-xs font-medium text-blue-600">📷 Visual:</span>
                              <p className="text-gray-600 text-sm mt-1">{scene.visual}</p>
                            </div>
                          )}
                          {scene.camera_movement && (
                            <div className="mb-2">
                              <span className="text-xs font-medium text-green-600">🎥 Camera:</span>
                              <p className="text-gray-600 text-sm mt-1">{scene.camera_movement}</p>
                            </div>
                          )}
                          {scene.description && !scene.narration && (
                            <p className="text-gray-600 text-sm">{scene.description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Shot List Tab */}
            {activeTab === 'shots' && result.shot_list && (
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-800 mb-3">📷 Shot List (Print & Take to Shoot)</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="text-left p-3 font-semibold text-gray-700">#</th>
                        <th className="text-left p-3 font-semibold text-gray-700">Shot Type</th>
                        <th className="text-left p-3 font-semibold text-gray-700">Location</th>
                        <th className="text-left p-3 font-semibold text-gray-700">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.shot_list.map((shot, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="p-3 font-medium text-purple-600">{shot.shot_number || i + 1}</td>
                          <td className="p-3 text-gray-700">{shot.shot_type}</td>
                          <td className="p-3 text-gray-700">{shot.location}</td>
                          <td className="p-3 text-gray-500 text-xs">{shot.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  onClick={() => {
                    const shotListText = result.shot_list.map(s => 
                      `${s.shot_number}. ${s.shot_type} - ${s.location} (${s.notes})`
                    ).join('\n');
                    copyToClipboard(shotListText, 'shotlist');
                  }}
                  className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200"
                >
                  {copied.shotlist ? '✓ Copied!' : '📋 Copy Shot List'}
                </button>
              </div>
            )}

            {/* Extras Tab */}
            {activeTab === 'extras' && (
              <div className="space-y-6">
                {/* Music Suggestions */}
                {result.music_suggestions && result.music_suggestions.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">🎵 Music Suggestions</h4>
                    <div className="flex flex-wrap gap-2">
                      {result.music_suggestions.map((music, i) => (
                        <span key={i} className="px-3 py-2 bg-purple-50 text-purple-700 rounded-lg text-sm">
                          {music}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Hashtags */}
                {result.hashtags && result.hashtags.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-800">#️⃣ Hashtags</h4>
                      <button
                        onClick={() => copyToClipboard(result.hashtags.join(' '), 'hashtags')}
                        className="text-sm text-purple-600 hover:text-purple-700"
                      >
                        {copied.hashtags ? '✓ Copied!' : 'Copy All'}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {result.hashtags.map((tag, i) => (
                        <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Thumbnail Ideas */}
                {result.thumbnail_ideas && result.thumbnail_ideas.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">🖼️ Thumbnail Ideas</h4>
                    <div className="space-y-2">
                      {result.thumbnail_ideas.map((idea, i) => (
                        <div key={i} className="flex items-start gap-2 p-3 bg-orange-50 rounded-lg border border-orange-100">
                          <span className="text-orange-500">📸</span>
                          <span className="text-gray-700 text-sm">{idea}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Filming Tips */}
                {result.tips && result.tips.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">💡 Pro Filming Tips</h4>
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                      <ul className="space-y-2">
                        {result.tips.map((tip, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <span className="text-green-600 font-bold">✓</span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error State */}
      {result && result.error && (
        <div className="bg-red-50 rounded-xl p-4 border border-red-200">
          <p className="text-red-700 flex items-center gap-2">
            <X className="w-5 h-5" />
            {result.error}
          </p>
        </div>
      )}
    </div>
  );
};

// Virtual Staging Component
const VirtualStagingComponent = ({ userId, API }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [roomType, setRoomType] = useState('living_room');
  const [style, setStyle] = useState('modern');
  const [promptStrength, setPromptStrength] = useState(0.7);
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const roomTypes = [
    { id: 'living_room', label: 'Living Room', icon: '🛋️' },
    { id: 'bedroom', label: 'Bedroom', icon: '🛏️' },
    { id: 'dining_room', label: 'Dining Room', icon: '🍽️' },
    { id: 'kitchen', label: 'Kitchen', icon: '🍳' },
    { id: 'bathroom', label: 'Bathroom', icon: '🚿' },
    { id: 'office', label: 'Home Office', icon: '💼' },
    { id: 'outdoor', label: 'Outdoor/Patio', icon: '🌳' }
  ];

  const styles = [
    { id: 'modern', label: 'Modern', desc: 'Clean lines, neutral colors' },
    { id: 'traditional', label: 'Traditional', desc: 'Classic elegance, warm tones' },
    { id: 'minimalist', label: 'Minimalist', desc: 'Simple, uncluttered spaces' },
    { id: 'luxury', label: 'Luxury', desc: 'High-end, premium finishes' },
    { id: 'scandinavian', label: 'Scandinavian', desc: 'Light wood, cozy textiles' },
    { id: 'coastal', label: 'Coastal', desc: 'Beach vibes, blues & whites' },
    { id: 'industrial', label: 'Industrial', desc: 'Raw materials, exposed elements' }
  ];

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('Image too large. Maximum size is 10MB');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 10 * 1024 * 1024) {
        alert('Image too large. Maximum size is 10MB');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setResult(null);
    }
  };

  const generateStaging = async () => {
    if (!selectedFile) {
      alert('Please select an image first');
      return;
    }

    setIsGenerating(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('room_type', roomType);
      formData.append('style', style);
      formData.append('prompt_strength', promptStrength.toString());
      formData.append('additional_instructions', additionalInstructions);

      const response = await axios.post(
        `${API}/premium/virtual-staging?user_id=${userId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        setResult({
          success: true,
          image: `data:image/png;base64,${response.data.staged_image_base64}`,
          roomType: response.data.room_type,
          style: response.data.style
        });
      } else {
        setResult({ success: false, error: response.data.error });
      }
    } catch (error) {
      console.error('Error generating staging:', error);
      setResult({ 
        success: false, 
        error: error.response?.data?.error || error.message || 'Failed to generate staged image' 
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadImage = () => {
    if (result?.image) {
      const link = document.createElement('a');
      link.href = result.image;
      link.download = `staged-${roomType}-${style}-${Date.now()}.png`;
      link.click();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Home className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">Virtual Staging</h2>
            <p className="text-indigo-100">Transform empty rooms into beautifully furnished spaces</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Left Column - Upload & Settings */}
        <div className="space-y-4">
          {/* Image Upload */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-purple-600" />
              Upload Room Photo
            </h3>
            
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
                previewUrl ? 'border-purple-400 bg-purple-50' : 'border-gray-300 hover:border-purple-400'
              }`}
              onClick={() => document.getElementById('staging-file-input').click()}
            >
              {previewUrl ? (
                <div className="relative">
                  <img 
                    src={previewUrl} 
                    alt="Selected room" 
                    className="max-h-48 mx-auto rounded-lg shadow-md"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setPreviewUrl(null);
                      setResult(null);
                    }}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <p className="text-sm text-gray-600 mt-2">{selectedFile?.name}</p>
                </div>
              ) : (
                <>
                  <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-1">Drag & drop an empty room photo</p>
                  <p className="text-purple-600 font-medium">or click to browse</p>
                  <p className="text-xs text-gray-400 mt-2">JPG, PNG up to 10MB</p>
                </>
              )}
              <input
                id="staging-file-input"
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          </div>

          {/* Room Type Selection */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-800 mb-4">Select Room Type</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {roomTypes.map((room) => (
                <button
                  key={room.id}
                  onClick={() => setRoomType(room.id)}
                  className={`p-3 rounded-xl text-center transition-all ${
                    roomType === room.id
                      ? 'bg-purple-100 border-2 border-purple-500 text-purple-700'
                      : 'bg-gray-50 border border-gray-200 hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <span className="text-2xl block mb-1">{room.icon}</span>
                  <span className="text-xs font-medium">{room.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Style Selection */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-800 mb-4">Choose Staging Style</h3>
            <div className="grid grid-cols-2 gap-2">
              {styles.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setStyle(s.id)}
                  className={`p-3 rounded-xl text-left transition-all ${
                    style === s.id
                      ? 'bg-purple-100 border-2 border-purple-500'
                      : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <p className={`font-medium ${style === s.id ? 'text-purple-700' : 'text-gray-800'}`}>{s.label}</p>
                  <p className="text-xs text-gray-500">{s.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Advanced Settings */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-800 mb-4">Advanced Settings</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transformation Intensity: {Math.round(promptStrength * 100)}%
                </label>
                <input
                  type="range"
                  min="0.3"
                  max="0.9"
                  step="0.1"
                  value={promptStrength}
                  onChange={(e) => setPromptStrength(parseFloat(e.target.value))}
                  className="w-full accent-purple-600"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Subtle</span>
                  <span>Moderate</span>
                  <span>Strong</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Instructions (optional)
                </label>
                <textarea
                  value={additionalInstructions}
                  onChange={(e) => setAdditionalInstructions(e.target.value)}
                  placeholder="e.g., Add warm lighting, include plants, use blue accents..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={generateStaging}
            disabled={!selectedFile || isGenerating}
            className={`w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 transition-all ${
              !selectedFile || isGenerating
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg hover:scale-[1.02]'
            }`}
          >
            {isGenerating ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Generating Staged Image...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate Virtual Staging
              </>
            )}
          </button>
        </div>

        {/* Right Column - Result */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5 min-h-[500px]">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              Staged Result
            </h3>

            {isGenerating && (
              <div className="flex flex-col items-center justify-center h-80">
                <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mb-4" />
                <p className="text-gray-600 font-medium">Creating your staged room...</p>
                <p className="text-sm text-gray-500 mt-1">This may take 30-60 seconds</p>
              </div>
            )}

            {!isGenerating && !result && (
              <div className="flex flex-col items-center justify-center h-80 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                  <Home className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-gray-600 font-medium">Your staged image will appear here</p>
                <p className="text-sm text-gray-500 mt-1">Upload a photo and click generate</p>
              </div>
            )}

            {result && result.success && (
              <div className="space-y-4">
                <div className="relative">
                  <img
                    src={result.image}
                    alt="Staged room"
                    className="w-full rounded-xl shadow-lg"
                  />
                  <div className="absolute top-3 left-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    Staged
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">{roomTypes.find(r => r.id === result.roomType)?.label}</span>
                      {' • '}
                      <span className="font-medium">{styles.find(s => s.id === result.style)?.label}</span> style
                    </p>
                  </div>
                  <button
                    onClick={downloadImage}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>

                {/* Before/After Comparison */}
                {previewUrl && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1 text-center">Before</p>
                      <img src={previewUrl} alt="Original" className="rounded-lg border border-gray-200" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1 text-center">After</p>
                      <img src={result.image} alt="Staged" className="rounded-lg border border-purple-200" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {result && !result.success && (
              <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                <p className="text-red-700 flex items-center gap-2">
                  <X className="w-5 h-5" />
                  {result.error}
                </p>
              </div>
            )}
          </div>

          {/* Tips */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-100">
            <h4 className="font-semibold text-indigo-800 mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Tips for Best Results
            </h4>
            <ul className="space-y-2 text-sm text-indigo-700">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 text-indigo-500" />
                Use well-lit, high-quality photos
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 text-indigo-500" />
                Empty rooms work best for staging
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 text-indigo-500" />
                Shoot from corner angles for better perspective
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 text-indigo-500" />
                Lower intensity preserves more original details
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Compare Areas with Neighborhood Selection Component - Geographic Grouping
const CompareAreasWithSelection = ({ formData, generatedDescription, userId, API }) => {
  const [neighborhoodData, setNeighborhoodData] = useState(null);
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]);
  const [isLoadingNeighborhoods, setIsLoadingNeighborhoods] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [city, setCity] = useState('');
  
  // Listing selection state
  const [savedListings, setSavedListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [isLoadingListings, setIsLoadingListings] = useState(false);
  
  // PDF Export state
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfBranding, setPdfBranding] = useState({});
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  
  // Active property data - either from selected listing or formData
  const activePropertyData = selectedListing || formData;

  // Load saved listings on mount
  useEffect(() => {
    fetchSavedListings();
  }, [userId]);

  const fetchSavedListings = async () => {
    if (!userId) return;
    setIsLoadingListings(true);
    try {
      const res = await axios.get(`${API}/my-listings?user_id=${userId}`);
      if (res.data && Array.isArray(res.data)) {
        setSavedListings(res.data);
      } else if (res.data && res.data.listings && Array.isArray(res.data.listings)) {
        setSavedListings(res.data.listings);
      } else {
        setSavedListings([]);
      }
    } catch (error) {
      console.error('Error fetching listings:', error);
      setSavedListings([]);
    } finally {
      setIsLoadingListings(false);
    }
  };

  const handleListingSelect = (listingId) => {
    if (!listingId) {
      setSelectedListing(null);
      setNeighborhoodData(null);
      setCity('');
      setResult(null);
      setSelectedNeighborhoods([]);
      setSelectedArea('');
      return;
    }
    const listing = savedListings.find(l => l.id === listingId);
    if (listing) {
      const listingData = {
        id: listing.id,
        address: listing.address,
        city: listing.city || '',
        state: listing.state || '',
        zipCode: listing.zip_code || listing.zipCode || '',
        country: listing.country || 'USA',
        bedrooms: listing.bedrooms?.toString() || '',
        bathrooms: listing.bathrooms?.toString() || '',
        price: listing.price ? `$${Number(listing.price).toLocaleString()}` : '',
        propertyType: listing.property_type || listing.propertyType || '',
        squareFootage: listing.square_footage?.toString() || listing.squareFootage?.toString() || '',
        description: listing.description || ''
      };
      setSelectedListing(listingData);
      
      if (listing.city) {
        setCity(listing.city);
      }
      
      setResult(null);
      setSelectedNeighborhoods([]);
      setNeighborhoodData(null);
      setSelectedArea('');
    }
  };

  // Load neighborhoods when listing or city changes
  useEffect(() => {
    const cityValue = selectedListing?.city || formData?.city;
    if (cityValue) {
      loadNeighborhoods();
    }
  }, [selectedListing?.city, selectedListing?.id]);

  useEffect(() => {
    const cityValue = formData?.city;
    if (cityValue && !selectedListing && !neighborhoodData) {
      loadNeighborhoods();
    }
  }, [formData?.city]);

  const loadNeighborhoods = async () => {
    const cityValue = selectedListing?.city || formData?.city;
    const stateValue = selectedListing?.state || formData?.state;
    const countryValue = selectedListing?.country || formData?.country || 'USA';
    
    if (!cityValue) {
      return;
    }

    setIsLoadingNeighborhoods(true);
    try {
      const res = await axios.post(`${API}/premium/neighborhoods/list?user_id=${userId}`, {
        address: selectedListing?.address || formData?.address || '',
        city: cityValue,
        state: stateValue,
        stateProvince: stateValue,
        country: countryValue
      });

      if (res.data.success) {
        setNeighborhoodData(res.data);
        setCity(res.data.city || cityValue);
        
        // Set first area as default selected tab
        if (res.data.areas) {
          const firstArea = Object.keys(res.data.areas)[0];
          setSelectedArea(firstArea);
        }
      } else {
        alert(res.data.error || 'Failed to load neighborhoods');
      }
    } catch (error) {
      console.error('Error loading neighborhoods:', error);
      alert('Failed to load neighborhoods. Please ensure city is filled in Generate Listing.');
    } finally {
      setIsLoadingNeighborhoods(false);
    }
  };

  const toggleNeighborhood = (neighborhood) => {
    if (selectedNeighborhoods.includes(neighborhood)) {
      setSelectedNeighborhoods(selectedNeighborhoods.filter(n => n !== neighborhood));
    } else if (selectedNeighborhoods.length < 3) {
      setSelectedNeighborhoods([...selectedNeighborhoods, neighborhood]);
    } else {
      alert('Maximum 3 neighborhoods. Deselect one first.');
    }
  };

  const generateComparison = async () => {
    if (selectedNeighborhoods.length !== 3) {
      alert('Please select exactly 3 neighborhoods to compare');
      return;
    }

    const cityValue = selectedListing?.city || formData?.city || city;
    const stateValue = selectedListing?.state || formData?.state;
    const countryValue = selectedListing?.country || formData?.country || 'USA';

    setIsGenerating(true);
    try {
      const res = await axios.post(`${API}/premium/neighborhood-compare?user_id=${userId}`, {
        selectedNeighborhoods,
        address: selectedListing?.address || formData?.address || '',
        city: cityValue,
        state: stateValue,
        zipCode: selectedListing?.zipCode || formData?.zipCode || '',
        country: countryValue,
        description: selectedListing?.description || generatedDescription || '',
        bedrooms: selectedListing?.bedrooms || formData?.beds || '',
        bathrooms: selectedListing?.bathrooms || formData?.baths || '',
        price: selectedListing?.price || formData?.price || '',
        propertyType: selectedListing?.propertyType || formData?.propertyType || '',
        squareFootage: selectedListing?.squareFootage || formData?.sqft || ''
      });

      if (res.data.success) {
        setResult(res.data.comparison);
      } else {
        alert(res.data.error || 'Failed to generate comparison');
      }
    } catch (error) {
      console.error('Error generating comparison:', error);
      alert('Failed to generate comparison. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Generate PDF for comparison report
  const generateComparisonPdf = async () => {
    setIsGeneratingPdf(true);
    try {
      // Build communities array from neighborhood results with ALL detailed data
      // Each neighborhood has nested objects like safety: { score: 7, rating: "Good", highlights: [...], ... }
      const communities = result.neighborhoods?.map((hood) => ({
        name: hood.name,
        overallScore: hood.overallScore || 0,
        bestFor: hood.bestFor || "",
        description: hood.summary || hood.description || "",
        summary: hood.summary || "",
        
        // Send complete safety data (object with score, rating, highlights, considerations)
        safety: hood.safety || { score: 0 },
        
        // Send complete schools data (object with score, rating, highlights, topSchools)
        schools: hood.schools || { score: 0 },
        
        // Send complete commute data (object with score, rating, downtown, transitAccess, highlights)
        commute: hood.commute || { score: 0 },
        
        // Send complete amenities data (object with score, rating, highlights, topSpots)
        amenities: hood.amenities || { score: 0 },
        
        // Send complete greenSpace data (object with score, rating, walkScore, highlights, parks)
        greenSpace: hood.greenSpace || { score: 0 }
      })) || [];

      // Build recommendation from result if available
      // recommendation can be an object with topChoice/reasoning or a string
      let recommendation = "";
      if (result.recommendation) {
        if (typeof result.recommendation === 'string') {
          recommendation = result.recommendation;
        } else if (result.recommendation.reasoning) {
          const rec = result.recommendation;
          recommendation = `${rec.reasoning} Top choice: ${rec.topChoice || 'N/A'}.`;
          if (rec.bestForFamilies) recommendation += ` Best for families: ${rec.bestForFamilies}.`;
          if (rec.bestForYoungProfessionals) recommendation += ` Best for young professionals: ${rec.bestForYoungProfessionals}.`;
        }
      }

      const response = await axios.post(`${API}/premium/pdf-export/compare-area?user_id=${userId}`, {
        communities,
        recommendation,
        city: city || formData?.city || "",
        branding: pdfBranding
      });

      if (response.data.success && response.data.pdfData) {
        // Create download link
        const link = document.createElement('a');
        link.href = response.data.pdfData;
        link.download = response.data.filename || 'community-comparison-report.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setShowPdfModal(false);
      } else {
        alert(response.data.error || 'Failed to generate PDF');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Get areas and current neighborhoods
  const areas = neighborhoodData?.areas ? Object.keys(neighborhoodData.areas) : [];
  const currentNeighborhoods = neighborhoodData?.areas?.[selectedArea] || [];

  return (
    <div className="space-y-6">
      {/* Listing Selector */}
      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select a Property
        </label>
        <select
          value={selectedListing?.id || ''}
          onChange={(e) => handleListingSelect(e.target.value || null)}
          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-700"
        >
          <option value="">-- Choose from My Listings --</option>
          {isLoadingListings ? (
            <option disabled>Loading listings...</option>
          ) : savedListings.length === 0 ? (
            <option disabled>No saved listings found</option>
          ) : (
            savedListings.map((listing) => (
              <option key={listing.id} value={listing.id}>
                {listing.address}{listing.city ? `, ${listing.city}` : ''}{listing.state ? ` ${listing.state}` : ''} {listing.price ? `- $${Number(listing.price).toLocaleString()}` : ''} {!listing.city ? '⚠️ No city' : ''}
              </option>
            ))
          )}
        </select>
        
        {selectedListing && (
          <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-sm text-purple-800 font-medium">{selectedListing.address}</p>
            <p className="text-xs text-purple-600 mt-1">
              {selectedListing.city && <span className="font-semibold">{selectedListing.city}, {selectedListing.state}</span>}
              {selectedListing.bedrooms && ` • ${selectedListing.bedrooms} bed`}
              {selectedListing.bathrooms && ` • ${selectedListing.bathrooms} bath`}
              {selectedListing.price && ` • ${selectedListing.price}`}
            </p>
            {!selectedListing.city && (
              <p className="text-xs text-orange-600 mt-1 font-medium">
                ⚠️ This listing is missing city info. Please update it in My Listings or use a listing with city data.
              </p>
            )}
          </div>
        )}
        
        {!selectedListing && savedListings.length > 0 && (
          <p className="text-xs text-gray-500 mt-2">
            Or fill out property details in the Generate Listing section
          </p>
        )}
      </div>

      {/* Loading State */}
      {isLoadingNeighborhoods && (
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading neighborhoods in {city || formData?.city}...</p>
          <p className="text-sm text-gray-500 mt-2">Organizing by geographic area...</p>
        </div>
      )}

      {/* No City Warning */}
      {!isLoadingNeighborhoods && !activePropertyData?.city && !formData?.city && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800 text-sm">
            ⚠️ Please fill out the <strong>City</strong> field in Generate Listing, or select a listing with city info above.
          </p>
        </div>
      )}

      {/* Main Content - when neighborhoods loaded */}
      {!isLoadingNeighborhoods && neighborhoodData && !result && (
        <>
          {/* Header with Selection Counter */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-5 rounded-xl border-2 border-purple-200">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-bold text-gray-800 text-lg">
                  Select 3 Neighborhoods in {city || formData?.city}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  ✓ Showing <strong>{neighborhoodData.totalCount}</strong> neighborhoods organized by {neighborhoodData.organizationType || 'area'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="px-4 py-2 bg-purple-600 text-white rounded-full font-bold text-lg">
                  {selectedNeighborhoods.length} / 3
                </div>
                <button
                  onClick={loadNeighborhoods}
                  className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
                  title="Refresh neighborhoods"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Selected Neighborhoods Pills */}
            {selectedNeighborhoods.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-purple-200">
                <span className="text-sm font-medium text-gray-700">Selected:</span>
                {selectedNeighborhoods.map((n, idx) => (
                  <span key={n} className="px-3 py-1 bg-purple-600 text-white rounded-full text-sm flex items-center gap-2">
                    <span className="w-5 h-5 bg-white/30 rounded-full flex items-center justify-center text-xs font-bold">{idx + 1}</span>
                    {n}
                    <button onClick={() => toggleNeighborhood(n)} className="hover:bg-purple-700 rounded-full p-0.5">
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Area Tabs */}
          {areas.length > 1 && (
            <div className="flex flex-wrap gap-2 bg-gray-100 p-2 rounded-xl">
              {areas.map((area) => {
                const count = neighborhoodData.areas[area]?.length || 0;
                const hasSelection = neighborhoodData.areas[area]?.some(n => selectedNeighborhoods.includes(n));
                return (
                  <button
                    key={area}
                    onClick={() => setSelectedArea(area)}
                    className={`flex-1 min-w-[140px] py-3 px-4 rounded-lg font-semibold transition-all ${
                      selectedArea === area
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <div className="text-center">
                      <div className="font-bold flex items-center justify-center gap-2">
                        {area}
                        {hasSelection && <span className="w-2 h-2 bg-green-400 rounded-full"></span>}
                      </div>
                      <div className={`text-xs mt-0.5 ${selectedArea === area ? 'text-white/80' : 'text-gray-500'}`}>
                        {count} neighborhoods
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Neighborhoods Grid for Selected Area */}
          <div>
            <div className="mb-3 text-sm text-gray-600 flex items-center justify-between">
              <span>
                Browsing <strong>{selectedArea}</strong> • {currentNeighborhoods.length} neighborhoods
              </span>
              {selectedNeighborhoods.length > 0 && selectedNeighborhoods.length < 3 && (
                <span className="text-purple-600 font-medium">
                  Select {3 - selectedNeighborhoods.length} more
                </span>
              )}
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 max-h-80 overflow-y-auto p-2 border border-gray-200 rounded-lg bg-gray-50">
              {currentNeighborhoods.map((neighborhood) => {
                const isSelected = selectedNeighborhoods.includes(neighborhood);
                const isDisabled = selectedNeighborhoods.length >= 3 && !isSelected;
                const selectionIndex = selectedNeighborhoods.indexOf(neighborhood);
                
                return (
                  <button
                    key={neighborhood}
                    onClick={() => toggleNeighborhood(neighborhood)}
                    disabled={isDisabled}
                    className={`p-3 rounded-lg border-2 transition-all font-medium text-sm ${
                      isSelected
                        ? 'bg-purple-600 border-purple-600 text-white shadow-lg scale-105'
                        : isDisabled
                        ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-purple-400 hover:bg-purple-50 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-left flex-1 truncate">{neighborhood}</span>
                      {isSelected && (
                        <span className="flex items-center justify-center w-6 h-6 bg-white/30 text-white text-xs font-bold rounded-full flex-shrink-0">
                          {selectionIndex + 1}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Compare Button */}
          <div className="flex justify-center pt-4">
            <button
              onClick={generateComparison}
              disabled={selectedNeighborhoods.length !== 3 || isGenerating}
              className="px-10 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 shadow-lg text-lg"
            >
              {isGenerating ? (
                <>
                  <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                  Analyzing Neighborhoods...
                </>
              ) : (
                <>
                  <Sparkles className="w-6 h-6" />
                  Compare {selectedNeighborhoods.length === 3 ? '3 Selected Neighborhoods' : `(Select ${3 - selectedNeighborhoods.length} More)`}
                </>
              )}
            </button>
          </div>

          {/* Tips */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <strong>Pro Tip:</strong> Use the tabs above to browse neighborhoods by geographic area. 
                Select 3 from anywhere in {city || formData?.city} to compare - you can mix neighborhoods from different areas!
              </div>
            </div>
          </div>
        </>
      )}

      {/* Results Display */}
      {result && (
        <div className="space-y-6">
          {/* Back Button and PDF Export */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                setResult(null);
                setSelectedNeighborhoods([]);
              }}
              className="text-purple-600 hover:text-purple-700 font-semibold flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Select Different Neighborhoods
            </button>
            <button
              onClick={() => setShowPdfModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all flex items-center gap-2"
              data-testid="compare-download-pdf-btn"
            >
              <Download className="w-4 h-4" />
              Download as PDF
            </button>
          </div>

          {/* Neighborhood Cards */}
          {result.neighborhoods && result.neighborhoods.map((hood, idx) => (
            <div key={idx} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Neighborhood Header */}
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-4 text-white">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    {hood.name}
                  </h3>
                  <div className="flex items-center gap-2">
                    <span className="text-3xl font-bold">{hood.overallScore?.toFixed(1) || '7.5'}</span>
                    <span className="text-sm opacity-80">/10</span>
                  </div>
                </div>
                <p className="text-white/80 text-sm mt-1">{hood.bestFor}</p>
              </div>
              
              {/* Quality Categories */}
              <div className="p-4 space-y-4">
                {/* Safety */}
                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                      🛡️ Safety
                    </h4>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                        hood.safety?.score >= 8 ? 'bg-green-100 text-green-700' :
                        hood.safety?.score >= 6 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {hood.safety?.rating || 'Good'}
                      </span>
                      <span className="text-lg font-bold text-gray-700">{hood.safety?.score || 7}/10</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {hood.safety?.highlights?.map((h, i) => (
                      <span key={i} className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-lg">✓ {h}</span>
                    ))}
                    {hood.safety?.considerations?.map((c, i) => (
                      <span key={i} className="px-2 py-1 bg-yellow-50 text-yellow-700 text-xs rounded-lg">⚠ {c}</span>
                    ))}
                  </div>
                </div>

                {/* Schools */}
                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                      🎓 Schools
                    </h4>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                        hood.schools?.score >= 8 ? 'bg-green-100 text-green-700' :
                        hood.schools?.score >= 6 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {hood.schools?.rating || 'Good'}
                      </span>
                      <span className="text-lg font-bold text-gray-700">{hood.schools?.score || 7}/10</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {hood.schools?.highlights?.map((h, i) => (
                      <span key={i} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-lg">✓ {h}</span>
                    ))}
                  </div>
                  {hood.schools?.topSchools?.length > 0 && (
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>Top Schools:</strong> {hood.schools.topSchools.join(', ')}
                    </p>
                  )}
                </div>

                {/* Commute */}
                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                      🚗 Commute & Accessibility
                    </h4>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                        hood.commute?.score >= 8 ? 'bg-green-100 text-green-700' :
                        hood.commute?.score >= 6 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {hood.commute?.rating || 'Good'}
                      </span>
                      <span className="text-lg font-bold text-gray-700">{hood.commute?.score || 7}/10</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-2">
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-xs text-gray-500">Downtown</p>
                      <p className="font-bold text-gray-800">{hood.commute?.downtown || '20 min'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-xs text-gray-500">Transit</p>
                      <p className="font-medium text-gray-800 text-sm">{hood.commute?.transitAccess || 'Bus available'}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {hood.commute?.highlights?.map((h, i) => (
                      <span key={i} className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-lg">✓ {h}</span>
                    ))}
                  </div>
                </div>

                {/* Amenities */}
                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                      🛍️ Amenities & Lifestyle
                    </h4>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                        hood.amenities?.score >= 8 ? 'bg-green-100 text-green-700' :
                        hood.amenities?.score >= 6 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {hood.amenities?.rating || 'Good'}
                      </span>
                      <span className="text-lg font-bold text-gray-700">{hood.amenities?.score || 7}/10</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {hood.amenities?.highlights?.map((h, i) => (
                      <span key={i} className="px-2 py-1 bg-orange-50 text-orange-700 text-xs rounded-lg">✓ {h}</span>
                    ))}
                  </div>
                  {hood.amenities?.topSpots?.length > 0 && (
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>Popular:</strong> {hood.amenities.topSpots.join(', ')}
                    </p>
                  )}
                </div>

                {/* Green Space */}
                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                      🌳 Green Space & Walkability
                    </h4>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                        hood.greenSpace?.score >= 8 ? 'bg-green-100 text-green-700' :
                        hood.greenSpace?.score >= 6 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {hood.greenSpace?.rating || 'Good'}
                      </span>
                      <span className="text-lg font-bold text-gray-700">{hood.greenSpace?.score || 7}/10</span>
                    </div>
                  </div>
                  {hood.greenSpace?.walkScore && (
                    <div className="bg-green-50 rounded-lg p-2 text-center mb-2 inline-block">
                      <p className="text-xs text-green-600">Walk Score</p>
                      <p className="font-bold text-green-700 text-xl">{hood.greenSpace.walkScore}</p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {hood.greenSpace?.highlights?.map((h, i) => (
                      <span key={i} className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-lg">✓ {h}</span>
                    ))}
                  </div>
                  {hood.greenSpace?.parks?.length > 0 && (
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>Parks:</strong> {hood.greenSpace.parks.join(', ')}
                    </p>
                  )}
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-700">{hood.summary}</p>
                </div>
              </div>
            </div>
          ))}

          {/* Recommendation */}
          {result.recommendation && (
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-6 border border-purple-200">
              <h3 className="font-bold text-purple-800 text-lg mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Our Recommendation
              </h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-purple-600 font-medium">Top Choice:</span>
                  <p className="text-lg font-bold text-gray-800">{result.recommendation.topChoice}</p>
                  <p className="text-gray-600">{result.recommendation.reasoning}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-3 border-t border-purple-200">
                  <div>
                    <p className="text-xs text-purple-600 font-medium">Best for Families</p>
                    <p className="font-semibold text-gray-800">{result.recommendation.bestForFamilies}</p>
                  </div>
                  <div>
                    <p className="text-xs text-purple-600 font-medium">Best for Professionals</p>
                    <p className="font-semibold text-gray-800">{result.recommendation.bestForYoungProfessionals}</p>
                  </div>
                  <div>
                    <p className="text-xs text-purple-600 font-medium">Best Value</p>
                    <p className="font-semibold text-gray-800">{result.recommendation.bestValue}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* PDF Export Modal */}
      {showPdfModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPdfModal(false)}
        >
          <div 
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-purple-600" />
                  Download Comparison as PDF
                </h3>
                <button
                  onClick={() => setShowPdfModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Summary of selected properties */}
              <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                <h4 className="font-semibold text-purple-800 mb-3">Selected Neighborhoods</h4>
                <div className="flex flex-wrap gap-2">
                  {result?.neighborhoods?.map((hood, idx) => (
                    <span key={idx} className="px-3 py-1 bg-white text-purple-700 rounded-full text-sm border border-purple-200">
                      {hood.name} ({hood.overallScore?.toFixed(1) || 'N/A'}/10)
                    </span>
                  ))}
                </div>
              </div>

              {/* Branding Panel */}
              <BrandingPanel 
                onBrandingChange={setPdfBranding}
                initialBranding={pdfBranding}
              />

              {/* Generate Button */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowPdfModal(false)}
                  className="px-6 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={generateComparisonPdf}
                  disabled={isGeneratingPdf}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 flex items-center gap-2"
                  data-testid="compare-generate-pdf-btn"
                >
                  {isGeneratingPdf ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5" />
                      Generate PDF
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Photo Library Modal Component
const PhotoLibraryModal = ({ listing, isOpen, onClose, API, userId, onPhotosUpdated }) => {
  const [photos, setPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [editingPhoto, setEditingPhoto] = useState(null);

  const photoCategories = [
    { id: 'all', label: 'All Photos', icon: '📷' },
    { id: 'exterior', label: 'Exterior', icon: '🏠' },
    { id: 'interior', label: 'Interior', icon: '🛋️' },
    { id: 'kitchen', label: 'Kitchen', icon: '🍳' },
    { id: 'bathroom', label: 'Bathroom', icon: '🚿' },
    { id: 'bedroom', label: 'Bedroom', icon: '🛏️' },
    { id: 'backyard', label: 'Backyard', icon: '🌳' },
    { id: 'other', label: 'Other', icon: '📁' }
  ];

  useEffect(() => {
    if (isOpen && listing) {
      fetchPhotos();
    }
  }, [isOpen, listing]);

  const fetchPhotos = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/listings/${listing.id}/photos?user_id=${userId}`);
      setPhotos(res.data.photos || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    
    for (const file of files) {
      try {
        // Convert to base64
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
        
        await axios.post(`${API}/listings/${listing.id}/photos?user_id=${userId}`, {
          listing_id: listing.id,
          filename: file.name,
          url: base64,
          category: selectedCategory === 'all' ? 'exterior' : selectedCategory
        });
      } catch (error) {
        console.error('Error uploading photo:', error);
      }
    }
    
    setUploading(false);
    fetchPhotos();
    if (onPhotosUpdated) onPhotosUpdated();
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      await axios.delete(`${API}/listings/${listing.id}/photos/${photoId}?user_id=${userId}`);
      fetchPhotos();
      if (onPhotosUpdated) onPhotosUpdated();
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  const handleSetPrimary = async (photoId) => {
    try {
      await axios.patch(`${API}/listings/${listing.id}/photos/${photoId}/primary?user_id=${userId}`);
      fetchPhotos();
      if (onPhotosUpdated) onPhotosUpdated();
    } catch (error) {
      console.error('Error setting primary photo:', error);
    }
  };

  const handleUpdatePhoto = async (photoId, updates) => {
    try {
      await axios.put(`${API}/listings/${listing.id}/photos/${photoId}?user_id=${userId}`, updates);
      fetchPhotos();
      setEditingPhoto(null);
    } catch (error) {
      console.error('Error updating photo:', error);
    }
  };

  const filteredPhotos = selectedCategory === 'all' 
    ? photos 
    : photos.filter(p => p.category === selectedCategory);

  const primaryPhoto = photos.find(p => p.is_primary);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-600 to-blue-600">
          <div className="text-white">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Photo Library
            </h2>
            <p className="text-sm text-white/80">{listing?.address}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-2">
            {/* Category Filter */}
            <select 
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white"
            >
              {photoCategories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.label} ({cat.id === 'all' ? photos.length : photos.filter(p => p.category === cat.id).length})
                </option>
              ))}
            </select>

            {/* View Mode Toggle */}
            <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden bg-white">
              <button 
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-purple-100 text-purple-600' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <ImageIcon className="w-4 h-4" />
              </button>
              <button 
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-purple-100 text-purple-600' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <Menu className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Upload Button */}
          <label className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-blue-700 transition-all cursor-pointer flex items-center gap-2">
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload Photos
              </>
            )}
            <input 
              type="file" 
              accept="image/*" 
              multiple 
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploading}
            />
          </label>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredPhotos.length === 0 ? (
            <div className="text-center py-16">
              <Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No photos yet</h3>
              <p className="text-gray-500 mb-4">Upload photos to create a visual library for this listing</p>
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl cursor-pointer hover:bg-purple-700">
                <Upload className="w-4 h-4" />
                Upload First Photo
                <input type="file" accept="image/*" multiple onChange={handleFileUpload} className="hidden" />
              </label>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredPhotos.map((photo) => (
                <div 
                  key={photo.id} 
                  className={`relative group rounded-xl overflow-hidden border-2 ${photo.is_primary ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200'} bg-gray-100`}
                >
                  <div className="aspect-square">
                    <img 
                      src={photo.url} 
                      alt={photo.filename}
                      className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => setSelectedPhoto(photo)}
                    />
                  </div>
                  
                  {/* Primary Badge */}
                  {photo.is_primary && (
                    <div className="absolute top-2 left-2 px-2 py-1 bg-purple-600 text-white text-xs rounded-full font-medium">
                      ⭐ Cover
                    </div>
                  )}

                  {/* Category Badge */}
                  <div className="absolute top-2 right-2 px-2 py-1 bg-black/50 text-white text-xs rounded-full capitalize">
                    {photo.category}
                  </div>

                  {/* Hover Actions */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button 
                      onClick={() => setSelectedPhoto(photo)}
                      className="p-2 bg-white rounded-xl hover:bg-gray-100"
                      title="View"
                    >
                      <Eye className="w-4 h-4 text-gray-700" />
                    </button>
                    {!photo.is_primary && (
                      <button 
                        onClick={() => handleSetPrimary(photo.id)}
                        className="p-2 bg-white rounded-xl hover:bg-gray-100"
                        title="Set as Cover"
                      >
                        <Star className="w-4 h-4 text-yellow-500" />
                      </button>
                    )}
                    <button 
                      onClick={() => setEditingPhoto(photo)}
                      className="p-2 bg-white rounded-xl hover:bg-gray-100"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4 text-blue-600" />
                    </button>
                    <button 
                      onClick={() => handleDeletePhoto(photo.id)}
                      className="p-2 bg-white rounded-xl hover:bg-gray-100"
                      title="Delete"
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </button>
                  </div>

                  {/* Caption */}
                  {photo.caption && (
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                      <p className="text-white text-xs truncate">{photo.caption}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredPhotos.map((photo) => (
                <div 
                  key={photo.id}
                  className={`flex items-center gap-4 p-3 rounded-xl border ${photo.is_primary ? 'border-purple-500 bg-purple-50' : 'border-gray-200 bg-white'} hover:bg-gray-50`}
                >
                  <img 
                    src={photo.url} 
                    alt={photo.filename}
                    className="w-16 h-16 rounded-lg object-cover cursor-pointer"
                    onClick={() => setSelectedPhoto(photo)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-800 truncate">{photo.filename}</p>
                      {photo.is_primary && (
                        <span className="px-2 py-0.5 bg-purple-600 text-white text-xs rounded-full">Cover</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span className="capitalize">{photo.category}</span>
                      {photo.caption && <span>• {photo.caption}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!photo.is_primary && (
                      <button 
                        onClick={() => handleSetPrimary(photo.id)}
                        className="p-2 hover:bg-yellow-100 rounded-lg"
                        title="Set as Cover"
                      >
                        <Star className="w-4 h-4 text-yellow-500" />
                      </button>
                    )}
                    <button 
                      onClick={() => setEditingPhoto(photo)}
                      className="p-2 hover:bg-blue-100 rounded-lg"
                    >
                      <Edit className="w-4 h-4 text-blue-600" />
                    </button>
                    <button 
                      onClick={() => handleDeletePhoto(photo.id)}
                      className="p-2 hover:bg-red-100 rounded-lg"
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer Stats */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>{photos.length} photos total</span>
            {primaryPhoto && (
              <span className="flex items-center gap-1">
                <Star className="w-4 h-4 text-yellow-500" />
                Cover photo set
              </span>
            )}
          </div>
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-xl">
            Close
          </button>
        </div>
      </div>

      {/* Photo Viewer Modal */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-60" onClick={() => setSelectedPhoto(null)}>
          <button 
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-xl"
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src={selectedPhoto.url} 
            alt={selectedPhoto.filename}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black/70 rounded-xl px-6 py-3 text-white text-center">
            <p className="font-medium">{selectedPhoto.filename}</p>
            {selectedPhoto.caption && <p className="text-sm text-gray-300">{selectedPhoto.caption}</p>}
          </div>
        </div>
      )}

      {/* Edit Photo Modal */}
      {editingPhoto && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-60" onClick={() => setEditingPhoto(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-800 mb-4">Edit Photo</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Caption</label>
                <input 
                  type="text" 
                  value={editingPhoto.caption || ''}
                  onChange={(e) => setEditingPhoto({...editingPhoto, caption: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400"
                  placeholder="Add a caption..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select 
                  value={editingPhoto.category}
                  onChange={(e) => setEditingPhoto({...editingPhoto, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl"
                >
                  {photoCategories.filter(c => c.id !== 'all').map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditingPhoto(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl">
                Cancel
              </button>
              <button 
                onClick={() => handleUpdatePhoto(editingPhoto.id, {
                  caption: editingPhoto.caption,
                  category: editingPhoto.category
                })}
                className="px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// My Listings Section Component (Functional)
const MyListingsSection = ({ userId, API }) => {
  const [listings, setListings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState({ active: 0, sold: 0, pending: 0, total: 0, documents: 0, clients: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPhotoLibrary, setShowPhotoLibrary] = useState(null); // Holds the listing to show photos for
  const [showDocModal, setShowDocModal] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [newListing, setNewListing] = useState({
    address: '', city: '', state: '', zip_code: '', price: 0,
    bedrooms: 0, bathrooms: 0, square_footage: 0, property_type: 'Single Family Home',
    status: 'active', client_name: '', client_email: '', client_phone: '',
    client_type: 'buyer', description: '', key_features: [], images: []
  });
  const [newDocument, setNewDocument] = useState({
    name: '', category: 'other', listing_id: '', notes: '', file: null
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedDocCategory, setSelectedDocCategory] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchData();
    }
  }, [userId]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [listingsRes, docsRes, activitiesRes, statsRes] = await Promise.all([
        axios.get(`${API}/my-listings?user_id=${userId}`),
        axios.get(`${API}/documents?user_id=${userId}`),
        axios.get(`${API}/activities?user_id=${userId}&limit=10`),
        axios.get(`${API}/my-listings/stats?user_id=${userId}`)
      ]);
      setListings(listingsRes.data.listings || []);
      setDocuments(docsRes.data.documents || []);
      setActivities(activitiesRes.data.activities || []);
      setStats({
        active: listingsRes.data.stats?.active || 0,
        sold: listingsRes.data.stats?.sold || 0,
        pending: listingsRes.data.stats?.pending || 0,
        total: listingsRes.data.stats?.total || 0,
        documents: statsRes.data.documents || 0,
        clients: statsRes.data.clients || 0
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddListing = async () => {
    try {
      await axios.post(`${API}/my-listings?user_id=${userId}`, newListing);
      setShowAddModal(false);
      setNewListing({
        address: '', city: '', state: '', zip_code: '', price: 0,
        bedrooms: 0, bathrooms: 0, square_footage: 0, property_type: 'Single Family Home',
        status: 'active', client_name: '', client_email: '', client_phone: '',
        client_type: 'buyer', description: '', key_features: [], images: []
      });
      fetchData();
    } catch (error) {
      console.error('Error adding listing:', error);
    }
  };

  const handleUpdateListing = async () => {
    if (!editingListing) return;
    try {
      await axios.put(`${API}/my-listings/${editingListing.id}?user_id=${userId}`, editingListing);
      setEditingListing(null);
      fetchData();
    } catch (error) {
      console.error('Error updating listing:', error);
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    try {
      await axios.delete(`${API}/my-listings/${listingId}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting listing:', error);
    }
  };

  const handleStatusChange = async (listingId, newStatus) => {
    try {
      await axios.patch(`${API}/my-listings/${listingId}/status?status=${newStatus}&user_id=${userId}`);
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleAddDocument = async () => {
    if (!newDocument.file) {
      alert('Please select a file to upload');
      return;
    }
    if (!newDocument.name) {
      alert('Please enter a document name');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', newDocument.file);
      formData.append('name', newDocument.name);
      formData.append('category', newDocument.category);
      formData.append('notes', newDocument.notes || '');
      if (newDocument.listing_id) {
        formData.append('listing_id', newDocument.listing_id);
      }
      
      await axios.post(`${API}/documents/upload?user_id=${userId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      setShowDocModal(false);
      setNewDocument({ name: '', category: 'other', listing_id: '', notes: '', file: null });
      setUploadProgress(0);
      // Force refresh documents data
      const docsRes = await axios.get(`${API}/documents?user_id=${userId}`);
      setDocuments(docsRes.data.documents || []);
      fetchData();
    } catch (error) {
      console.error('Error uploading document:', error);
      alert(error.response?.data?.detail || 'Error uploading document. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await axios.delete(`${API}/documents/${docId}?user_id=${userId}`);
      // Immediately update local state for responsive UI
      setDocuments(prev => prev.filter(d => d.id !== docId));
      // Also refresh all data to ensure consistency
      fetchData();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Error deleting document. Please try again.');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.id}/download?user_id=${userId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename || `${doc.name}.${doc.file_type}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Error downloading document. The file may not exist.');
    }
  };

  const getListingNameById = (listingId) => {
    const listing = listings.find(l => l.id === listingId);
    return listing ? `${listing.address}, ${listing.city}` : 'No listing';
  };

  const getDocsForCategory = (categoryId) => {
    return documents.filter(d => d.category === categoryId);
  };

  const filteredListings = listings.filter(l => {
    const matchesSearch = l.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         l.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || l.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const documentCategories = [
    { id: 'purchase_agreement', label: 'Purchase Agreements', color: 'red' },
    { id: 'listing_agreement', label: 'Listing Agreements', color: 'blue' },
    { id: 'disclosure', label: 'Disclosure Forms', color: 'green' },
    { id: 'inspection', label: 'Inspection Reports', color: 'yellow' },
    { id: 'contract', label: 'Client Contracts', color: 'purple' },
    { id: 'other', label: 'Other Documents', color: 'gray' }
  ];

  const getDocCountByCategory = (cat) => documents.filter(d => d.category === cat).length;

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return `${Math.floor(diff / 86400)} days ago`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">My Listings</h1>
          <p className="text-gray-500">Manage your property listings and documents</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-blue-700 transition-all flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Listing
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Home className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-gray-800">{stats.active}</p>
              <p className="text-xs text-gray-500 truncate">Active Listings</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-gray-800">{stats.sold}</p>
              <p className="text-xs text-gray-500 truncate">Sold This Year</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-gray-800">{stats.documents}</p>
              <p className="text-xs text-gray-500 truncate">Documents</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-gray-800">{stats.clients}</p>
              <p className="text-xs text-gray-500 truncate">Clients</p>
            </div>
          </div>
        </div>
      </div>

      {/* Listings Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="font-semibold text-gray-800">Property Listings ({filteredListings.length})</h2>
            <div className="flex items-center gap-2">
              <input 
                type="text" 
                placeholder="Search..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 sm:flex-none px-3 py-1.5 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <select 
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-200 rounded-xl text-sm text-gray-600"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="sold">Sold</option>
              </select>
            </div>
          </div>
        </div>
        
        {filteredListings.length === 0 ? (
          <div className="p-8 text-center">
            <Home className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No listings yet. Add your first listing!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Property</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase hidden sm:table-cell">Client</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Price</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase hidden md:table-cell">Photos</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
              {filteredListings.map((listing) => (
                <tr key={listing.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center overflow-hidden">
                        {listing.primary_photo ? (
                          <img src={listing.primary_photo} alt={listing.address} className="w-full h-full object-cover" />
                        ) : (
                          <Home className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{listing.address}</p>
                        <p className="text-xs text-gray-500">{listing.city}, {listing.state} {listing.zip_code}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <p className="text-sm text-gray-700">{listing.client_name || 'No client'}</p>
                    <p className="text-xs text-gray-500 capitalize">{listing.client_type}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-800">${listing.price?.toLocaleString()}</p>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <button 
                      onClick={() => setShowPhotoLibrary(listing)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-xl text-sm font-medium transition-colors"
                      data-testid={`photo-library-btn-${listing.id}`}
                    >
                      <Camera className="w-4 h-4" />
                      <span>{listing.photo_count || 0} Photos</span>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={listing.status}
                      onChange={(e) => handleStatusChange(listing.id, e.target.value)}
                      className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${
                        listing.status === 'active' ? 'bg-green-100 text-green-700' :
                        listing.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        listing.status === 'sold' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}
                    >
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="sold">Sold</option>
                      <option value="archived">Archived</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => setShowPhotoLibrary(listing)}
                        className="p-1.5 hover:bg-purple-50 rounded-xl" 
                        title="Photo Library"
                      >
                        <Camera className="w-4 h-4 text-purple-600" />
                      </button>
                      <button 
                        onClick={() => { setNewDocument({...newDocument, listing_id: listing.id, name: '', category: 'other', notes: '', file: null}); setShowDocModal(true); }}
                        className="p-1.5 hover:bg-blue-50 rounded-xl" 
                        title="Add Document"
                      >
                        <FileText className="w-4 h-4 text-blue-600" />
                      </button>
                      <button 
                        onClick={() => setEditingListing(listing)}
                        className="p-1.5 hover:bg-gray-100 rounded-xl" 
                        title="Edit"
                      >
                        <Edit className="w-4 h-4 text-gray-500" />
                      </button>
                      <button 
                        onClick={() => handleDeleteListing(listing.id)}
                        className="p-1.5 hover:bg-red-50 rounded-xl" 
                        title="Delete"
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Documents & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Document Categories */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-800">Document Library</h2>
            <span className="text-sm text-gray-500">{documents.length} total</span>
          </div>
          
          {/* Category List or Expanded View */}
          {!selectedDocCategory ? (
            <>
              <div className="space-y-3">
                {documentCategories.map((cat) => (
                  <div 
                    key={cat.id} 
                    onClick={() => setSelectedDocCategory(cat)}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 bg-${cat.color}-100 rounded-xl flex items-center justify-center`}>
                        <FileText className={`w-5 h-5 text-${cat.color}-600`} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{cat.label}</p>
                        <p className="text-xs text-gray-500">{getDocCountByCategory(cat.id)} documents</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                ))}
              </div>
              <button 
                onClick={() => setShowDocModal(true)}
                className="w-full mt-4 py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-purple-500 hover:text-purple-600 transition-colors flex items-center justify-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Add New Document
              </button>
            </>
          ) : (
            /* Expanded Category View */
            <div>
              <button 
                onClick={() => setSelectedDocCategory(null)}
                className="flex items-center gap-2 text-purple-600 hover:text-purple-700 mb-4"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to Categories
              </button>
              <div className={`bg-${selectedDocCategory.color}-50 rounded-xl p-3 mb-4`}>
                <h3 className={`font-semibold text-${selectedDocCategory.color}-700`}>{selectedDocCategory.label}</h3>
                <p className="text-sm text-gray-600">{getDocsForCategory(selectedDocCategory.id).length} documents</p>
              </div>
              
              {getDocsForCategory(selectedDocCategory.id).length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No documents in this category</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {getDocsForCategory(selectedDocCategory.id).map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 group">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                          <FileText className="w-4 h-4 text-gray-500" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-800 text-sm truncate">{doc.name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {doc.listing_id ? getListingNameById(doc.listing_id) : 'No listing'} 
                            {doc.file_type && ` • ${doc.file_type.toUpperCase()}`}
                            {doc.file_size > 0 && ` • ${(doc.file_size / 1024 / 1024).toFixed(1)}MB`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {doc.file_path && (
                          <button 
                            onClick={() => handleDownloadDocument(doc)}
                            className="p-1.5 hover:bg-purple-100 rounded-lg"
                            title="Download"
                          >
                            <Download className="w-4 h-4 text-purple-600" />
                          </button>
                        )}
                        <button 
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="p-1.5 hover:bg-red-100 rounded-lg"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <button 
                onClick={() => { setShowDocModal(true); setNewDocument(prev => ({...prev, category: selectedDocCategory.id})); }}
                className="w-full mt-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Add to {selectedDocCategory.label}
              </button>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-800">Recent Activity</h2>
          </div>
          {activities.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No recent activity</p>
          ) : (
            <div className="space-y-4">
              {activities.map((activity) => (
                <div key={activity.id} className="flex gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    activity.activity_type === 'listing_created' ? 'bg-purple-100' :
                    activity.activity_type === 'document_uploaded' ? 'bg-blue-100' :
                    activity.activity_type === 'status_changed' ? 'bg-green-100' :
                    'bg-gray-100'
                  }`}>
                    {activity.activity_type === 'listing_created' && <Home className="w-4 h-4 text-purple-600" />}
                    {activity.activity_type === 'document_uploaded' && <Upload className="w-4 h-4 text-blue-600" />}
                    {activity.activity_type === 'status_changed' && <Check className="w-4 h-4 text-green-600" />}
                    {activity.activity_type === 'client_added' && <Users className="w-4 h-4 text-yellow-600" />}
                  </div>
                  <div>
                    <p className="text-sm text-gray-800">{activity.description}</p>
                    <p className="text-xs text-gray-500">{formatTimeAgo(activity.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Listing Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">Add New Listing</h2>
              <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-gray-100 rounded-xl">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                <input type="text" value={newListing.address} onChange={(e) => setNewListing({...newListing, address: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent" placeholder="123 Main Street" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input type="text" value={newListing.city} onChange={(e) => setNewListing({...newListing, city: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent" placeholder="Seattle" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input type="text" value={newListing.state} onChange={(e) => setNewListing({...newListing, state: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent" placeholder="WA" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                <input type="number" value={newListing.price} onChange={(e) => setNewListing({...newListing, price: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent" placeholder="500000" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
                <select value={newListing.property_type} onChange={(e) => setNewListing({...newListing, property_type: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                  <option>Single Family Home</option>
                  <option>Condo</option>
                  <option>Townhouse</option>
                  <option>Multi-Family</option>
                  <option>Land</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bedrooms</label>
                <input type="number" value={newListing.bedrooms} onChange={(e) => setNewListing({...newListing, bedrooms: parseInt(e.target.value) || 0})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bathrooms</label>
                <input type="number" step="0.5" value={newListing.bathrooms} onChange={(e) => setNewListing({...newListing, bathrooms: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Square Footage</label>
                <input type="number" value={newListing.square_footage} onChange={(e) => setNewListing({...newListing, square_footage: parseInt(e.target.value) || 0})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={newListing.status} onChange={(e) => setNewListing({...newListing, status: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="sold">Sold</option>
                </select>
              </div>
              <div className="col-span-2 border-t pt-4 mt-2">
                <h3 className="font-medium text-gray-800 mb-3">Client Information</h3>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                <input type="text" value={newListing.client_name} onChange={(e) => setNewListing({...newListing, client_name: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Type</label>
                <select value={newListing.client_type} onChange={(e) => setNewListing({...newListing, client_type: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                  <option value="buyer">Buyer</option>
                  <option value="seller">Seller</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Email</label>
                <input type="email" value={newListing.client_email} onChange={(e) => setNewListing({...newListing, client_email: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Phone</label>
                <input type="tel" value={newListing.client_phone} onChange={(e) => setNewListing({...newListing, client_phone: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
              <button onClick={handleAddListing} className="px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700">Add Listing</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Listing Modal */}
      {editingListing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">Edit Listing</h2>
              <button onClick={() => setEditingListing(null)} className="p-2 hover:bg-gray-100 rounded-xl">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input type="text" value={editingListing.address} onChange={(e) => setEditingListing({...editingListing, address: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input type="text" value={editingListing.city} onChange={(e) => setEditingListing({...editingListing, city: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input type="text" value={editingListing.state} onChange={(e) => setEditingListing({...editingListing, state: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                <input type="number" value={editingListing.price} onChange={(e) => setEditingListing({...editingListing, price: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                <input type="text" value={editingListing.client_name} onChange={(e) => setEditingListing({...editingListing, client_name: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditingListing(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
              <button onClick={handleUpdateListing} className="px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Document Modal */}
      {showDocModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">Upload Document</h2>
              <button onClick={() => { setShowDocModal(false); setNewDocument({ name: '', category: 'other', listing_id: '', notes: '', file: null }); }} className="p-2 hover:bg-gray-100 rounded-xl">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              {/* File Upload Area */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select File *</label>
                <div 
                  className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                    newDocument.file ? 'border-purple-400 bg-purple-50' : 'border-gray-300 hover:border-purple-400'
                  }`}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const file = e.dataTransfer.files[0];
                    if (file) {
                      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
                      if (validTypes.includes(file.type)) {
                        setNewDocument({...newDocument, file, name: newDocument.name || file.name.replace(/\.[^/.]+$/, "")});
                      } else {
                        alert('Invalid file type. Please upload PDF, DOC, DOCX, JPG, or PNG files.');
                      }
                    }
                  }}
                >
                  {newDocument.file ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="w-8 h-8 text-purple-600" />
                      <div className="text-left">
                        <p className="font-medium text-gray-800">{newDocument.file.name}</p>
                        <p className="text-sm text-gray-500">{(newDocument.file.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setNewDocument({...newDocument, file: null}); }}
                        className="p-1 hover:bg-purple-100 rounded-lg"
                      >
                        <X className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600">Drag & drop a file here, or</p>
                      <label className="cursor-pointer">
                        <span className="text-purple-600 hover:text-purple-700 font-medium">browse</span>
                        <input 
                          type="file" 
                          className="hidden" 
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setNewDocument({...newDocument, file, name: newDocument.name || file.name.replace(/\.[^/.]+$/, "")});
                            }
                          }}
                        />
                      </label>
                      <p className="text-xs text-gray-400 mt-2">PDF, DOC, DOCX, JPG, PNG (max 10MB)</p>
                    </>
                  )}
                </div>
              </div>
              
              {/* Document Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Document Name *</label>
                <input 
                  type="text" 
                  value={newDocument.name} 
                  onChange={(e) => setNewDocument({...newDocument, name: e.target.value})} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                  placeholder="Purchase Agreement - 123 Main St" 
                />
              </div>
              
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select 
                  value={newDocument.category} 
                  onChange={(e) => setNewDocument({...newDocument, category: e.target.value})} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="purchase_agreement">Purchase Agreement</option>
                  <option value="listing_agreement">Listing Agreement</option>
                  <option value="disclosure">Disclosure Form</option>
                  <option value="inspection">Inspection Report</option>
                  <option value="contract">Client Contract</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              {/* Related Listing */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Related Listing</label>
                <select 
                  value={newDocument.listing_id} 
                  onChange={(e) => setNewDocument({...newDocument, listing_id: e.target.value})} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">No specific listing</option>
                  {listings.map(l => (
                    <option key={l.id} value={l.id}>{l.address}, {l.city}</option>
                  ))}
                </select>
              </div>
              
              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea 
                  value={newDocument.notes} 
                  onChange={(e) => setNewDocument({...newDocument, notes: e.target.value})} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                  rows={2} 
                  placeholder="Optional notes about this document..." 
                />
              </div>
              
              {/* Upload Progress */}
              {isUploading && (
                <div className="bg-purple-50 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-purple-700">Uploading...</span>
                    <span className="text-sm text-purple-600">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-purple-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button 
                onClick={() => { setShowDocModal(false); setNewDocument({ name: '', category: 'other', listing_id: '', notes: '', file: null }); }} 
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl"
                disabled={isUploading}
              >
                Cancel
              </button>
              <button 
                onClick={handleAddDocument} 
                disabled={isUploading || !newDocument.file || !newDocument.name}
                className={`px-4 py-2 rounded-xl flex items-center gap-2 ${
                  isUploading || !newDocument.file || !newDocument.name 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                <Upload className="w-4 h-4" />
                {isUploading ? 'Uploading...' : 'Upload Document'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Library Modal */}
      <PhotoLibraryModal 
        listing={showPhotoLibrary}
        isOpen={!!showPhotoLibrary}
        onClose={() => setShowPhotoLibrary(null)}
        API={API}
        userId={userId}
        onPhotosUpdated={fetchData}
      />
    </div>
  );
};

// Generate Listing Section Component (Redesigned)
const GenerateSection = ({ onGenerate, isGenerating: parentIsGenerating, generatedDescription: parentDescription, onCopy, images, setImages, imageAnalysis, setImageAnalysis, isAnalyzing, setIsAnalyzing, analyzeImages, formData: parentFormData, setFormData: setParentFormData, onDescriptionGenerated, showToast, userId, onNavigate }) => {
  // Use local state that syncs with parent
  const [formData, setFormData] = useState({
    propertyType: parentFormData?.propertyType || '',
    country: parentFormData?.country || 'USA',
    address: parentFormData?.address || '',
    city: parentFormData?.city || '',
    state: parentFormData?.state || '',
    zipCode: parentFormData?.zipCode || '',
    beds: parentFormData?.bedrooms || parentFormData?.beds || '',
    baths: parentFormData?.bathrooms || parentFormData?.baths || '',
    sqft: parentFormData?.squareFootage || parentFormData?.sqft || '',
    price: parentFormData?.price || '',
    keyFeatures: parentFormData?.keyFeatures ? (Array.isArray(parentFormData.keyFeatures) ? parentFormData.keyFeatures : []) : [],
    neighborhood: parentFormData?.neighborhoodHighlights || parentFormData?.neighborhood || '',
    style: parentFormData?.style || 'Luxury',
    tone: parentFormData?.tone || 'Elegant',
  });

  const [localDescription, setLocalDescription] = useState(parentDescription || '');
  const [qualityScore, setQualityScore] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  // Sync formData changes to parent - debounced to prevent excessive re-renders
  const syncTimeoutRef = React.useRef(null);
  useEffect(() => {
    if (setParentFormData) {
      // Clear previous timeout
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
      // Debounce the sync to parent - only sync after 100ms of no changes (reduced from 500ms)
      syncTimeoutRef.current = setTimeout(() => {
        setParentFormData({
          propertyType: formData.propertyType,
          country: formData.country,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          bedrooms: formData.beds,
          bathrooms: formData.baths,
          beds: formData.beds,
          baths: formData.baths,
          squareFootage: formData.sqft,
          sqft: formData.sqft,
          keyFeatures: formData.keyFeatures,
          neighborhoodHighlights: formData.neighborhood,
          neighborhood: formData.neighborhood,
          style: formData.style,
          tone: formData.tone,
          price: formData.price
        });
      }, 100);
    }
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [formData, setParentFormData]);

  // Image upload handler
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const newImages = await Promise.all(files.map(async (file) => ({
      file, preview: URL.createObjectURL(file), name: file.name,
      base64: await fileToBase64(file), analyzed: false
    })));
    setImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    if (setImageAnalysis) setImageAnalysis(null);
  };

  const handleAnalyzeImages = async () => {
    if (images.length === 0) return;
    if (setIsAnalyzing) setIsAnalyzing(true);
    try {
      const res = await axios.post(`${API}/analyze-images`, { images: images.map(img => img.base64) });
      if (res.data.success) {
        if (setImageAnalysis) setImageAnalysis(res.data);
        // Auto-add detected features
        if (res.data.key_features?.length > 0) {
          const newFeatures = res.data.key_features.filter(f => !formData.keyFeatures.includes(f));
          if (newFeatures.length > 0) {
            setFormData(prev => ({
              ...prev,
              keyFeatures: [...prev.keyFeatures, ...newFeatures.slice(0, 5)]
            }));
          }
        }
      }
    } catch (err) { console.error(err); }
    if (setIsAnalyzing) setIsAnalyzing(false);
  };

  const suggestedFeatures = [
    'Vaulted Ceilings',
    'Natural Light',
    'Walk-in Closet',
    'Gourmet Kitchen',
    'Hardwood Floors',
    'Private Backyard',
    'Smart Home',
    'Open Floor Plan',
  ];

  const addFeature = (feature) => {
    if (!formData.keyFeatures.includes(feature)) {
      setFormData(prev => ({
        ...prev,
        keyFeatures: [...prev.keyFeatures, feature]
      }));
    }
  };

  const removeFeature = (feature) => {
    setFormData(prev => ({
      ...prev,
      keyFeatures: prev.keyFeatures.filter(f => f !== feature)
    }));
  };

  const handleGenerate = async () => {
    if (!formData.propertyType || !formData.address || !formData.beds || !formData.baths) {
      alert('Please fill in required fields: Property Type, Address, Beds, and Baths');
      return;
    }
    
    setIsGenerating(true);
    try {
      const payload = {
        region: formData.country === 'Canada' ? 'canada' : 'usa',
        country: formData.country || 'USA',
        propertyType: formData.propertyType,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        bedrooms: formData.beds,
        bathrooms: formData.baths,
        squareFootage: formData.sqft,
        keyFeatures: formData.keyFeatures.join(', '),
        neighborhoodHighlights: formData.neighborhood,
        style: formData.style,
        tone: formData.tone,
        price: formData.price,
        imageCount: images ? images.length : 0,
        imageAnalysis: imageAnalysis?.analysis || ''
      };
      
      const res = await axios.post(`${API}/generate-description`, payload);
      if (res.data.description) {
        setLocalDescription(res.data.description);
        // Sync description to parent
        if (onDescriptionGenerated) {
          onDescriptionGenerated(res.data.description);
        }
        // Calculate quality score based on description length and keywords
        const wordCount = res.data.description.split(' ').length;
        const score = Math.min(95, Math.floor(70 + (wordCount / 10)));
        setQualityScore(score);
        
        // Auto-save to My Listings with ALL form data including images
        try {
          if (userId) {
            const listingPayload = {
              address: formData.address,
              city: formData.city || '',
              state: formData.state || '',
              zip_code: formData.zipCode || '',
              country: formData.country || 'USA',
              price: parseFloat(formData.price?.replace(/[$,]/g, '') || '0'),
              bedrooms: parseInt(formData.beds) || 0,
              bathrooms: parseFloat(formData.baths) || 0,
              square_footage: parseInt(formData.sqft?.replace(/,/g, '') || '0'),
              property_type: formData.propertyType,
              status: 'active',
              description: res.data.description,
              key_features: formData.keyFeatures || [],
              neighborhood_highlights: formData.neighborhood || '',
              images: images ? images.map(img => img.base64 || img.preview || img) : []
            };
            
            const saveRes = await axios.post(`${API}/my-listings?user_id=${userId}`, listingPayload);
            if (saveRes.data.success) {
              if (showToast) {
                showToast('Listing generated and saved to My Listings!', 'success');
              }
              console.log('Listing auto-saved to My Listings:', saveRes.data.listing?.id);
            }
          } else {
            if (showToast) {
              showToast('Description generated successfully!', 'success');
            }
          }
        } catch (saveErr) {
          console.error('Auto-save to My Listings failed:', saveErr);
          // Still show success for description generation
          if (showToast) {
            showToast('Description generated! (Note: Could not auto-save to listings)', 'success');
          }
        }
      }
    } catch (error) {
      console.error('Error generating description:', error);
      alert('Failed to generate description. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const regenerate = () => {
    handleGenerate();
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Generate New Listing</h1>
        <p className="text-gray-600">Smart Property Details Form</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Form (2/3 width) */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            {/* Country & Property Type */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country *
                </label>
                <select
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value, state: ''})}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-700"
                >
                  <option value="USA">🇺🇸 USA</option>
                  <option value="Canada">🇨🇦 Canada</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Property Type *
                </label>
                <select
                  value={formData.propertyType}
                  onChange={(e) => setFormData({...formData, propertyType: e.target.value})}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-700"
                >
                  <option value="">Select type...</option>
                  <option value="Single Family Home">Single Family Home</option>
                  <option value="Condo">Condo</option>
                  <option value="Townhouse">Townhouse</option>
                  <option value="Multi-Family">Multi-Family</option>
                  <option value="Land">Land</option>
                  <option value="Commercial">Commercial</option>
                </select>
              </div>
            </div>

            {/* Street Address */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Street Address *
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="123 Maple Avenue"
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-700"
                />
              </div>
            </div>

            {/* City, State/Province, ZIP/Postal Code */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  placeholder={formData.country === 'USA' ? 'e.g., Seattle' : 'e.g., Calgary'}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 text-gray-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.country === 'USA' ? 'State *' : 'Province *'}
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                  placeholder={formData.country === 'USA' ? 'e.g., WA' : 'e.g., AB'}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 text-gray-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {formData.country === 'USA' ? 'ZIP Code *' : 'Postal Code *'}
                </label>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({...formData, zipCode: e.target.value})}
                  placeholder={formData.country === 'USA' ? 'e.g., 98101' : 'e.g., T2P 2M5'}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 text-gray-700"
                />
              </div>
            </div>

            {/* Location Info Tip */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
              <p className="text-xs text-blue-700">
                💡 Complete location details help marketing tools provide accurate, location-specific results (market comparisons, SEO keywords, local data, etc.)
              </p>
            </div>

            {/* Beds, Baths, Sqft, Price */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Beds *</label>
                <input
                  type="number"
                  value={formData.beds}
                  onChange={(e) => setFormData({...formData, beds: e.target.value})}
                  placeholder="3"
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 text-gray-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Baths *</label>
                <input
                  type="text"
                  value={formData.baths}
                  onChange={(e) => setFormData({...formData, baths: e.target.value})}
                  placeholder="2.5"
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 text-gray-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sqft</label>
                <input
                  type="text"
                  value={formData.sqft}
                  onChange={(e) => setFormData({...formData, sqft: e.target.value})}
                  placeholder="2,200"
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 text-gray-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
                <input
                  type="text"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  placeholder="$750,000"
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 text-gray-700"
                />
              </div>
            </div>

            {/* Neighborhood */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Neighborhood Highlights</label>
              <input
                type="text"
                value={formData.neighborhood}
                onChange={(e) => setFormData({...formData, neighborhood: e.target.value})}
                placeholder="Near schools, parks, downtown..."
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 text-gray-700"
              />
            </div>

            {/* Property Photos Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Camera className="w-4 h-4 text-purple-600" />
                  Property Photos
                </label>
                {images && images.length > 0 && (
                  <button
                    onClick={handleAnalyzeImages}
                    disabled={isAnalyzing}
                    className="text-sm px-3 py-1 bg-purple-100 text-purple-700 rounded-xl hover:bg-purple-200 transition-colors flex items-center gap-1 disabled:opacity-50"
                  >
                    {isAnalyzing ? (
                      <>
                        <div className="w-3 h-3 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3" />
                        AI Analyze Photos
                      </>
                    )}
                  </button>
                )}
              </div>
              
              {/* Upload Area */}
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="property-photos"
                />
                <label htmlFor="property-photos" className="cursor-pointer block text-center">
                  {(!images || images.length === 0) ? (
                    <div className="py-4">
                      <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600 font-medium">Click to upload property photos</p>
                      <p className="text-gray-400 text-sm mt-1">AI will analyze photos to suggest features & improve descriptions</p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2 py-2 text-purple-600 hover:text-purple-700">
                      <Plus className="w-4 h-4" />
                      <span className="text-sm font-medium">Add more photos</span>
                    </div>
                  )}
                </label>
              </div>

              {/* Image Preview Grid */}
              {images && images.length > 0 && (
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {images.map((img, idx) => (
                    <div key={idx} className="relative group aspect-square rounded-xl overflow-hidden border border-gray-200">
                      <img src={img.preview} alt={`Property ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => removeImage(idx)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      {img.analyzed && (
                        <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-green-500 text-white text-[10px] rounded">
                          Analyzed
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Image Analysis Results */}
              {imageAnalysis && (
                <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-800">AI Photo Analysis</span>
                  </div>
                  {imageAnalysis.analysis && (
                    <p className="text-sm text-purple-700 mb-2">{imageAnalysis.analysis}</p>
                  )}
                  {imageAnalysis.key_features && imageAnalysis.key_features.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {imageAnalysis.key_features.map((feature, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-purple-200 text-purple-800 rounded-full">
                          {feature}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Style & Tone */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Style</label>
                <select
                  value={formData.style}
                  onChange={(e) => setFormData({...formData, style: e.target.value})}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 text-gray-700"
                >
                  <option>Luxury</option>
                  <option>Family-Friendly</option>
                  <option>Modern</option>
                  <option>Traditional</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tone</label>
                <select
                  value={formData.tone}
                  onChange={(e) => setFormData({...formData, tone: e.target.value})}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 text-gray-700"
                >
                  <option>Elegant</option>
                  <option>Warm</option>
                  <option>Exciting</option>
                  <option>Professional</option>
                </select>
              </div>
            </div>

            {/* Key Features */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Key Features</label>
              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 border border-gray-300 rounded-xl min-h-[60px]">
                {formData.keyFeatures.map((feature, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-xl text-sm font-medium text-gray-700"
                  >
                    {feature}
                    <button
                      onClick={() => removeFeature(feature)}
                      className="hover:text-red-600 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
                {formData.keyFeatures.length === 0 && (
                  <span className="text-sm text-gray-400">Add features from suggestions below</span>
                )}
              </div>
            </div>

            {/* AI Suggestions */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <label className="text-sm font-medium text-gray-700">AI Suggestions</label>
              </div>
              <div className="flex flex-wrap gap-2">
                {suggestedFeatures.map((feature, idx) => (
                  <button
                    key={idx}
                    onClick={() => addFeature(feature)}
                    disabled={formData.keyFeatures.includes(feature)}
                    className={`
                      inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition-all
                      ${formData.keyFeatures.includes(feature)
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200'
                      }
                    `}
                  >
                    {feature}
                    {!formData.keyFeatures.includes(feature) && <span className="text-lg leading-none">+</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-xl text-white font-semibold text-lg shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isGenerating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Generate Description
                </>
              )}
            </button>
          </div>
        </div>

        {/* Right Column - Output (1/3 width) */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm sticky top-24">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Generated Description</h3>
            
            {localDescription ? (
              <>
                {/* Quality Score */}
                <div className="flex justify-center mb-6">
                  <div className="relative w-32 h-32">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        stroke="#E5E7EB"
                        strokeWidth="8"
                        fill="none"
                      />
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        stroke="url(#qualityGradient)"
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={`${qualityScore * 3.51} 351`}
                        strokeLinecap="round"
                      />
                      <defs>
                        <linearGradient id="qualityGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#8B5CF6" />
                          <stop offset="100%" stopColor="#3B82F6" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-3xl font-bold text-gray-900">{qualityScore}</span>
                      <span className="text-xs text-gray-500">/100</span>
                      <span className="text-xs font-medium text-purple-600 mt-1">
                        {qualityScore >= 90 ? 'Excellent' : qualityScore >= 75 ? 'Good' : 'Fair'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Generated Text */}
                <div className="mb-4 p-4 bg-gray-50 rounded-xl max-h-64 overflow-y-auto">
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {localDescription}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2">
                  <button 
                    onClick={() => copyToClipboard(localDescription)}
                    className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button className="w-full py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors">
                    <Wand className="w-4 h-4" />
                    Edit
                  </button>
                  <button 
                    onClick={regenerate}
                    disabled={isGenerating}
                    className="w-full py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                  >
                    <Sparkles className="w-4 h-4" />
                    Regenerate
                  </button>
                  <button 
                    onClick={async () => {
                      try {
                        // Create listing data with all form data and images
                        const listingData = {
                          address: formData.address || '',
                          city: formData.city || '',
                          state: formData.state || '',
                          zip_code: formData.zipCode || '',
                          price: parseFloat(String(formData.price || '0').replace(/[^0-9.]/g, '')) || 0,
                          bedrooms: parseInt(formData.beds) || 0,
                          bathrooms: parseFloat(formData.baths) || 0,
                          square_footage: parseInt(String(formData.sqft).replace(/,/g, '')) || 0,
                          property_type: formData.propertyType || 'Single Family Home',
                          status: 'active',
                          description: localDescription || '',
                          key_features: formData.keyFeatures || [],
                          neighborhood: formData.neighborhood || '',
                          images: images ? images.map(img => img.base64 || img.preview || img) : []
                        };
                        
                        await axios.post(`${API}/my-listings?user_id=${userId}`, listingData);
                        if (showToast) showToast('Listing saved to My Listings!', 'success');
                        else alert('Listing saved to My Listings!');
                      } catch (error) {
                        console.error('Error saving listing:', error);
                        if (showToast) showToast('Failed to save listing', 'error');
                        else alert('Failed to save listing');
                      }
                    }}
                    className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    Save to My Listings
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <Sparkles className="w-8 h-8 text-purple-600" />
                </div>
                <p className="text-sm text-gray-500 text-center">
                  Fill in the property details and click Generate to create your listing
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Action Bar - Top 5 AI Marketing Tools */}
      <div className="mt-6 rounded-2xl overflow-hidden shadow-xl" style={{ background: 'linear-gradient(90deg, #6B46C1 0%, #5B21B6 30%, #4C1D95 60%, #3B82F6 100%)' }}>
        <div className="flex items-center justify-between px-4 py-3">
          <button 
            onClick={() => onNavigate && onNavigate('variations')}
            className="flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-sm rounded-xl text-white font-medium hover:bg-white/30 transition-all"
          >
            <Copy className="w-4 h-4" />
            A/B Variations
          </button>
          <button 
            onClick={() => onNavigate && onNavigate('social')}
            className="flex items-center gap-2 px-5 py-2.5 text-white/90 hover:text-white hover:bg-white/10 rounded-xl font-medium transition-all"
          >
            <Share className="w-4 h-4" />
            Social Media
          </button>
          <button 
            onClick={() => onNavigate && onNavigate('video')}
            className="flex items-center gap-2 px-5 py-2.5 text-white/90 hover:text-white hover:bg-white/10 rounded-xl font-medium transition-all"
          >
            <Video className="w-4 h-4" />
            Video Scripts
          </button>
          <button 
            onClick={() => onNavigate && onNavigate('clientcomm')}
            className="flex items-center gap-2 px-5 py-2.5 text-white/90 hover:text-white hover:bg-white/10 rounded-xl font-medium transition-all"
          >
            <Mail className="w-4 h-4" />
            Client Emails
          </button>
          <button 
            onClick={() => onNavigate && onNavigate('hashtags')}
            className="flex items-center gap-2 px-5 py-2.5 text-white/90 hover:text-white hover:bg-white/10 rounded-xl font-medium transition-all"
          >
            <Hash className="w-4 h-4" />
            Hashtags
          </button>
        </div>
      </div>
    </div>
  );
};

// Feature Sheet Generator Component
const FeatureSheetGenerator = ({ parentFormData, images, generatedDescription, imageAnalysis, userId, API }) => {
  const [featureSheet, setFeatureSheet] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [generatedPDF, setGeneratedPDF] = useState(null);
  const [savedListings, setSavedListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  
  // NEW: Floor plan and community features state
  const [floorPlanImage, setFloorPlanImage] = useState(null);
  const [locationMapImage, setLocationMapImage] = useState(null);  // NEW: Location map for community page
  const [communityFeatures, setCommunityFeatures] = useState(null);
  const [customCommunityFeatures, setCustomCommunityFeatures] = useState('');
  const [isLoadingCommunity, setIsLoadingCommunity] = useState(false);
  
  // Local form state for feature sheet
  const [formData, setFormData] = useState({
    // Property Info
    address: parentFormData?.address || '',
    city: parentFormData?.city || '',
    stateProvince: parentFormData?.stateProvince || parentFormData?.state || '',
    neighborhood: parentFormData?.neighborhoodHighlights || parentFormData?.neighborhood || '',
    propertyType: parentFormData?.propertyType || '',
    bedrooms: parentFormData?.bedrooms || parentFormData?.beds || '',
    bathrooms: parentFormData?.bathrooms || parentFormData?.baths || '',
    squareFootage: parentFormData?.squareFootage || parentFormData?.sqft || '',
    price: parentFormData?.price || '',
    
    // Feature Sheet Specific
    mainHeadline: '',
    description: generatedDescription || '',
    propertyFeatures: Array.isArray(parentFormData?.keyFeatures) ? parentFormData.keyFeatures : [],
    tagline: 'Exclusive Listing',  // NEW: Customizable tagline
    virtualTourUrl: '',            // NEW: Virtual tour URL for QR code
    
    // Floor Plan Info
    mainFloorSqFt: '',
    upperFloorSqFt: '',
    basementFloorSqFt: '',
    
    // Agent Info
    agentName: '',
    agentCompany: '',
    agentPhone: '',
    agentEmail: '',
    
    // Images
    coverImage: null,
    interiorImages: [],
    mainFloorPlan: null,
    upperFloorPlan: null,
    basementFloorPlan: null,
    companyLogo: null,
    
    // Branding Customization
    primaryColor: '#1E40AF',
    secondaryColor: '#9F1239',
    backgroundColor: '#FFFFFF',
    textColor: '#1F2937',
    layoutStyle: 'modern',
    logoPlacement: 'top-center',
  });

  // Fetch saved listings on mount
  useEffect(() => {
    if (userId && API) {
      fetchSavedListings();
    }
  }, [userId, API]);

  const fetchSavedListings = async () => {
    try {
      const res = await axios.get(`${API}/my-listings?user_id=${userId}`);
      setSavedListings(res.data.listings || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    }
  };

  const handleListingSelect = async (listingId) => {
    if (!listingId) {
      setSelectedListing(null);
      return;
    }
    
    const listing = savedListings.find(l => l.id === listingId);
    if (listing) {
      setSelectedListing(listing);
      
      // Fetch listing photos for cover image
      let coverImageUrl = null;
      try {
        const photosRes = await axios.get(`${API}/listings/${listingId}/photos?user_id=${userId}`);
        const photos = photosRes.data.photos || [];
        const primary = photos.find(p => p.is_primary) || photos[0];
        if (primary) {
          coverImageUrl = primary.url;
        }
      } catch (error) {
        console.error('Error fetching photos:', error);
      }
      
      setFormData(prev => ({
        ...prev,
        address: listing.address || '',
        city: listing.city || '',
        stateProvince: listing.state || '',
        neighborhood: listing.neighborhood || '',
        propertyType: listing.property_type || '',
        bedrooms: listing.bedrooms?.toString() || '',
        bathrooms: listing.bathrooms?.toString() || '',
        squareFootage: listing.square_footage?.toString() || '',
        price: listing.price ? `$${listing.price.toLocaleString()}` : '',
        description: listing.description || prev.description,
        mainHeadline: `${listing.property_type || 'Beautiful Home'} in ${listing.city}`,
        coverImage: coverImageUrl,
      }));
    }
  };

  // Sync with parent form data when it changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      address: parentFormData?.address || prev.address,
      city: parentFormData?.city || prev.city,
      stateProvince: parentFormData?.stateProvince || parentFormData?.state || prev.stateProvince,
      neighborhood: parentFormData?.neighborhoodHighlights || parentFormData?.neighborhood || prev.neighborhood,
      propertyType: parentFormData?.propertyType || prev.propertyType,
      bedrooms: parentFormData?.bedrooms || parentFormData?.beds || prev.bedrooms,
      bathrooms: parentFormData?.bathrooms || parentFormData?.baths || prev.bathrooms,
      squareFootage: parentFormData?.squareFootage || parentFormData?.sqft || prev.squareFootage,
      price: parentFormData?.price || prev.price,
      propertyFeatures: Array.isArray(parentFormData?.keyFeatures) && parentFormData.keyFeatures.length > 0 
        ? parentFormData.keyFeatures 
        : prev.propertyFeatures,
    }));
  }, [parentFormData]);

  // Sync generated description when it changes
  useEffect(() => {
    if (generatedDescription) {
      setFormData(prev => ({
        ...prev,
        description: generatedDescription
      }));
    }
  }, [generatedDescription]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle array inputs (features)
  const addToArray = (field, value) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value]
      }));
    }
  };

  const removeFromArray = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // Handle image uploads - convert to base64 for API
  const handleImageUpload = async (field, files) => {
    const fileArray = Array.from(files);
    
    // Helper function to convert file to base64
    const fileToBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    };
    
    if (field === 'interiorImages') {
      // Convert all files to base64
      const base64Images = await Promise.all(fileArray.map(f => fileToBase64(f)));
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], ...base64Images]
      }));
    } else {
      // Convert single file to base64
      const base64 = await fileToBase64(fileArray[0]);
      setFormData(prev => ({
        ...prev,
        [field]: base64
      }));
    }
  };

  const templates = [
    { id: 'modern', name: 'Modern', description: 'Clean, minimalist design' },
    { id: 'luxury', name: 'Luxury', description: 'Elegant, high-end presentation' },
    { id: 'classic', name: 'Classic', description: 'Traditional professional style' },
    { id: 'compact', name: 'Compact', description: 'Single page, info-dense' }
  ];

  const generateFeatureSheet = async () => {
    setIsGenerating(true);
    setGeneratedPDF(null);
    
    try {
      // Prepare community features - use custom or AI-generated
      let finalCommunityFeatures = communityFeatures;
      if (customCommunityFeatures.trim()) {
        const customHighlights = customCommunityFeatures.split('\n').filter(f => f.trim());
        finalCommunityFeatures = {
          highlights: customHighlights,
          lifestyle: `Community features for ${formData.address || 'this property'}`,
          neighborhood: formData.neighborhood || formData.city,
          city: formData.city
        };
      }
      
      // Add manual scores if provided
      if (formData.walkScore || formData.bikeScore || formData.transitScore) {
        finalCommunityFeatures = {
          ...finalCommunityFeatures,
          scores: {
            walkability: formData.walkScore ? parseInt(formData.walkScore) : null,
            bikeability: formData.bikeScore ? parseInt(formData.bikeScore) : null,
            transit: formData.transitScore ? parseInt(formData.transitScore) : null
          }
        };
      }
      
      // Use the enhanced print-marketing/generate endpoint
      const response = await axios.post(`${API}/premium/print-marketing/generate`, {
        templateType: 'feature_sheet',
        address: formData.address || '123 Main Street',
        city: formData.city || parentFormData?.city || '',
        stateProvince: formData.stateProvince || parentFormData?.stateProvince || '',
        neighborhood: formData.neighborhood || formData.city || '',
        price: formData.price || 'Contact for Price',
        bedrooms: formData.bedrooms || '3',
        bathrooms: formData.bathrooms || '2',
        squareFootage: formData.squareFootage || '1,500',
        propertyType: formData.propertyType || 'Home',
        description: formData.description || generatedDescription || '',
        features: formData.propertyFeatures || [],
        agentName: formData.agentName || 'Your Name',
        agentCompany: formData.agentCompany || 'Real Estate Company',
        agentPhone: formData.agentPhone || '(555) 123-4567',
        agentEmail: formData.agentEmail || 'agent@email.com',
        // Enhanced fields for professional design
        floorPlanImage: floorPlanImage || '',
        communityFeatures: finalCommunityFeatures || {},
        images: formData.coverImage ? [formData.coverImage, ...(formData.interiorImages || [])] : images || [],
        // Branding and logo fields
        logoImage: formData.companyLogo || '',
        primaryColor: formData.primaryColor || '#1E3A5F',
        accentColor: formData.secondaryColor || '#9F1239',
        // NEW: Enhanced feature sheet fields
        tagline: formData.tagline || 'Exclusive Listing',
        virtualTourUrl: formData.virtualTourUrl || '',
        // Cover image and location map for new template
        coverImage: formData.coverImage || '',
        locationMapImage: locationMapImage || ''
      });
      
      if (response.data.success && response.data.pdfData) {
        // Create download link from base64
        setGeneratedPDF(response.data.pdfData);
        
        // Update preview
        setFeatureSheet({
          headline: formData.mainHeadline || `${formData.propertyType || 'Beautiful Property'} in ${formData.neighborhood || formData.city || 'Prime Location'}`,
          tagline: "Your Dream Home Awaits",
          highlights: [
            `${formData.bedrooms || '3'} Bedrooms`,
            `${formData.bathrooms || '2'} Bathrooms`,
            formData.squareFootage ? `${formData.squareFootage} Sq Ft` : 'Spacious Living',
            ...formData.propertyFeatures.slice(0, 3)
          ],
          description: formData.description || "Experience luxury living in this stunning property...",
          price: formData.price || "Contact for Price",
          address: formData.address || "123 Main Street",
          agent: {
            name: formData.agentName,
            company: formData.agentCompany,
            phone: formData.agentPhone,
            email: formData.agentEmail
          },
          hasFloorPlan: !!floorPlanImage,
          hasCommunityFeatures: !!finalCommunityFeatures
        });
      } else {
        throw new Error(response.data.error || 'Failed to generate PDF');
      }
      
    } catch (error) {
      console.error('Error generating feature sheet:', error);
      alert('Failed to generate feature sheet. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const [activeSection, setActiveSection] = useState('property');
  
  const sections = [
    { id: 'property', label: 'Property', icon: Home },
    { id: 'community', label: 'Community', icon: MapPin },
    { id: 'images', label: 'Images', icon: Camera },
    { id: 'branding', label: 'Branding', icon: Sparkles },
  ];

  // Load community features from AI
  const loadCommunityFeatures = async () => {
    const cityValue = formData.city || parentFormData?.city;
    const neighborhoodValue = formData.neighborhood || parentFormData?.neighborhoodHighlights || cityValue;
    
    if (!cityValue) {
      alert('Please fill out city first in the Property section');
      return;
    }
    
    if (!userId) {
      alert('Please log in to use this feature');
      return;
    }
    
    setIsLoadingCommunity(true);
    try {
      const res = await axios.post(`${API}/premium/community-features?user_id=${userId}`, {
        city: cityValue,
        neighborhood: neighborhoodValue,
        stateProvince: formData.stateProvince || parentFormData?.stateProvince || '',
        country: parentFormData?.country || 'USA',
        address: formData.address || parentFormData?.address || ''
      });
      
      if (res.data.success) {
        setCommunityFeatures(res.data);
      } else {
        alert(res.data.error || 'Failed to generate community features');
      }
    } catch (error) {
      console.error('Error loading community features:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to generate community features';
      alert(errorMsg);
    } finally {
      setIsLoadingCommunity(false);
    }
  };

  // Handle floor plan upload
  const handleFloorPlanUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFloorPlanImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLocationMapUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setLocationMapImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-6 tab-content">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-50 mb-1">
            📄 Feature Sheet Generator
          </h2>
          <p className="text-slate-400 text-sm">
            Create professional print-ready feature sheets
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-600/20 to-rose-700/20 border border-blue-500/30 rounded-xl">
          <Crown className="w-4 h-4 text-blue-400" />
          <span className="text-blue-300 font-semibold text-sm">Premium</span>
        </div>
      </div>

      {/* Listing Selector */}
      <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-xl p-4 border border-purple-500/30">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-purple-300 mb-1">
              <FileText className="w-4 h-4 inline mr-1" />
              Select a Listing to Auto-Fill
            </label>
            <select
              value={selectedListing?.id || ''}
              onChange={(e) => handleListingSelect(e.target.value)}
              className="w-full px-3 py-2 border border-purple-500/50 rounded-xl bg-slate-800 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">-- Choose from My Listings --</option>
              {savedListings.map((listing) => (
                <option key={listing.id} value={listing.id}>
                  {listing.address}, {listing.city} - ${listing.price?.toLocaleString()}
                </option>
              ))}
            </select>
          </div>
          {selectedListing && (
            <div className="flex-shrink-0 bg-slate-800/50 rounded-xl p-3 border border-purple-500/30">
              <p className="text-sm font-medium text-white">{selectedListing.address}</p>
              <p className="text-xs text-slate-400">
                {selectedListing.bedrooms} bed • {selectedListing.bathrooms} bath • {selectedListing.square_footage?.toLocaleString()} sqft
              </p>
            </div>
          )}
        </div>
        {savedListings.length === 0 && (
          <p className="text-sm text-purple-400 mt-2">
            💡 Add listings in "My Listings" tab to quickly use them here!
          </p>
        )}
      </div>

      {/* Section Navigation */}
      <div className="flex gap-2 border-b border-slate-700 pb-3">
        {sections.map((section) => {
          const Icon = section.icon;
          return (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                activeSection === section.id
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {section.label}
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Form Sections */}
        <div className="lg:col-span-2 space-y-4">
          
          {/* PROPERTY SECTION */}
          {activeSection === 'property' && (
            <>
              {/* Property Details Card */}
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Home className="w-5 h-5 text-blue-400" />
                  Property Details
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">Address *</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      placeholder="123 Main St"
                      className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm focus:border-blue-500 transition-all"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">City *</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="e.g., Calgary"
                      className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm focus:border-blue-500 transition-all"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">State/Province</label>
                    <input
                      type="text"
                      value={formData.stateProvince}
                      onChange={(e) => handleInputChange('stateProvince', e.target.value)}
                      placeholder="e.g., Alberta"
                      className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm focus:border-blue-500 transition-all"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">Neighborhood</label>
                    <input
                      type="text"
                      value={formData.neighborhood}
                      onChange={(e) => handleInputChange('neighborhood', e.target.value)}
                      placeholder="e.g., Downtown"
                      className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm focus:border-blue-500 transition-all"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">Property Type</label>
                    <select
                      value={formData.propertyType}
                      onChange={(e) => handleInputChange('propertyType', e.target.value)}
                      className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm focus:border-blue-500 transition-all"
                    >
                      <option value="">Select...</option>
                      <option value="Single Family Home">Single Family Home</option>
                      <option value="Condo">Condo</option>
                      <option value="Townhouse">Townhouse</option>
                      <option value="Multi-Family">Multi-Family</option>
                      <option value="Land">Land</option>
                      <option value="Commercial">Commercial</option>
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3 md:col-span-2">
                    <div>
                      <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">Beds</label>
                      <input
                        type="number"
                        value={formData.bedrooms}
                        onChange={(e) => handleInputChange('bedrooms', e.target.value)}
                        placeholder="3"
                        className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm focus:border-blue-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">Baths</label>
                      <input
                        type="number"
                        step="0.5"
                        value={formData.bathrooms}
                        onChange={(e) => handleInputChange('bathrooms', e.target.value)}
                        placeholder="2.5"
                        className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm focus:border-blue-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">Sq Ft</label>
                      <input
                        type="number"
                        value={formData.squareFootage}
                        onChange={(e) => handleInputChange('squareFootage', e.target.value)}
                        placeholder="2000"
                        className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm focus:border-blue-500 transition-all"
                      />
                    </div>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">Price</label>
                    <input
                      type="text"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', e.target.value)}
                      placeholder="$500,000"
                      className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm focus:border-blue-500 transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Description & Features Card */}
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" />
                  Description & Features
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">Headline</label>
                    <input
                      type="text"
                      value={formData.mainHeadline}
                      onChange={(e) => handleInputChange('mainHeadline', e.target.value)}
                      placeholder="e.g., Stunning Modern Home with Mountain Views"
                      className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm focus:border-blue-500 transition-all"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows="4"
                      placeholder="Enter property description..."
                      className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm focus:border-blue-500 transition-all resize-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">Property Features</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        id="feature-input"
                        placeholder="Add feature (e.g., Hardwood Floors)"
                        className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            addToArray('propertyFeatures', e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                      <button
                        onClick={() => {
                          const input = document.getElementById('feature-input');
                          addToArray('propertyFeatures', input.value);
                          input.value = '';
                        }}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-xl text-white text-sm font-medium"
                      >
                        Add
                      </button>
                    </div>
                    {formData.propertyFeatures.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.propertyFeatures.map((feature, index) => (
                          <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-700 rounded-xl text-sm text-slate-200">
                            {feature}
                            <button onClick={() => removeFromArray('propertyFeatures', index)} className="text-red-400 hover:text-red-300">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Agent Info Card */}
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-400" />
                  Agent Information
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">Name</label>
                    <input
                      type="text"
                      value={formData.agentName}
                      onChange={(e) => handleInputChange('agentName', e.target.value)}
                      placeholder="John Smith"
                      className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">Company</label>
                    <input
                      type="text"
                      value={formData.agentCompany}
                      onChange={(e) => handleInputChange('agentCompany', e.target.value)}
                      placeholder="RE/MAX"
                      className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">Phone</label>
                    <input
                      type="tel"
                      value={formData.agentPhone}
                      onChange={(e) => handleInputChange('agentPhone', e.target.value)}
                      placeholder="(555) 123-4567"
                      className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">Email</label>
                    <input
                      type="email"
                      value={formData.agentEmail}
                      onChange={(e) => handleInputChange('agentEmail', e.target.value)}
                      placeholder="agent@email.com"
                      className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* COMMUNITY SECTION - Floor Plan & Community Features */}
          {activeSection === 'community' && (
            <>
              {/* Floor Plan Upload */}
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-400" />
                  Floor Plan (Optional)
                </h3>
                <p className="text-slate-400 text-sm mb-4">
                  Upload a floor plan drawing to include in your feature sheet
                </p>
                
                {!floorPlanImage ? (
                  <div className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center hover:border-blue-500 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFloorPlanUpload}
                      className="hidden"
                      id="floor-plan-upload"
                    />
                    <label htmlFor="floor-plan-upload" className="cursor-pointer">
                      <Upload className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                      <p className="text-slate-300 font-medium">Click to upload floor plan</p>
                      <p className="text-slate-500 text-sm mt-1">PNG, JPG up to 10MB</p>
                    </label>
                  </div>
                ) : (
                  <div className="relative">
                    <img 
                      src={floorPlanImage} 
                      alt="Floor plan" 
                      className="w-full max-h-64 object-contain border-2 border-green-500 rounded-xl bg-slate-900"
                    />
                    <button
                      onClick={() => setFloorPlanImage(null)}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <p className="text-green-400 text-sm mt-2 flex items-center gap-1">
                      <Check className="w-4 h-4" /> Floor plan uploaded successfully
                    </p>
                  </div>
                )}
              </div>

              {/* Location Map Upload - NEW */}
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-400" />
                  Location Map (Optional)
                </h3>
                <p className="text-slate-400 text-sm mb-4">
                  Upload a neighborhood/area map to include in the community features page
                </p>
                
                {!locationMapImage ? (
                  <div className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center hover:border-blue-500 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLocationMapUpload}
                      className="hidden"
                      id="location-map-upload"
                    />
                    <label htmlFor="location-map-upload" className="cursor-pointer">
                      <Upload className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                      <p className="text-slate-300 font-medium">Click to upload location map</p>
                      <p className="text-slate-500 text-sm mt-1">PNG, JPG up to 10MB</p>
                    </label>
                  </div>
                ) : (
                  <div className="relative">
                    <img 
                      src={locationMapImage} 
                      alt="Location map" 
                      className="w-full max-h-64 object-contain border-2 border-green-500 rounded-xl bg-slate-900"
                    />
                    <button
                      onClick={() => setLocationMapImage(null)}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <p className="text-green-400 text-sm mt-2 flex items-center gap-1">
                      <Check className="w-4 h-4" /> Location map uploaded successfully
                    </p>
                  </div>
                )}
              </div>

              {/* Walkability Scores - Manual Entry */}
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-green-400" />
                  Walkability Scores (Optional)
                </h3>
                <p className="text-slate-400 text-sm mb-4">
                  Enter scores from walkscore.com or leave blank to skip this section in the PDF
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Walk Score</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      placeholder="e.g., 75"
                      value={formData.walkScore || ''}
                      onChange={(e) => setFormData({...formData, walkScore: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Bike Score</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      placeholder="e.g., 65"
                      value={formData.bikeScore || ''}
                      onChange={(e) => setFormData({...formData, bikeScore: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Transit Score</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      placeholder="e.g., 55"
                      value={formData.transitScore || ''}
                      onChange={(e) => setFormData({...formData, transitScore: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>
              </div>

              {/* AI Community Features */}
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-blue-400" />
                    Community Features
                  </h3>
                  <button
                    onClick={loadCommunityFeatures}
                    disabled={isLoadingCommunity}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 flex items-center gap-2 transition-all"
                  >
                    {isLoadingCommunity ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Generate with AI
                      </>
                    )}
                  </button>
                </div>
                
                <p className="text-slate-400 text-sm mb-4">
                  AI will generate local schools, parks, transit, and amenities for your neighborhood
                </p>
                
                {/* AI Generated Features Display */}
                {communityFeatures && (
                  <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-600 max-h-80 overflow-y-auto mb-4">
                    {/* Highlights */}
                    {communityFeatures.highlights && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-purple-400 mb-2">Highlights</h4>
                        <div className="space-y-1">
                          {communityFeatures.highlights.map((h, i) => (
                            <div key={i} className="text-sm text-slate-300 flex items-start gap-2">
                              <span className="text-green-400">✓</span>
                              <span>{h}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Walkability */}
                    {communityFeatures.walkability && (
                      <div className="mb-4 bg-emerald-900/30 p-3 rounded-lg border border-emerald-700/50">
                        <span className="text-emerald-400 font-semibold">🚶 Walkability: {communityFeatures.walkability.score}</span>
                        <p className="text-slate-400 text-xs mt-1">{communityFeatures.walkability.description}</p>
                      </div>
                    )}
                    
                    {/* Schools */}
                    {communityFeatures.schools && communityFeatures.schools.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-blue-400 mb-2">🎓 Schools</h4>
                        {communityFeatures.schools.slice(0, 3).map((s, i) => (
                          <div key={i} className="text-xs text-slate-400 mb-1">
                            {s.name} - {s.rating} ({s.distance})
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Parks */}
                    {communityFeatures.parks && communityFeatures.parks.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-green-400 mb-2">🌳 Parks</h4>
                        {communityFeatures.parks.slice(0, 3).map((p, i) => (
                          <div key={i} className="text-xs text-slate-400 mb-1">
                            {p.name} ({p.distance}) - {p.features}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Transit */}
                    {communityFeatures.transit && communityFeatures.transit.length > 0 && (
                      <div className="mb-3">
                        <h4 className="text-sm font-semibold text-orange-400 mb-2">🚇 Transit</h4>
                        {communityFeatures.transit.slice(0, 3).map((t, i) => (
                          <div key={i} className="text-xs text-slate-400 mb-1">
                            {t.name || t.type} - {t.distance}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="mt-3 pt-3 border-t border-slate-600">
                      <p className="text-xs text-slate-500">
                        ✨ AI generated for {communityFeatures.neighborhood}, {communityFeatures.city}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Manual Override */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Or add custom community highlights (one per line):
                  </label>
                  <textarea
                    value={customCommunityFeatures}
                    onChange={(e) => setCustomCommunityFeatures(e.target.value)}
                    placeholder={"Close to public transit\nTop-rated schools nearby\nWalking distance to parks\nVibrant shopping district"}
                    className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm focus:border-blue-500 transition-all resize-none"
                    rows={4}
                  />
                </div>
                
                <div className="mt-4 bg-blue-900/30 border border-blue-700/50 rounded-lg p-3">
                  <p className="text-xs text-blue-300">
                    💡 These community features will be included as a dedicated page in your Feature Sheet PDF
                  </p>
                </div>
              </div>
            </>
          )}

          {/* IMAGES SECTION */}
          {activeSection === 'images' && (
            <>
              {/* Cover Image */}
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Camera className="w-5 h-5 text-blue-400" />
                  Cover Image *
                </h3>
                <div className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center hover:border-blue-500 transition-colors cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('coverImage', e.target.files)}
                    className="hidden"
                    id="cover-image"
                  />
                  <label htmlFor="cover-image" className="cursor-pointer">
                    <Camera className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-300 font-medium">
                      {formData.coverImage ? formData.coverImage.name : 'Click to upload cover image'}
                    </p>
                    <p className="text-slate-500 text-sm mt-1">This will be the main image on your feature sheet</p>
                  </label>
                </div>
              </div>

              {/* Interior Images */}
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Camera className="w-5 h-5 text-blue-400" />
                  Interior Images
                  <span className="text-xs text-slate-400 font-normal">(6-12 recommended)</span>
                </h3>
                <div className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center hover:border-blue-500 transition-colors cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleImageUpload('interiorImages', e.target.files)}
                    className="hidden"
                    id="interior-images"
                  />
                  <label htmlFor="interior-images" className="cursor-pointer">
                    <Camera className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-300 font-medium">
                      {formData.interiorImages.length > 0 
                        ? `${formData.interiorImages.length} images selected` 
                        : 'Click to upload interior images'}
                    </p>
                  </label>
                </div>
              </div>

              {/* Company Logo */}
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-blue-400" />
                  Company Logo
                </h3>
                <div className="border-2 border-dashed border-slate-600 rounded-xl p-6 text-center hover:border-blue-500 transition-colors cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload('companyLogo', e.target.files)}
                    className="hidden"
                    id="company-logo"
                  />
                  <label htmlFor="company-logo" className="cursor-pointer">
                    <Camera className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-slate-300 text-sm">
                      {formData.companyLogo ? formData.companyLogo.name : 'Upload your logo'}
                    </p>
                  </label>
                </div>
              </div>
            </>
          )}

          {/* BRANDING SECTION */}
          {activeSection === 'branding' && (
            <>
              {/* Template Selection */}
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4">Choose Template</h3>
                <div className="grid grid-cols-2 gap-3">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => setSelectedTemplate(template.id)}
                      className={`p-4 rounded-xl border transition-all text-left ${
                        selectedTemplate === template.id
                          ? 'bg-blue-600/20 border-blue-500'
                          : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                      }`}
                    >
                      <div className="font-semibold text-white">{template.name}</div>
                      <div className="text-xs text-slate-400 mt-1">{template.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Brand Colors */}
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4">Brand Colors</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-slate-400 mb-2">Primary Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.primaryColor}
                        onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer border-2 border-slate-600"
                      />
                      <input
                        type="text"
                        value={formData.primaryColor}
                        onChange={(e) => handleInputChange('primaryColor', e.target.value)}
                        className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-2">Accent Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.secondaryColor}
                        onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer border-2 border-slate-600"
                      />
                      <input
                        type="text"
                        value={formData.secondaryColor}
                        onChange={(e) => handleInputChange('secondaryColor', e.target.value)}
                        className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm font-mono"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Quick Presets */}
                <div className="mt-4">
                  <label className="block text-xs text-slate-400 mb-2">Quick Presets</label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { name: 'Blue', primary: '#1E40AF', secondary: '#9F1239' },
                      { name: 'Gold', primary: '#B8860B', secondary: '#1A1A1A' },
                      { name: 'Teal', primary: '#0D9488', secondary: '#F59E0B' },
                      { name: 'Navy', primary: '#1E3A8A', secondary: '#DC2626' },
                    ].map((preset) => (
                      <button
                        key={preset.name}
                        onClick={() => {
                          handleInputChange('primaryColor', preset.primary);
                          handleInputChange('secondaryColor', preset.secondary);
                        }}
                        className="p-2 bg-slate-700 hover:bg-slate-600 rounded-xl border border-slate-600 transition-all"
                      >
                        <div className="flex gap-1 mb-1 justify-center">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: preset.primary }}></div>
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: preset.secondary }}></div>
                        </div>
                        <p className="text-xs text-slate-300">{preset.name}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Logo Placement */}
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4">Logo Placement</h3>
                <div className="grid grid-cols-3 sm:grid-cols-3 gap-3">
                  {[
                    { value: 'top-left', label: 'Top Left' },
                    { value: 'top-center', label: 'Center' },
                    { value: 'top-right', label: 'Top Right' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleInputChange('logoPlacement', option.value)}
                      className={`p-3 rounded-xl border transition-all text-sm ${
                        formData.logoPlacement === option.value
                          ? 'border-blue-500 bg-blue-500/20 text-white'
                          : 'border-slate-600 bg-slate-700 text-slate-300 hover:border-slate-500'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Enhanced Feature Sheet Options */}
              <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4">✨ Enhanced Options</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">Cover Tagline</label>
                    <select
                      value={formData.tagline}
                      onChange={(e) => handleInputChange('tagline', e.target.value)}
                      className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm focus:border-blue-500 transition-all"
                    >
                      <option value="Exclusive Listing">Exclusive Listing</option>
                      <option value="Just Listed">Just Listed</option>
                      <option value="New on Market">New on Market</option>
                      <option value="Price Reduced">Price Reduced</option>
                      <option value="Open House">Open House</option>
                      <option value="Coming Soon">Coming Soon</option>
                      <option value="Luxury Living">Luxury Living</option>
                      <option value="Must See">Must See</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-blue-400 mb-1.5 uppercase">Virtual Tour URL (for QR Code)</label>
                    <input
                      type="url"
                      value={formData.virtualTourUrl}
                      onChange={(e) => handleInputChange('virtualTourUrl', e.target.value)}
                      placeholder="https://tour.example.com/property123"
                      className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-slate-50 text-sm focus:border-blue-500 transition-all"
                    />
                    <p className="text-xs text-slate-500 mt-1">Add a URL to generate a scannable QR code on the contact page</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right Column - Summary & Generate */}
        <div className="space-y-4">
          {/* Property Summary Card */}
          <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700 lg:sticky lg:top-6">
            <h3 className="text-lg font-semibold text-white mb-4">Summary</h3>
            
            {formData.address && (
              <div className="flex items-center gap-2 text-slate-300 text-sm mb-4 pb-3 border-b border-slate-700">
                <MapPin className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <span className="truncate">{formData.address}</span>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div className="bg-slate-700/50 rounded-xl p-2.5 text-center">
                <div className="text-lg font-bold text-blue-400">{formData.bedrooms || '-'}</div>
                <div className="text-xs text-slate-400">Beds</div>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-2.5 text-center">
                <div className="text-lg font-bold text-blue-400">{formData.bathrooms || '-'}</div>
                <div className="text-xs text-slate-400">Baths</div>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-2.5 text-center">
                <div className="text-lg font-bold text-blue-400">{formData.squareFootage || '-'}</div>
                <div className="text-xs text-slate-400">Sq Ft</div>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-2.5 text-center">
                <div className="text-lg font-bold text-rose-400">{formData.price || '-'}</div>
                <div className="text-xs text-slate-400">Price</div>
              </div>
            </div>
            
            {/* Checklist */}
            <div className="space-y-2 mb-4 text-sm">
              <div className={`flex items-center gap-2 ${formData.address ? 'text-green-400' : 'text-slate-500'}`}>
                {formData.address ? <Check className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border border-slate-500" />}
                Address
              </div>
              <div className={`flex items-center gap-2 ${formData.coverImage ? 'text-green-400' : 'text-slate-500'}`}>
                {formData.coverImage ? <Check className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border border-slate-500" />}
                Cover Image
              </div>
              <div className={`flex items-center gap-2 ${formData.description ? 'text-green-400' : 'text-slate-500'}`}>
                {formData.description ? <Check className="w-4 h-4" /> : <div className="w-4 h-4 rounded-full border border-slate-500" />}
                Description
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={generateFeatureSheet}
              disabled={isGenerating || !formData.coverImage || !formData.address}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-blue-500 hover:to-rose-600 disabled:from-slate-600 disabled:to-slate-600 rounded-xl text-white font-bold shadow-lg transition-all disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Generating...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Generate PDF
                </span>
              )}
            </button>
            
            {(!formData.coverImage || !formData.address) && (
              <p className="text-xs text-slate-500 text-center mt-2">
                Address and cover image required
              </p>
            )}
          </div>

          {/* Generated PDF Download */}
          {generatedPDF && (
            <div className="bg-green-900/20 rounded-xl p-5 border border-green-500/50">
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Check className="w-5 h-5 text-green-400" />
                Ready!
              </h3>
              <div className="space-y-2">
                <a
                  href={generatedPDF}
                  download="feature-sheet.pdf"
                  className="flex items-center justify-center gap-2 w-full py-2.5 bg-green-600 hover:bg-green-500 rounded-xl text-white font-medium text-sm transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </a>
                <button
                  onClick={() => window.open(generatedPDF, '_blank')}
                  className="flex items-center justify-center gap-2 w-full py-2.5 bg-slate-700 hover:bg-slate-600 rounded-xl text-slate-200 font-medium text-sm transition-colors"
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Generated Feature Sheet Preview */}
      {featureSheet && (
        <div className="bg-white rounded-xl overflow-hidden shadow-2xl animate-slide-in-up">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
            <h1 className="text-3xl font-bold">{featureSheet.headline}</h1>
            <p className="text-blue-100 mt-2">{featureSheet.tagline}</p>
          </div>
          
          {/* Main Image */}
          {images && images.length > 0 && (
            <div className="relative h-64">
              <img src={images[0].preview} alt="Main property" className="w-full h-full object-cover" />
              <div className="absolute bottom-4 right-4 bg-rose-600 text-white px-4 py-2 rounded-xl font-bold text-xl shadow-lg">
                {featureSheet.price}
              </div>
            </div>
          )}
          
          {/* Content */}
          <div className="p-6">
            <div className="flex items-center gap-2 text-gray-600 mb-4">
              <MapPin className="w-5 h-5" />
              <span className="font-medium">{featureSheet.address}</span>
            </div>
            
            {/* Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              {featureSheet.highlights.map((highlight, idx) => (
                <div key={idx} className="bg-slate-100 rounded-xl p-3 text-center text-gray-700 text-sm font-medium">
                  {highlight}
                </div>
              ))}
            </div>
            
            {/* Description */}
            <p className="text-gray-600 leading-relaxed mb-6">
              {featureSheet.description.substring(0, 500)}{featureSheet.description.length > 500 ? '...' : ''}
            </p>

            {/* Agent Info */}
            {featureSheet.agent.name && (
              <div className="border-t pt-4 flex items-center justify-between">
                <div>
                  <div className="font-bold text-gray-800">{featureSheet.agent.name}</div>
                  <div className="text-sm text-gray-600">{featureSheet.agent.company}</div>
                </div>
                <div className="text-right text-sm text-gray-600">
                  <div>{featureSheet.agent.phone}</div>
                  <div>{featureSheet.agent.email}</div>
                </div>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="bg-slate-100 p-4 flex justify-between items-center">
            <a 
              href={generatedPDF}
              download="feature-sheet.pdf"
              className="px-6 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </a>
            <button 
              onClick={() => {
                if (generatedPDF) {
                  const printWindow = window.open(generatedPDF, '_blank');
                  if (printWindow) {
                    printWindow.onload = () => printWindow.print();
                  }
                }
              }}
              className="px-6 py-2 border border-slate-300 text-slate-600 rounded-xl font-medium hover:bg-slate-200 transition-colors flex items-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Scroll to Top Button
const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <button 
      onClick={scrollToTop}
      className={`scroll-to-top ${isVisible ? 'visible' : ''}`}
      aria-label="Scroll to top"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
    </button>
  );
};

// Stats Dashboard Component
const StatsDashboard = ({ description }) => {
  const wordCount = description ? description.split(/\s+/).length : 0;
  const charCount = description ? description.length : 0;
  const sentenceCount = description ? description.split(/[.!?]+/).filter(s => s.trim()).length : 0;
  const readingTime = Math.ceil(wordCount / 200);
  const seoScore = Math.min(100, Math.floor((wordCount / 200) * 50 + (charCount > 500 ? 30 : 15) + (sentenceCount > 5 ? 20 : 10)));

  return (
    <div className="stats-dashboard">
      <div className="stat-item hover-lift">
        <div className="stat-value">{wordCount}</div>
        <div className="stat-label">Words</div>
      </div>
      <div className="stat-item hover-lift">
        <div className="stat-value">{charCount}</div>
        <div className="stat-label">Characters</div>
      </div>
      <div className="stat-item hover-lift">
        <div className="stat-value">{sentenceCount}</div>
        <div className="stat-label">Sentences</div>
      </div>
      <div className="stat-item hover-lift">
        <div className="stat-value">{readingTime}m</div>
        <div className="stat-label">Read Time</div>
      </div>
      <div className="stat-item hover-lift bg-green-500/10 border-green-500/30">
        <div className="stat-value text-green-400">{seoScore}</div>
        <div className="stat-label text-green-400">SEO Score</div>
      </div>
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ icon: Icon, label, description, isActive, isLocked, onClick }) => (
  <div 
    onClick={onClick}
    className={`feature-card ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
  >
    <div className="flex items-start gap-4">
      <div className={`p-3 rounded-xl ${isActive ? 'bg-blue-500/20' : 'bg-slate-700/50'}`}>
        <Icon className={`w-6 h-6 ${isActive ? 'text-blue-400' : 'text-slate-400'}`} />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h4 className={`font-semibold ${isActive ? 'text-white' : 'text-slate-200'}`}>{label}</h4>
          {isLocked && <Lock className="w-4 h-4 text-amber-400" />}
        </div>
        <p className="text-sm text-slate-400 mt-1">{description}</p>
      </div>
    </div>
  </div>
);

// Pricing Modal
const PricingModal = ({ isOpen, onClose, onSubscribe }) => {
  if (!isOpen) return null;

  const freeFeatures = [
    "✨ AI-powered listing descriptions",
    "📋 Copy to clipboard",
    "5 generations per month"
  ];

  const basicFeatures = [
    "✨ AI-powered listing descriptions",
    "📱 Social Media Post Generator",
    "#️⃣ Hashtag Generator",
    "💾 Save & manage listings",
    "📋 Copy to clipboard",
    "25 generations per month"
  ];

  const premiumFeatures = [
    "🔄 Unlimited generations",
    "📝 A/B Description Variations",
    "📱 QR Code Generator",
    "#️⃣ Hashtag Generator",
    "🎴 Property Highlight Cards",
    "✉️ Client Email Templates",
    "🗺️ Neighborhood Comparison",
    "🎬 Video Script Generator",
    "📧 Email Marketing Suite",
    "👥 Buyer Persona Targeting",
    "🔍 Advanced SEO Analyzer",
    "🖨️ Print Marketing Materials",
    "🏠 Open House Toolkit",
    "👔 Professional Agent Tools",
    "🌍 Multi-Language Translation",
    "📱 Social Media Content",
    "📄 MLS Export & PDF Brochures",
    "🛋️ Virtual Staging AI",
    "💰 AI Pricing Suggestions",
    "⚡ Priority Support"
  ];

  const brokerageFeatures = [
    "🏢 Unlimited listings",
    "🤖 Custom AI models",
    "👥 Team collaboration",
    "📊 Advanced analytics",
    "👤 Dedicated account manager",
    "🔗 API access",
    "📞 24/7 support",
    "🎯 White-label options",
    "📈 Performance reporting"
  ];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 modal-backdrop">
      <div className="bg-slate-800 rounded-3xl p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto border border-slate-700 shadow-2xl modal-content custom-scrollbar">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-3xl font-bold text-slate-50 mb-2">Choose Your Plan</h2>
            <p className="text-slate-400">Select the perfect plan for your real estate business</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200 transition-colors">
            <X className="w-8 h-8" />
          </button>
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          {/* Free Plan */}
          <div className="p-5 bg-slate-900/50 rounded-2xl border border-slate-700 hover:border-slate-500 transition-all">
            <div className="mb-4">
              <h3 className="text-xl font-bold text-slate-50 mb-2">Free</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-bold text-slate-50">$0</span>
                <span className="text-sm text-slate-400">/month</span>
              </div>
              <p className="text-slate-400 text-xs">Try out the basics</p>
            </div>
            
            <ul className="space-y-2 mb-6">
              {freeFeatures.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-300">
                  <Check className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                  <span className="text-xs">{feature}</span>
                </li>
              ))}
            </ul>
            
            <button 
              onClick={onClose} 
              className="w-full py-2 bg-slate-700 hover:bg-slate-600 border border-slate-600 rounded-xl text-slate-50 font-medium text-sm transition-all"
            >
              Current Plan
            </button>
          </div>

          {/* Basic Plan - $29.99 */}
          <div className="p-5 bg-slate-900/50 rounded-2xl border border-slate-700 hover:border-blue-500/50 transition-all">
            <div className="mb-4">
              <h3 className="text-xl font-bold text-slate-50 mb-2">Basic</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-bold text-slate-50">$29.99</span>
                <span className="text-sm text-slate-400">/month</span>
              </div>
              <p className="text-slate-400 text-xs">For individual agents</p>
            </div>
            
            <ul className="space-y-2 mb-6">
              {basicFeatures.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-200">
                  <Check className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span className="text-xs">{feature}</span>
                </li>
              ))}
            </ul>
            
            <button 
              onClick={() => onSubscribe('basic')} 
              className="w-full py-2 bg-blue-600 hover:bg-blue-500 rounded-xl text-white font-medium text-sm transition-all"
            >
              Get Basic
            </button>
          </div>

          {/* Premium Plan - $59.99 */}
          <div className="p-5 bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-2xl border-2 border-purple-500 relative shadow-xl shadow-purple-500/20">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full text-xs font-bold text-white shadow-lg">
              ⭐ MOST POPULAR
            </div>
            
            <div className="mb-4 pt-2">
              <h3 className="text-xl font-bold text-slate-50 mb-2 flex items-center gap-2">
                <Crown className="w-5 h-5 text-purple-400" />
                Premium
              </h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-bold text-slate-50">$59.99</span>
                <span className="text-sm text-slate-400">/month</span>
              </div>
              <p className="text-slate-300 text-xs">Everything in Basic, plus:</p>
            </div>
            
            <ul className="space-y-1.5 mb-6 max-h-48 overflow-y-auto pr-1">
              {premiumFeatures.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-100">
                  <Check className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                  <span className="text-xs">{feature}</span>
                </li>
              ))}
            </ul>
            
            <button 
              onClick={() => onSubscribe('premium')} 
              className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 rounded-xl text-white font-bold text-sm shadow-lg transition-all"
            >
              Upgrade to Premium
            </button>
          </div>

          {/* Brokerage Plan - $199 */}
          <div className="p-5 bg-gradient-to-br from-yellow-900/20 to-orange-900/20 rounded-2xl border border-yellow-500/50 hover:border-yellow-400 transition-all">
            <div className="mb-4">
              <h3 className="text-xl font-bold text-slate-50 mb-2 flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                Brokerage
              </h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-bold text-slate-50">$199</span>
                <span className="text-sm text-slate-400">/month</span>
              </div>
              <p className="text-slate-300 text-xs">Enterprise features for teams</p>
            </div>
            
            <ul className="space-y-2 mb-6">
              {brokerageFeatures.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-100">
                  <Check className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <span className="text-xs">{feature}</span>
                </li>
              ))}
            </ul>
            
            <button 
              onClick={() => onSubscribe('brokerage')} 
              className="w-full py-2 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 rounded-xl text-white font-bold text-sm transition-all"
            >
              Activate Genius
            </button>
            <p className="text-center text-[10px] text-slate-500 mt-2">Intelligence Score: 95/100</p>
          </div>
        </div>

        {/* Features Comparison Note */}
        <div className="mt-6 p-3 bg-slate-900/50 rounded-xl border border-slate-700">
          <p className="text-xs text-slate-400 text-center">
            All paid plans • Cancel anytime
          </p>
        </div>
      </div>
    </div>
  );
};

// Plans Selection Page Component
const PlansSelectionPage = ({ onSelectPlan, onBack, referralCode, referralPartnerName }) => {
  const freeFeatures = [
    "✨ AI-powered listing descriptions",
    "📋 Copy to clipboard",
    "5 generations per month"
  ];

  const basicFeatures = [
    "✨ AI-powered listing descriptions",
    "📱 Social Media Post Generator",
    "#️⃣ Hashtag Generator",
    "💾 Save & manage listings",
    "📋 Copy to clipboard",
    "25 generations per month"
  ];

  const premiumFeatures = [
    "✨ Everything in Basic, plus:",
    "🔄 Unlimited generations",
    "📝 A/B Description Variations",
    "📱 QR Code Generator",
    "#️⃣ Hashtag Generator",
    "🎴 Property Highlight Cards",
    "✉️ Client Email Templates",
    "🗺️ Neighborhood Comparison",
    "🎬 Video Script Generator",
    "📧 Email Marketing Suite",
    "👥 Buyer Persona Targeting",
    "🔍 Advanced SEO Analyzer",
    "🖨️ Print Marketing Materials",
    "🏠 Open House Toolkit",
    "👔 Professional Agent Tools",
    "🌍 Multi-Language Translation",
    "📱 Social Media Content Generator",
    "📄 MLS Export & PDF Brochures",
    "🛋️ Virtual Staging AI",
    "💰 AI Pricing Suggestions",
    "⚡ Priority Support"
  ];

  const brokerageFeatures = [
    "🌟 Everything in Premium, plus:",
    "👥 Unlimited agent sub-accounts",
    "🏢 Unlimited listings",
    "🤖 Custom AI models",
    "👥 Team collaboration tools"
  ];

  return (
    <div className="min-h-screen py-8 px-4" style={{ background: 'linear-gradient(135deg, #5B21B6 0%, #4C1D95 30%, #3730A3 60%, #1E3A8A 100%)' }}>
      {/* Referral Banner */}
      {referralCode && referralPartnerName && (
        <div className="max-w-6xl mx-auto mb-4">
          <div className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-4 py-3 rounded-xl text-center shadow-lg">
            <span className="font-medium">🎉 You were referred by {referralPartnerName}</span>
            <span className="ml-2 text-white/80">— Welcome to GeniusList!</span>
          </div>
        </div>
      )}
      
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/logo.png" alt="GeniusList" className="w-20 h-20 rounded-2xl mx-auto mb-4 object-contain" />
          <h1 className="text-4xl font-bold text-white mb-3">Choose Your Plan</h1>
          <p className="text-white/80 text-lg">Select the perfect plan for your real estate business</p>
        </div>

        {/* Plans Grid - Using flex with equal height cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 items-stretch">
          {/* Free Plan */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 hover:border-white/40 transition-all hover:transform hover:scale-105 flex flex-col" data-testid="plan-free">
            <div className="mb-5">
              <h3 className="text-2xl font-bold text-white mb-2">Free</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-3xl font-bold text-white whitespace-nowrap">$0</span>
                <span className="text-white/60 text-sm">/month</span>
              </div>
              <p className="text-white/70 text-sm">Try out the basics</p>
            </div>
            
            <ul className="space-y-3 flex-grow">
              {freeFeatures.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-white/90">
                  <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{feature}</span>
                </li>
              ))}
            </ul>
            
            <button 
              onClick={() => onSelectPlan('free')}
              className="w-full py-3 bg-white/20 hover:bg-white/30 border border-white/30 rounded-xl text-white font-semibold transition-all mt-6"
              data-testid="select-plan-free"
            >
              Start Free
            </button>
          </div>

          {/* Basic Plan - $29.99 */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-blue-400/50 hover:border-blue-400 transition-all hover:transform hover:scale-105 flex flex-col" data-testid="plan-basic">
            <div className="mb-5">
              <h3 className="text-2xl font-bold text-white mb-2">Basic</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-3xl font-bold text-white whitespace-nowrap">$29.99</span>
                <span className="text-white/60 text-sm">/month</span>
              </div>
              <p className="text-white/70 text-sm">For individual agents</p>
            </div>
            
            <ul className="space-y-3 flex-grow">
              {basicFeatures.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-white/90">
                  <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{feature}</span>
                </li>
              ))}
            </ul>
            
            <button 
              onClick={() => onSelectPlan('basic')}
              className="w-full py-3 bg-blue-500 hover:bg-blue-400 rounded-xl text-white font-semibold transition-all shadow-lg shadow-blue-500/30 mt-6"
              data-testid="select-plan-basic"
            >
              Get Basic - $29.99/mo
            </button>
          </div>

          {/* Premium Plan - $59.99 - Most Popular */}
          <div className="backdrop-blur-lg rounded-2xl p-6 border-2 relative shadow-xl hover:transform hover:scale-105 transition-all flex flex-col" style={{ background: 'linear-gradient(135deg, rgba(107, 70, 193, 0.4) 0%, rgba(59, 130, 246, 0.4) 100%)', borderColor: '#3B82F6', boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)' }} data-testid="plan-premium">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-sm font-bold text-white shadow-lg" style={{ background: 'linear-gradient(90deg, #6B46C1 0%, #3B82F6 100%)' }}>
              ⭐ MOST POPULAR
            </div>
            
            <div className="mb-5 pt-2">
              <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                <Crown className="w-6 h-6 text-blue-300" />
                Premium
              </h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-3xl font-bold text-white whitespace-nowrap">$59.99</span>
                <span className="text-white/60 text-sm">/month</span>
              </div>
              <p className="text-white/70 text-sm">Full marketing suite</p>
            </div>
            
            <ul className="space-y-2 flex-grow max-h-64 overflow-y-auto pr-2 custom-scrollbar">
              {premiumFeatures.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-white">
                  <Check className="w-5 h-5 text-blue-300 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{feature}</span>
                </li>
              ))}
            </ul>
            
            <button 
              onClick={() => onSelectPlan('premium')}
              className="w-full py-3 rounded-xl text-white font-bold text-lg shadow-lg transition-all mt-6 hover:opacity-90"
              style={{ background: 'linear-gradient(90deg, #6B46C1 0%, #3B82F6 100%)', boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)' }}
              data-testid="select-plan-premium"
            >
              Get Premium - $59.99/mo
            </button>
          </div>

          {/* Brokerage Plan - $199 */}
          <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 backdrop-blur-lg rounded-2xl p-6 border border-yellow-400/50 hover:border-yellow-400 transition-all hover:transform hover:scale-105 flex flex-col" data-testid="plan-brokerage">
            <div className="mb-5">
              <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                <Zap className="w-6 h-6 text-yellow-400" />
                Brokerage
              </h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-3xl font-bold text-white whitespace-nowrap">$199</span>
                <span className="text-white/60 text-sm">/month</span>
              </div>
              <p className="text-white/70 text-sm">Enterprise for teams</p>
            </div>
            
            <ul className="space-y-2 flex-grow max-h-64 overflow-y-auto pr-2 custom-scrollbar">
              {brokerageFeatures.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-white/90">
                  <Check className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{feature}</span>
                </li>
              ))}
            </ul>
            
            <button 
              onClick={() => onSelectPlan('brokerage')}
              className="w-full py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 rounded-xl text-white font-bold transition-all shadow-lg shadow-yellow-500/30 mt-6"
              data-testid="select-plan-brokerage"
            >
              Activate Genius - $199/mo
            </button>
          </div>
        </div>

        {/* Back to Login Link */}
        <div className="text-center">
          <p className="text-white/70 mb-4">All paid plans • Cancel anytime</p>
          <button 
            onClick={onBack}
            className="text-white/80 hover:text-white hover:underline font-medium"
            data-testid="back-to-login"
          >
            ← Already have an account? Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

// Auth Modal
const AuthModal = ({ isOpen, onClose, onAuth, embedded = false, selectedPlan = null, onShowPlans = null, initialMode = 'login', referralCode = '', referralPartnerName = '' }) => {
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  
  // Check if this is a post-payment signup (user already has email from Stripe)
  const paidUserEmail = localStorage.getItem('paid_user_email');
  const paidSessionId = localStorage.getItem('paid_session_id');
  const paidUserName = localStorage.getItem('paid_user_name');
  const isPostPaymentSignup = !!paidUserEmail && !!paidSessionId;

  // Update isLogin when initialMode changes
  React.useEffect(() => {
    setIsLogin(initialMode === 'login');
    // Pre-fill email and name from payment if available
    if (isPostPaymentSignup) {
      setEmail(paidUserEmail);
      if (paidUserName) {
        setName(paidUserName);
      }
    }
  }, [initialMode, isPostPaymentSignup, paidUserEmail, paidUserName]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotPasswordEmail
      });
      
      if (res.data.success) {
        setForgotPasswordSent(true);
      } else {
        setError(res.data.error || 'Failed to send reset email');
      }
    } catch (err) {
      setError('Failed to send reset email. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  if (!isOpen && !embedded) return null;

  // Forgot Password UI
  if (showForgotPassword) {
    const forgotPasswordContent = (
      <div className="space-y-4">
        <button
          onClick={() => {
            setShowForgotPassword(false);
            setForgotPasswordSent(false);
            setForgotPasswordEmail('');
            setError('');
          }}
          className={`flex items-center gap-2 ${embedded ? 'text-gray-600' : 'text-slate-400'} hover:underline text-sm mb-4`}
        >
          ← Back to Sign In
        </button>
        
        <h2 className={`text-2xl font-bold ${embedded ? 'text-gray-900' : 'text-white'}`}>
          Reset Password
        </h2>
        
        {forgotPasswordSent ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-700">
            <p className="font-medium">Check your email!</p>
            <p className="text-sm mt-1">If an account exists with that email, we've sent password reset instructions.</p>
          </div>
        ) : (
          <>
            <p className={`text-sm ${embedded ? 'text-gray-600' : 'text-slate-400'}`}>
              Enter your email and we'll send you a link to reset your password.
            </p>
            
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                {error}
              </div>
            )}
            
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${embedded ? 'text-gray-600' : 'text-slate-300'}`}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  required
                  className={`w-full px-4 py-3 rounded-xl border ${embedded ? 'border-gray-300 bg-white text-gray-900' : 'border-slate-600 bg-slate-700/50 text-white'} focus:outline-none focus:ring-2 focus:ring-purple-500`}
                  placeholder="Enter your email"
                />
              </div>
              
              <button
                type="submit"
                disabled={forgotPasswordLoading}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 rounded-xl text-white font-bold transition-all"
              >
                {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          </>
        )}
      </div>
    );
    
    if (embedded) return forgotPasswordContent;
    
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-slate-800/90 backdrop-blur-lg rounded-2xl p-8 max-w-md w-full border border-slate-700 shadow-2xl">
          {forgotPasswordContent}
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Check if this is a post-payment signup (completing account after Stripe payment)
      const sessionId = localStorage.getItem('paid_session_id');
      const paidEmail = localStorage.getItem('paid_user_email');
      
      if (!isLogin && sessionId && paidEmail) {
        // Post-payment signup: User already paid, just needs to set password
        const res = await axios.post(`${API}/auth/complete-payment-signup`, {
          email: paidEmail,
          password,
          name: name || paidUserName || paidEmail.split('@')[0],
          session_id: sessionId
        });
        
        if (res.data.success) {
          // Clear payment-related localStorage
          localStorage.removeItem('paid_session_id');
          localStorage.removeItem('paid_user_email');
          localStorage.removeItem('paid_user_name');
          localStorage.removeItem('paid_user_id');
          localStorage.removeItem('pending_plan');
          
          // Clear referral codes
          clearReferralCode();
          localStorage.removeItem('gl_referral_code');
          localStorage.removeItem('gl_referral_partner');
          
          // Log them in
          onAuth({ ...res.data, selectedPlan: selectedPlan || res.data.plan });
          if (!embedded) onClose();
        } else {
          setError(res.data.error || 'Failed to complete signup');
        }
      } else {
        // Normal login or signup flow
        const endpoint = isLogin ? '/auth/login' : '/auth/register';
        // Include referral code in signup payload
        const storedReferralCode = getReferralCode() || localStorage.getItem('gl_referral_code');
        const payload = isLogin 
          ? { email, password } 
          : { 
              email, 
              password, 
              name, 
              plan: selectedPlan || 'free',
              referral_code: referralCode || storedReferralCode || null
            };
        const res = await axios.post(`${API}${endpoint}`, payload);

        if (res.data.success) {
          // Track referral with Partner Dashboard on successful signup
          if (!isLogin && storedReferralCode) {
            try {
              const trackResult = await trackReferral({
                refCode: storedReferralCode,
                customerEmail: email,
                customerName: name,
                plan: selectedPlan || 'free'
              });
              
              if (trackResult.success && trackResult.referral_id) {
                console.log('Referral tracked successfully:', trackResult.referral_id);
                
                // Save referral_id to user record in backend for payment activation
                try {
                  await axios.post(`${API}/user/save-referral-id?user_id=${res.data.user_id}&referral_id=${trackResult.referral_id}`);
                  console.log('Referral ID saved to user record');
                } catch (saveErr) {
                  console.error('Failed to save referral ID to user:', saveErr);
                  // Store in localStorage as backup
                  localStorage.setItem('gl_tracked_referral_id', trackResult.referral_id);
                }
              } else {
                console.log('Referral tracking failed:', trackResult.error);
              }
            } catch (trackErr) {
              console.error('Error tracking referral:', trackErr);
            }
            
            // Clear referral code after tracking attempt
            clearReferralCode();
            localStorage.removeItem('gl_referral_code');
            localStorage.removeItem('gl_referral_partner');
          }
          
          // Pass the selected plan along with auth data
          onAuth({ ...res.data, selectedPlan: selectedPlan || 'free' });
          if (!embedded) onClose();
        } else {
          setError(res.data.error || 'Authentication failed');
        }
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    }
    setLoading(false);
  };

  // Emergent Google Auth - No configuration needed!
  const handleGoogleSignIn = () => {
    setGoogleLoading(true);
    setError('');
    
    // Store the selected plan in localStorage so we can retrieve it after redirect
    if (selectedPlan) {
      localStorage.setItem('pendingPlan', selectedPlan);
    }
    
    // Redirect to Emergent Auth
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `${EMERGENT_AUTH_URL}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSignUpClick = () => {
    if (onShowPlans) {
      onShowPlans();
    } else {
      setIsLogin(false);
    }
  };

  const getPlanBadge = () => {
    if (!selectedPlan || selectedPlan === 'free') return null;
    const planNames = {
      basic: { name: 'Basic', price: '$29.99/mo', color: 'bg-blue-500' },
      premium: { name: 'Premium', price: '$59.99/mo', color: 'bg-purple-500' },
      brokerage: { name: 'Brokerage', price: '$199/mo', color: 'bg-yellow-500' }
    };
    const plan = planNames[selectedPlan];
    if (!plan) return null;
    return (
      <div className={`${plan.color} text-white text-xs font-semibold px-3 py-1 rounded-full inline-flex items-center gap-1 mb-4`}>
        <Crown className="w-3 h-3" /> {plan.name} Plan - {plan.price}
      </div>
    );
  };

  const formContent = (
    <>
      <div className={`flex justify-between items-center mb-4 ${embedded ? '' : 'animate-slide-in-down'}`}>
        <div>
          <h2 className={`text-2xl font-bold ${embedded ? 'text-gray-800' : 'text-white'}`}>
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h2>
          {!isLogin && getPlanBadge()}
        </div>
        {!embedded && (
          <button onClick={onClose} className="text-slate-400 hover:text-white btn-press"><X className="w-6 h-6" /></button>
        )}
      </div>

      {/* Referral Banner - Show when valid referral code is present */}
      {!isLogin && (referralPartnerName || localStorage.getItem('gl_referral_partner')) && (
        <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100 rounded-xl">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <Gift className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-purple-900">
                You were referred by <span className="font-bold">{referralPartnerName || localStorage.getItem('gl_referral_partner')}</span>
              </p>
              <p className="text-xs text-purple-600">Welcome to GeniusList!</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {!isLogin && (
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${embedded ? 'text-gray-600' : 'input-label'}`}>Name</label>
            <input 
              type="text" 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              className={embedded ? "w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" : "modern-input"} 
              placeholder="Your name"
              data-testid="signup-name"
            />
          </div>
        )}
        <div>
          <label className={`block text-sm font-medium mb-1.5 ${embedded ? 'text-gray-600' : 'input-label'}`}>Email</label>
          <input 
            type="email" 
            value={email} 
            onChange={(e) => !isPostPaymentSignup && setEmail(e.target.value)} 
            className={`${embedded ? "w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" : "modern-input"} ${isPostPaymentSignup ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            placeholder="you@example.com" 
            required
            readOnly={isPostPaymentSignup}
            data-testid="auth-email"
          />
          {isPostPaymentSignup && (
            <p className="text-xs text-gray-500 mt-1">Email from your payment - cannot be changed</p>
          )}
        </div>
        <div>
          <label className={`block text-sm font-medium mb-1.5 ${embedded ? 'text-gray-600' : 'input-label'}`}>
            {isPostPaymentSignup ? 'Create Password' : 'Password'}
          </label>
          <input 
            type="password" 
            value={password} 
            onChange={(e) => setPassword(e.target.value)} 
            className={embedded ? "w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" : "modern-input"} 
            placeholder="••••••••" 
            required
            data-testid="auth-password"
          />
        </div>

        {error && <p className="text-red-500 text-sm" data-testid="auth-error">{error}</p>}

        <button 
          type="submit" 
          disabled={loading} 
          className={`w-full py-3 ${embedded ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500' : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-blue-500 hover:to-rose-600'} rounded-xl text-white font-bold transition-all`}
          data-testid="auth-submit"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <LoadingSpinner size="sm" /> Processing...
            </span>
          ) : isPostPaymentSignup ? 'Complete Signup' : (isLogin ? 'Sign In' : 'Create Account')}
        </button>

        {/* Forgot Password Link - Only show on login */}
        {isLogin && (
          <div className="text-center mt-3">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className={`text-sm ${embedded ? 'text-purple-600' : 'text-purple-400'} hover:underline`}
            >
              Forgot your password?
            </button>
          </div>
        )}

        {/* 
        ===========================================
        GOOGLE SIGN-IN - TEMPORARILY DISABLED
        Uncomment when ready to launch with Google Auth
        ===========================================
        
        {/* Divider *}
        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className={`w-full border-t ${embedded ? 'border-gray-200' : 'border-slate-600'}`}></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className={`px-3 ${embedded ? 'bg-white text-gray-500' : 'bg-slate-800/50 text-slate-400'}`}>
              or continue with
            </span>
          </div>
        </div>

        {/* Google Sign-In Button - Using Emergent Auth (No Config Needed!) *}
        <div className="flex justify-center" data-testid="google-signin-container">
          {googleLoading ? (
            <div className="flex items-center justify-center py-3 px-6 bg-white rounded-lg shadow-sm border border-gray-200">
              <LoadingSpinner size="sm" />
              <span className="ml-2 text-gray-600">Redirecting to Google...</span>
            </div>
          ) : (
            <button
              onClick={handleGoogleSignIn}
              className="flex items-center justify-center gap-3 w-full py-3 px-6 bg-white hover:bg-gray-50 rounded-lg shadow-sm border border-gray-200 transition-colors"
              data-testid="google-signin-button"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="text-gray-700 font-medium">
                {isLogin ? 'Sign in with Google' : 'Sign up with Google'}
              </span>
            </button>
          )}
        </div>
        
        END OF GOOGLE SIGN-IN SECTION
        ===========================================
        */}
      </form>

      {/* Only show "Sign In" link when in signup mode (for existing users) */}
      {!isLogin && (
        <p className={`text-center mt-4 ${embedded ? 'text-gray-500' : 'text-slate-400'}`}>
          Already have an account?{' '}
          <button 
            onClick={() => setIsLogin(true)} 
            className="text-purple-600 hover:underline font-medium"
            data-testid="show-login"
          >
            Sign In
          </button>
        </p>
      )}
      
      {/* Show "Create Account" link when in login mode - redirects to plan selection if no plan selected */}
      {isLogin && (
        <p className={`text-center mt-4 ${embedded ? 'text-gray-500' : 'text-slate-400'}`}>
          Don't have an account?{' '}
          <button 
            onClick={() => {
              // If no plan selected, go to plan selection first
              if (!selectedPlan && onShowPlans) {
                onShowPlans();
              } else {
                setIsLogin(false);
              }
            }} 
            className="text-purple-600 hover:underline font-medium"
            data-testid="show-signup"
          >
            Create Account
          </button>
        </p>
      )}
    </>
  );

  if (embedded) {
    return formContent;
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 modal-backdrop">
      <div className="glass-card rounded-2xl p-8 max-w-md w-full modal-content">
        {formContent}
      </div>
    </div>
  );
};

// Main App Component
function ListingApp() {
  // Auth State
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showPlansPage, setShowPlansPage] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [userPlan, setUserPlan] = useState('free'); // 'free', 'basic', 'premium', or 'brokerage'
  const [showLandingPage, setShowLandingPage] = useState(true); // Show landing page by default
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false); // Mobile sidebar state
  const [showPaymentSuccessSignup, setShowPaymentSuccessSignup] = useState(false); // Show signup after payment
  
  // Password Management State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [hasPassword, setHasPassword] = useState(true);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  
  // Referral Code State
  const [referralCode, setReferralCode] = useState('');
  const [referralPartnerName, setReferralPartnerName] = useState('');
  
  // Admin emails - add your email(s) here for admin access
  const ADMIN_EMAILS = ['info@geniuslist.ai', 'admin@geniuslist.com', 'testuser@example.com'];
  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());

  // Function to check feature access
  const hasAccess = (feature) => {
    if (userPlan === 'premium') return true;
    if (userPlan === 'basic' && feature === 'generate') return true;
    return false;
  };

  // Form State
  const [formData, setFormData] = useState({
    region: '', propertyType: '', address: '', bedrooms: '', bathrooms: '',
    squareFootage: '', keyFeatures: '', neighborhoodHighlights: '',
    style: 'Luxury', tone: 'Elegant', price: ''
  });

  // Media State
  const [images, setImages] = useState([]);

  // Generation State
  const [generatedDescription, setGeneratedDescription] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Toast State
  const [toast, setToast] = useState(null);
  
  // Show toast helper
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Check if user has password set
  const checkHasPassword = async () => {
    const userId = user?.user_id || user?.id;
    if (!userId) return;
    
    try {
      const res = await axios.get(`${API}/auth/has-password?user_id=${userId}`);
      if (res.data.success) {
        setHasPassword(res.data.has_password);
      }
    } catch (err) {
      console.error('Error checking password status:', err);
    }
  };

  // Handle password change/set
  const handlePasswordSubmit = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    
    const userId = user?.user_id || user?.id;
    if (!userId) {
      setPasswordError('Please log in to change your password');
      return;
    }
    
    // Validation
    if (passwordData.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    setIsChangingPassword(true);
    
    try {
      let res;
      if (hasPassword) {
        // Change existing password
        if (!passwordData.currentPassword) {
          setPasswordError('Please enter your current password');
          setIsChangingPassword(false);
          return;
        }
        res = await axios.post(`${API}/auth/change-password`, {
          user_id: userId,
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        });
      } else {
        // Set new password for first time
        res = await axios.post(`${API}/auth/set-password?user_id=${userId}&new_password=${encodeURIComponent(passwordData.newPassword)}`);
      }
      
      if (res.data.success) {
        setPasswordSuccess(hasPassword ? 'Password updated successfully!' : 'Password set successfully!');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setHasPassword(true);
        showToast(hasPassword ? 'Password updated!' : 'Password set!', 'success');
      } else {
        setPasswordError(res.data.error || 'Failed to update password');
      }
    } catch (err) {
      console.error('Password change error:', err);
      setPasswordError(err.response?.data?.error || 'Failed to update password. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Image Analysis
  const [imageAnalysis, setImageAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);

  // Premium Features State
  const [activeTab, setActiveTab] = useState('generate');
  
  // Stable callback for navigation that won't cause re-renders
  const handleTabChange = React.useCallback((newTab) => {
    setActiveTab(newTab);
  }, []);
  
  const [translations, setTranslations] = useState({});
  const [socialMedia, setSocialMedia] = useState(null);
  const [mlsExports, setMlsExports] = useState({});
  const [videoScript, setVideoScript] = useState(null);
  const [emailContent, setEmailContent] = useState(null);
  const [buyerPersona, setBuyerPersona] = useState(null);
  const [neighborhoodData, setNeighborhoodData] = useState(null);
  const [seoAnalysis, setSeoAnalysis] = useState(null);
  const [printMarketing, setPrintMarketing] = useState(null);
  const [openHouseContent, setOpenHouseContent] = useState(null);
  const [agentContent, setAgentContent] = useState(null);
  const [stagingSuggestions, setStagingSuggestions] = useState(null);
  const [pricingSuggestions, setPricingSuggestions] = useState(null);
  const [isLoading, setIsLoading] = useState({});
  
  // New Features State
  const [qrCode, setQrCode] = useState(null);
  const [descriptionVariations, setDescriptionVariations] = useState(null);
  const [hashtags, setHashtags] = useState(null);
  const [highlightCard, setHighlightCard] = useState(null);
  const [clientTemplate, setClientTemplate] = useState(null);
  const [neighborhoodCompare, setNeighborhoodCompare] = useState(null);
  const [neighborhoodsToCompare, setNeighborhoodsToCompare] = useState(['', '', '']);
  
  // Pricing PDF Modal State
  const [showPricingPdfModal, setShowPricingPdfModal] = useState(false);
  const [pricingPdfData, setPricingPdfData] = useState(null);
  
  // Dashboard Stats State
  const [dashboardStats, setDashboardStats] = useState({ 
    listings: 0, 
    active: 0, 
    sold: 0, 
    clients: 0,
    documents: 0 
  });
  const [dashboardListings, setDashboardListings] = useState([]);
  
  const [clientFormData, setClientFormData] = useState({
    templateType: 'pre_showing',
    clientName: '',
    showingDate: '',
    showingTime: '',
    price: '',
    newPrice: ''
  });

  // Listen for pricing PDF modal events
  useEffect(() => {
    const handleShowPricingPdfModal = () => {
      if (window.pricingPdfData) {
        setPricingPdfData(window.pricingPdfData);
        setShowPricingPdfModal(true);
      }
    };
    window.addEventListener('showPricingPdfModal', handleShowPricingPdfModal);
    return () => window.removeEventListener('showPricingPdfModal', handleShowPricingPdfModal);
  }, []);

  // Load user from localStorage
  useEffect(() => {
    const savedUser = localStorage.getItem('listingai_user');
    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      setUser(parsedUser);
      // Set the plan from saved user data
      if (parsedUser.plan) {
        setUserPlan(parsedUser.plan);
      } else if (parsedUser.is_premium) {
        setUserPlan('premium');
      } else if (parsedUser.is_basic) {
        setUserPlan('basic');
      }
    }
  }, []);

  // Check if user has password when settings tab is opened
  useEffect(() => {
    if (activeTab === 'settings' && user) {
      checkHasPassword();
    }
  }, [activeTab, user]);

  // Detect and validate referral code from URL
  // Supports BOTH formats:
  //   - Path-based: /ref/PARTNER-CODE (from Partner Dashboard)
  //   - Query-based: ?ref=PARTNER-CODE (legacy)
  useEffect(() => {
    const detectReferralCode = async () => {
      // Check URL for ref parameter (query string)
      const urlParams = new URLSearchParams(window.location.search);
      let refCode = urlParams.get('ref') || urlParams.get('referral');
      
      // Also check for path-based referral: /ref/:code or /referral/:code
      const pathMatch = window.location.pathname.match(/^\/(ref|referral)\/([^\/]+)/i);
      if (pathMatch && pathMatch[2]) {
        refCode = pathMatch[2];
        console.log(`Detected path-based referral code: ${refCode}`);
      }
      
      // Check localStorage for previously stored referral code (using referralService)
      const storedRef = getReferralCode() || localStorage.getItem('gl_referral_code');
      const storedPartnerName = localStorage.getItem('gl_referral_partner');
      
      const codeToValidate = refCode || storedRef;
      
      if (codeToValidate) {
        // Store in localStorage using referralService (or update if from URL)
        if (refCode) {
          saveReferralCode(refCode.toUpperCase());
          localStorage.setItem('gl_referral_code', refCode.toUpperCase());
        }
        
        // If we already have cached partner name, use it
        if (storedPartnerName && !refCode) {
          setReferralCode(storedRef);
          setReferralPartnerName(storedPartnerName);
          return;
        }
        
        // Validate with Partner Dashboard API (via referralService)
        try {
          const validationResult = await validatePartnerCode(codeToValidate);
          
          if (validationResult.success) {
            setReferralCode(validationResult.partner_code);
            setReferralPartnerName(validationResult.partner_name);
            saveReferralCode(validationResult.partner_code);
            localStorage.setItem('gl_referral_code', validationResult.partner_code);
            localStorage.setItem('gl_referral_partner', validationResult.partner_name);
            console.log(`Referral code validated: ${validationResult.partner_code} from ${validationResult.partner_name}`);
            
            // For path-based referrals, redirect to clean URL and show PLANS PAGE first
            if (pathMatch) {
              setShowLandingPage(false);
              setShowPlansPage(true);  // Show plans page instead of direct signup
              window.history.replaceState(null, '', '/');
            }
          } else {
            // Invalid code - clear it
            console.log('Invalid referral code:', codeToValidate);
            clearReferralCode();
            localStorage.removeItem('gl_referral_code');
            localStorage.removeItem('gl_referral_partner');
            setReferralCode('');
            setReferralPartnerName('');
            
            // Redirect to home if path-based referral was invalid
            if (pathMatch) {
              window.history.replaceState(null, '', '/');
            }
          }
        } catch (err) {
          console.error('Error validating referral code:', err);
          // On network error, still show plans page with the code (optimistic approach)
          // The code will be validated again during signup
          if (pathMatch) {
            setReferralCode(codeToValidate.toUpperCase());
            setReferralPartnerName('Your Referral Partner');  // Placeholder until validated
            setShowLandingPage(false);
            setShowPlansPage(true);
            window.history.replaceState(null, '', '/');
          }
        }
        
        // Clean up URL (remove ref parameter) but keep other params
        if (refCode && !pathMatch) {
          urlParams.delete('ref');
          urlParams.delete('referral');
          const newUrl = urlParams.toString() 
            ? `${window.location.pathname}?${urlParams.toString()}`
            : window.location.pathname;
          window.history.replaceState(null, '', newUrl);
        }
      }
    };
    
    detectReferralCode();
  }, []);

  // Handle Emergent Google Auth callback - process session_id from URL hash
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  useEffect(() => {
    const handleAuthCallback = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];
        if (sessionId) {
          console.log('Processing Emergent auth callback with session_id');
          try {
            // Call backend to exchange session_id for user data via Emergent API
            const res = await axios.post(`${API}/auth/emergent-callback`, {
              session_id: sessionId,
              plan: localStorage.getItem('pendingPlan') || 'free'
            });
            
            console.log('Emergent auth response:', res.data);
            
            if (res.data.success) {
              // Clear pending plan
              localStorage.removeItem('pendingPlan');
              
              const userData = res.data;
              
              // Set user and plan
              setUser(userData);
              if (userData.plan) {
                setUserPlan(userData.plan);
              } else if (userData.is_premium) {
                setUserPlan('premium');
              } else {
                setUserPlan('free');
              }
              localStorage.setItem('listingai_user', JSON.stringify(userData));
              
              // Clear the hash from URL without page reload
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
              
              showToast('Successfully signed in with Google!', 'success');
            } else {
              console.error('Auth callback failed:', res.data.error);
              showToast(res.data.error || 'Sign in failed. Please try again.', 'error');
              // Clear the hash from URL
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }
          } catch (err) {
            console.error('Auth callback error:', err.response?.data || err.message);
            showToast('Sign in failed. Please try again.', 'error');
            // Clear the hash from URL
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
          }
        }
      }
    };
    
    handleAuthCallback();
  }, []);

  const handleAuth = (userData) => {
    setUser(userData);
    // Set the user plan from the auth response
    if (userData.plan) {
      setUserPlan(userData.plan);
    } else if (userData.is_premium) {
      setUserPlan('premium');
    } else {
      setUserPlan('free');
    }
    localStorage.setItem('listingai_user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('listingai_user');
  };

  // Fetch dashboard stats when user is logged in
  useEffect(() => {
    const fetchDashboardStats = async () => {
      if (user?.user_id) {
        try {
          const [statsRes, listingsRes] = await Promise.all([
            axios.get(`${API}/my-listings/stats?user_id=${user.user_id}`),
            axios.get(`${API}/my-listings?user_id=${user.user_id}`)
          ]);
          
          const listings = listingsRes.data.listings || [];
          const activeCount = listings.filter(l => l.status === 'active').length;
          const soldCount = listings.filter(l => l.status === 'sold').length;
          
          setDashboardStats({
            listings: statsRes.data.listings || listings.length,
            active: activeCount,
            sold: soldCount,
            clients: statsRes.data.clients || 0,
            documents: statsRes.data.documents || 0
          });
          setDashboardListings(listings);
        } catch (error) {
          console.error('Error fetching dashboard stats:', error);
        }
      }
    };
    fetchDashboardStats();
  }, [user]);

  const handleSubscribe = async (planType) => {
    // Free plan - just close modal
    if (planType === 'free') {
      setShowPricingModal(false);
      return;
    }

    // For paid plans, redirect to Stripe Checkout
    try {
      setShowPricingModal(false);
      showToast('Redirecting to secure checkout...', 'info');
      
      // Include referral code if user was referred
      const storedReferralCode = referralCode || localStorage.getItem('gl_referral_code');
      const res = await axios.post(`${API}/payments/create-checkout`, {
        plan_id: planType,
        origin_url: window.location.origin,
        user_email: user?.email || '',
        user_name: user?.name || '',
        referral_code: storedReferralCode || null
      });
      
      if (res.data.success && res.data.checkout_url) {
        // Store pending plan for after payment
        localStorage.setItem('pending_plan', planType);
        localStorage.setItem('pending_session', res.data.session_id);
        // Redirect to Stripe Checkout
        window.location.href = res.data.checkout_url;
      } else {
        showToast(res.data.error || 'Failed to create checkout session', 'error');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      showToast('Failed to start checkout. Please try again.', 'error');
    }
  };

  // Handle payment return from Stripe (Payment First Flow)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      // Payment successful - verify and handle account
      const verifyPayment = async () => {
        try {
          const res = await axios.get(`${API}/payments/status/${sessionId}`);
          if (res.data.success && res.data.payment_status === 'paid') {
            const pendingPlan = localStorage.getItem('pending_plan') || res.data.plan;
            
            // Restore referral info if it was stored before payment
            const storedReferralCode = localStorage.getItem('gl_referral_code');
            const storedPartnerName = localStorage.getItem('gl_referral_partner');
            if (storedReferralCode && storedPartnerName) {
              setReferralCode(storedReferralCode);
              setReferralPartnerName(storedPartnerName);
            }
            
            if (user) {
              // User already logged in - just update their plan
              showToast(`🎉 Welcome to ${pendingPlan}! Your subscription is now active.`, 'success');
              const updatedUser = {
                ...user,
                plan: pendingPlan,
                is_premium: pendingPlan === 'premium' || pendingPlan === 'brokerage',
                is_basic: pendingPlan === 'basic'
              };
              setUser(updatedUser);
              setUserPlan(pendingPlan);
              localStorage.setItem('listingai_user', JSON.stringify(updatedUser));
              
              // Notify Partner Dashboard of plan upgrade if user was referred
              let referralId = localStorage.getItem('gl_tracked_referral_id');
              if (!referralId && user?.user_id) {
                try {
                  const userRes = await axios.get(`${API}/auth/user/${user.user_id}`);
                  if (userRes.data.success && userRes.data.referral_id) {
                    referralId = userRes.data.referral_id;
                  }
                } catch (e) {
                  console.log('Could not fetch user referral_id:', e);
                }
              }
              
              if (referralId && pendingPlan !== 'free') {
                try {
                  const upgradeResult = await upgradeReferral(referralId, pendingPlan);
                  if (upgradeResult.success) {
                    console.log('Referral upgrade tracked:', referralId, pendingPlan);
                  }
                } catch (upgradeErr) {
                  console.error('Failed to track referral upgrade:', upgradeErr);
                }
              }
              
              // Clean up payment-related localStorage
              localStorage.removeItem('pending_plan');
              localStorage.removeItem('pending_session');
            } else if (res.data.token && !res.data.needs_password) {
              // Existing user with password - auto-login
              showToast(`🎉 Welcome back! Your ${pendingPlan} subscription is now active.`, 'success');
              const userData = {
                token: res.data.token,
                user_id: res.data.user_id,
                name: res.data.user_name,
                email: res.data.user_email,
                plan: pendingPlan,
                is_premium: pendingPlan === 'premium' || pendingPlan === 'brokerage',
                is_basic: pendingPlan === 'basic'
              };
              handleAuth(userData);
              localStorage.removeItem('pending_plan');
              localStorage.removeItem('pending_session');
            } else {
              // New user OR user needs to set password - show password setup
              showToast(`🎉 Payment successful! Set your password to complete signup.`, 'success');
              setSelectedPlan(pendingPlan);
              setShowPlansPage(false);
              setShowLandingPage(false);
              setShowPaymentSuccessSignup(true);
              // Store user info from payment for account completion
              localStorage.setItem('paid_session_id', sessionId);
              if (res.data.user_email) {
                localStorage.setItem('paid_user_email', res.data.user_email);
              }
              if (res.data.user_id) {
                localStorage.setItem('paid_user_id', res.data.user_id);
              }
              if (res.data.user_name) {
                localStorage.setItem('paid_user_name', res.data.user_name);
              }
            }
          } else {
            // Payment not complete or failed
            console.log('Payment verification returned:', res.data);
            showToast('Payment verification pending. Please wait...', 'info');
          }
        } catch (err) {
          console.error('Payment verification error:', err);
          showToast('Error verifying payment. Please contact support.', 'error');
        }
        
        // Clear URL params
        window.history.replaceState({}, '', window.location.pathname);
      };
      
      verifyPayment();
    } else if (paymentStatus === 'cancelled') {
      showToast('Payment was cancelled. You can try again anytime.', 'info');
      localStorage.removeItem('pending_plan');
      localStorage.removeItem('pending_session');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const isPremium = user?.is_premium || userPlan === 'premium';
  const isBasic = user?.is_basic || userPlan === 'basic';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const newImages = await Promise.all(files.map(async (file) => ({
      file, preview: URL.createObjectURL(file), name: file.name,
      base64: await fileToBase64(file), enhanced: false
    })));
    setImages(prev => [...prev, ...newImages]);
    setImageAnalysis(null);
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImageAnalysis(null);
  };

  const analyzeImages = async () => {
    if (images.length === 0) return;
    setIsAnalyzing(true);
    try {
      const res = await axios.post(`${API}/analyze-images`, { images: images.map(img => img.base64) });
      if (res.data.success) {
        setImageAnalysis(res.data);
        if (!formData.keyFeatures && res.data.key_features?.length > 0) {
          setFormData(prev => ({ ...prev, keyFeatures: res.data.key_features.join(', ') }));
        }
      }
    } catch (err) { console.error(err); }
    setIsAnalyzing(false);
  };

  const enhanceAllImages = async () => {
    setIsEnhancing(true);
    try {
      const enhanced = await Promise.all(images.map(async (img) => {
        if (img.enhanced) return img;
        const res = await axios.post(`${API}/enhance-image`, { image: img.base64, quality: 90 });
        if (res.data.success) {
          return { ...img, preview: res.data.optimized_image, base64: res.data.optimized_image, enhanced: true };
        }
        return img;
      }));
      setImages(enhanced);
    } catch (err) { console.error(err); }
    setIsEnhancing(false);
  };

  const generateDescription = async () => {
    setIsGenerating(true);
    try {
      const res = await axios.post(`${API}/generate-description`, {
        ...formData, imageCount: images.length, videoCount: 0,
        imageAnalysis: imageAnalysis?.analysis || ''
      });
      if (res.data.success) {
        setGeneratedDescription(res.data.description);
        showToast('Description generated successfully!', 'success');
      } else {
        showToast(res.data.error || 'Failed to generate description', 'error');
      }
    } catch (err) { 
      console.error(err);
      showToast('Failed to generate description. Please try again.', 'error');
    }
    setIsGenerating(false);
  };

  // Premium API calls helper
  const callPremiumAPI = async (endpoint, payload, setResult, loadingKey) => {
    if (!isPremium) {
      setShowPricingModal(true);
      return;
    }
    setIsLoading(prev => ({ ...prev, [loadingKey]: true }));
    try {
      const res = await axios.post(`${API}/premium/${endpoint}?user_id=${user?.user_id || ''}`, payload);
      if (res.data.success) {
        setResult(res.data);
      } else if (res.data.error?.includes('Premium')) {
        setShowPricingModal(true);
      }
    } catch (err) { console.error(err); }
    setIsLoading(prev => ({ ...prev, [loadingKey]: false }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    showToast('Copied to clipboard!', 'success');
    setTimeout(() => setCopied(false), 2000);
  };

  const canGenerate = formData.region && formData.propertyType && formData.address && formData.bedrooms && formData.bathrooms;

  // Tab configurations
  const freeTabs = [
    { id: 'generate', label: 'Generate', icon: Sparkles, premium: false }
  ];

  const premiumTabs = [
    { id: 'video', label: 'Video Scripts', icon: Video, premium: true },
    { id: 'email', label: 'Email Marketing', icon: Mail, premium: true },
    { id: 'persona', label: 'Buyer Persona', icon: Users, premium: true },
    { id: 'neighborhood', label: 'Neighborhood', icon: MapPin, premium: true },
    { id: 'seo', label: 'SEO Analyzer', icon: Search, premium: true },
    { id: 'openhouse', label: 'Open House', icon: HomeIcon, premium: true },
    { id: 'agent', label: 'Agent Tools', icon: User, premium: true },
    { id: 'translate', label: 'Translate', icon: Globe, premium: true },
    { id: 'social', label: 'Social Media', icon: Share, premium: true },
    { id: 'staging', label: 'Staging', icon: Sofa, premium: true },
    { id: 'pricing', label: 'Pricing', icon: DollarSign, premium: true },
  ];

  const allTabs = [...freeTabs, ...premiumTabs];

  return (
    <AuthContext.Provider value={{ user, isPremium }}>
      <div className="min-h-screen bg-gray-50">
        {user ? (
          <>
            {/* Sidebar */}
            <Sidebar 
              activeSection={activeTab}
              onSectionChange={handleTabChange}
              userPlan={userPlan}
              onUpgrade={() => setShowPricingModal(true)}
              isAdmin={isAdmin}
              onFeedback={() => setShowFeedbackModal(true)}
              isMobileOpen={isMobileSidebarOpen}
              onCloseMobile={() => setIsMobileSidebarOpen(false)}
            />
            
            {/* Top Bar */}
            <TopBar 
              user={user} 
              onLogout={handleLogout}
              onToggleMobileMenu={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
            />
            
            {/* Main Content - adjusted for sidebar and topbar */}
            <main className="lg:ml-64 min-h-screen" style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)', marginTop: 'calc(4rem + env(safe-area-inset-top, 0px))' }}>
              {/* Rounded White Content Container */}
              <div className="p-4 lg:p-6 pb-20">
                <div className="max-w-6xl mx-auto bg-white rounded-2xl lg:rounded-3xl shadow-xl border border-gray-100 p-4 lg:p-6 min-h-[calc(100vh-180px)]">
                {/* Dashboard Section */}
                {activeTab === 'dashboard' && (
                  <div className="space-y-6">
                    {/* Welcome Header */}
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6 border border-purple-100/50">
                      <div className="flex items-center justify-between">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-800">
                            Welcome back{user.name ? `, ${user.name}` : ''}! 👋
                          </h1>
                          <p className="text-gray-500 mt-1">
                            {userPlan === 'premium' 
                              ? 'You have full access to all features' 
                              : 'Upgrade to Premium for access to all marketing tools'}
                          </p>
                        </div>
                        <PlanBadge plan={userPlan} />
                      </div>
                    </div>
                    
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-3 sm:p-5 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xl sm:text-2xl font-bold text-gray-800">{dashboardStats.listings || dashboardListings.length || 0}</p>
                            <p className="text-xs sm:text-sm text-gray-500 truncate">Listings Created</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-xl p-3 sm:p-5 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Share className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xl sm:text-2xl font-bold text-gray-800">{dashboardStats.active || 0}</p>
                            <p className="text-xs sm:text-sm text-gray-500 truncate">Active Listings</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-xl p-3 sm:p-5 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xl sm:text-2xl font-bold text-gray-800">{dashboardStats.sold || 0}</p>
                            <p className="text-xs sm:text-sm text-gray-500 truncate">Properties Sold</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-xl p-3 sm:p-5 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Users className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xl sm:text-2xl font-bold text-gray-800">{dashboardStats.clients || 0}</p>
                            <p className="text-xs sm:text-sm text-gray-500 truncate">Clients</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Quick Actions */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <h2 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h2>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <button 
                          onClick={() => handleTabChange('generate')}
                          className="flex flex-col items-center gap-2 p-4 bg-purple-50 hover:bg-purple-100 rounded-xl transition-colors"
                        >
                          <Sparkles className="w-6 h-6 text-purple-600" />
                          <span className="text-sm font-medium text-gray-700">New Listing</span>
                        </button>
                        <button 
                          onClick={() => handleTabChange('feature-sheet')}
                          className="flex flex-col items-center gap-2 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                        >
                          <FileText className="w-6 h-6 text-blue-600" />
                          <span className="text-sm font-medium text-gray-700">Feature Sheet</span>
                        </button>
                        <button 
                          onClick={() => handleTabChange('social')}
                          className="flex flex-col items-center gap-2 p-4 bg-pink-50 hover:bg-pink-100 rounded-xl transition-colors"
                        >
                          <Share className="w-6 h-6 text-pink-600" />
                          <span className="text-sm font-medium text-gray-700">Social Media</span>
                        </button>
                        <button 
                          onClick={() => handleTabChange('video')}
                          className="flex flex-col items-center gap-2 p-4 bg-green-50 hover:bg-green-100 rounded-xl transition-colors"
                        >
                          <Video className="w-6 h-6 text-green-600" />
                          <span className="text-sm font-medium text-gray-700">Video Script</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Generate Listing Section */}
                {activeTab === 'generate' && (
                  <GenerateSection 
                    onGenerate={generateDescription}
                    isGenerating={isGenerating}
                    generatedDescription={generatedDescription}
                    onCopy={copyToClipboard}
                    images={images}
                    setImages={setImages}
                    imageAnalysis={imageAnalysis}
                    setImageAnalysis={setImageAnalysis}
                    isAnalyzing={isAnalyzing}
                    setIsAnalyzing={setIsAnalyzing}
                    analyzeImages={analyzeImages}
                    formData={formData}
                    setFormData={setFormData}
                    onDescriptionGenerated={setGeneratedDescription}
                    showToast={showToast}
                    userId={user?.user_id}
                    onNavigate={handleTabChange}
                  />
                )}

                {/* My Listings Section */}
                {activeTab === 'my-listings' && (
                  <MyListingsSection userId={user?.user_id} API={API} />
                )}

                {/* Feature Sheet Section */}
                {activeTab === 'feature-sheet' && (
                  <div className="space-y-6">
                    <div>
                      <h1 className="text-2xl font-bold text-gray-800">Feature Sheet Generator</h1>
                      <p className="text-gray-500">Create professional PDF feature sheets</p>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <FeatureSheetGenerator 
                        parentFormData={formData}
                        images={images}
                        generatedDescription={generatedDescription}
                        imageAnalysis={imageAnalysis}
                        userId={user?.user_id}
                        API={API}
                      />
                    </div>
                  </div>
                )}

                {/* Phase 1 AI Tools */}

                {/* A/B Variations */}
                {activeTab === 'variations' && (
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-800">A/B Variations</h1>
                          <p className="text-gray-500">Generate multiple versions optimized for different audiences</p>
                        </div>
                        {userPlan !== 'premium' && <PlanBadge plan={userPlan} />}
                      </div>
                      {userPlan === 'premium' ? (
                        <AIMarketingToolsSingle 
                          toolId="variations"
                          userPlan={userPlan}
                          generatedDescription={generatedDescription}
                          formData={formData}
                          userId={user?.user_id}
                          API={API}
                        />
                      ) : (
                        <PremiumLock onUpgrade={() => setShowPricingModal(true)} />
                      )}
                    </div>
                  </div>
                )}

                {/* QR Code */}
                {activeTab === 'qrcode' && (
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-800">QR Code</h1>
                          <p className="text-gray-500">Generate QR codes for your property listings</p>
                        </div>
                        {userPlan !== 'premium' && <PlanBadge plan={userPlan} />}
                      </div>
                      {userPlan === 'premium' ? (
                        <AIMarketingToolsSingle 
                          toolId="qrcode"
                          userPlan={userPlan}
                          generatedDescription={generatedDescription}
                          formData={formData}
                          userId={user?.user_id}
                          API={API}
                        />
                      ) : (
                        <PremiumLock onUpgrade={() => setShowPricingModal(true)} />
                      )}
                    </div>
                  </div>
                )}

                {/* Hashtags */}
                {activeTab === 'hashtags' && (
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-800">Hashtags</h1>
                          <p className="text-gray-500">Generate optimized hashtags for social media platforms</p>
                        </div>
                        {userPlan === 'free' && <PlanBadge plan={userPlan} />}
                      </div>
                      {(userPlan === 'premium' || userPlan === 'basic' || userPlan === 'brokerage') ? (
                        <AIMarketingToolsSingle 
                          toolId="hashtags"
                          userPlan={userPlan}
                          generatedDescription={generatedDescription}
                          formData={formData}
                          userId={user?.user_id}
                          API={API}
                        />
                      ) : (
                        <PremiumLock onUpgrade={() => setShowPricingModal(true)} />
                      )}
                    </div>
                  </div>
                )}

                {/* Highlight Cards */}
                {activeTab === 'highlight' && (
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-800">Highlight Cards</h1>
                          <p className="text-gray-500">Create visual highlight cards for key property features</p>
                        </div>
                        {userPlan !== 'premium' && <PlanBadge plan={userPlan} />}
                      </div>
                      {userPlan === 'premium' ? (
                        <AIMarketingToolsSingle 
                          toolId="highlights"
                          userPlan={userPlan}
                          generatedDescription={generatedDescription}
                          formData={formData}
                          userId={user?.user_id}
                          API={API}
                        />
                      ) : (
                        <PremiumLock onUpgrade={() => setShowPricingModal(true)} />
                      )}
                    </div>
                  </div>
                )}

                {/* Video Scripts */}
                {activeTab === 'video' && (
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <Video className="w-7 h-7 text-purple-600" />
                            Video Script Generator
                          </h1>
                          <p className="text-gray-500">Create professional video scripts for property tours, TikToks, Reels & more</p>
                        </div>
                        {userPlan !== 'premium' && <PlanBadge plan={userPlan} />}
                      </div>
                      {userPlan === 'premium' ? (
                        <VideoScriptEnhanced 
                          formData={formData}
                          generatedDescription={generatedDescription}
                          images={images}
                          userId={user?.user_id}
                          API={API}
                        />
                      ) : (
                        <PremiumLock onUpgrade={() => setShowPricingModal(true)} />
                      )}
                    </div>
                  </div>
                )}

                {/* Phase 2 Tools */}

                {/* Social Media */}
                {activeTab === 'social' && (
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-800">Social Media</h1>
                          <p className="text-gray-500">Generate platform-specific social media posts</p>
                        </div>
                        {userPlan === 'free' && <PlanBadge plan={userPlan} />}
                      </div>
                      {(userPlan === 'premium' || userPlan === 'basic' || userPlan === 'brokerage') ? (
                        <AIMarketingToolsSingle 
                          toolId="social"
                          userPlan={userPlan}
                          generatedDescription={generatedDescription}
                          formData={formData}
                          userId={user?.user_id}
                          API={API}
                        />
                      ) : (
                        <PremiumLock onUpgrade={() => setShowPricingModal(true)} />
                      )}
                    </div>
                  </div>
                )}

                {/* Client Emails */}
                {activeTab === 'clientcomm' && (
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-800">Client Emails</h1>
                          <p className="text-gray-500">Generate professional email templates with multiple tone variations</p>
                        </div>
                        {userPlan !== 'premium' && <PlanBadge plan={userPlan} />}
                      </div>
                      {userPlan === 'premium' ? (
                        <ClientEmailsComponent 
                          userId={user?.user_id}
                          API={API}
                          formData={formData}
                        />
                      ) : (
                        <PremiumLock onUpgrade={() => setShowPricingModal(true)} />
                      )}
                    </div>
                  </div>
                )}

                {/* Compare Areas */}
                {/* Compare Areas - Updated with Neighborhood Selection UI */}
                {activeTab === 'compare' && (
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-800">Compare Neighborhoods</h1>
                          <p className="text-gray-500 mt-1">Select 3 neighborhoods to compare and get AI-powered recommendations</p>
                        </div>
                        {userPlan !== 'premium' && <PlanBadge plan={userPlan} />}
                      </div>
                      {userPlan === 'premium' ? (
                        <CompareAreasWithSelection 
                          formData={formData}
                          generatedDescription={generatedDescription}
                          userId={user?.user_id}
                          API={API}
                        />
                      ) : (
                        <PremiumLock onUpgrade={() => setShowPricingModal(true)} />
                      )}
                    </div>
                  </div>
                )}

                {/* Phase 3 Tools */}

                {/* SEO Analyzer */}
                {activeTab === 'seo' && (
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-800">SEO Analyzer</h1>
                          <p className="text-gray-500">Analyze and optimize your listing for search engines</p>
                        </div>
                        {userPlan !== 'premium' && <PlanBadge plan={userPlan} />}
                      </div>
                      {userPlan === 'premium' ? (
                        <AIMarketingToolsSingle 
                          toolId="seo"
                          userPlan={userPlan}
                          generatedDescription={generatedDescription}
                          formData={formData}
                          userId={user?.user_id}
                          API={API}
                        />
                      ) : (
                        <PremiumLock onUpgrade={() => setShowPricingModal(true)} />
                      )}
                    </div>
                  </div>
                )}

                {/* Translate */}
                {activeTab === 'translate' && (
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-800">Translate</h1>
                          <p className="text-gray-500">Translate your listing to multiple languages</p>
                        </div>
                        {userPlan !== 'premium' && <PlanBadge plan={userPlan} />}
                      </div>
                      {userPlan === 'premium' ? (
                        <AIMarketingToolsSingle 
                          toolId="translate"
                          userPlan={userPlan}
                          generatedDescription={generatedDescription}
                          formData={formData}
                          userId={user?.user_id}
                          API={API}
                        />
                      ) : (
                        <PremiumLock onUpgrade={() => setShowPricingModal(true)} />
                      )}
                    </div>
                  </div>
                )}

                {/* Buyer Persona - ENHANCED */}
                {activeTab === 'persona' && (
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <Users className="w-7 h-7 text-purple-600" />
                            Buyer Persona Builder
                          </h1>
                          <p className="text-gray-500">Create detailed buyer profiles with customizable demographics, priorities, and pain points</p>
                        </div>
                        {userPlan !== 'premium' && <PlanBadge plan={userPlan} />}
                      </div>
                      {userPlan === 'premium' ? (
                        <BuyerPersonaEnhanced 
                          formData={formData}
                          generatedDescription={generatedDescription}
                          API={API}
                          userId={user?.user_id}
                        />
                      ) : (
                        <PremiumLock onUpgrade={() => setShowPricingModal(true)} />
                      )}
                    </div>
                  </div>
                )}

                {/* Virtual Staging - ENHANCED WITH AI PHOTO STAGING */}
                {activeTab === 'staging' && (
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-800">Virtual Staging</h1>
                          <p className="text-gray-500">AI-powered staging recommendations & photo visualization</p>
                        </div>
                        {userPlan !== 'premium' && <PlanBadge plan={userPlan} />}
                      </div>
                      {userPlan === 'premium' ? (
                        <VirtualStagingEnhanced 
                          userPlan={userPlan}
                          generatedDescription={generatedDescription}
                          formData={formData}
                          userId={user?.user_id}
                          API={API}
                        />
                      ) : (
                        <PremiumLock onUpgrade={() => setShowPricingModal(true)} />
                      )}
                    </div>
                  </div>
                )}

                {/* Phase 4 Tools - FINAL */}

                {/* Open House - Enhanced Toolkit */}
                {activeTab === 'openhouse' && (
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <Home className="w-7 h-7 text-purple-600" />
                            Open House Toolkit
                          </h1>
                          <p className="text-gray-500">Complete toolkit for successful open houses - invitations, sign-ins, follow-ups & more</p>
                        </div>
                        {userPlan !== 'premium' && <PlanBadge plan={userPlan} />}
                      </div>
                      {userPlan === 'premium' ? (
                        <OpenHouseToolkit 
                          formData={formData}
                          generatedDescription={generatedDescription}
                          API={API}
                          userId={user?.user_id}
                        />
                      ) : (
                        <PremiumLock onUpgrade={() => setShowPricingModal(true)} />
                      )}
                    </div>
                  </div>
                )}

                {/* Agent Tools */}
                {activeTab === 'agent' && (
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-800">Agent Tools</h1>
                          <p className="text-gray-500">Professional tools for real estate agents</p>
                        </div>
                        {userPlan !== 'premium' && <PlanBadge plan={userPlan} />}
                      </div>
                      {userPlan === 'premium' ? (
                        <AIMarketingToolsSingle 
                          toolId="agent"
                          userPlan={userPlan}
                          generatedDescription={generatedDescription}
                          formData={formData}
                          userId={user?.user_id}
                          API={API}
                        />
                      ) : (
                        <PremiumLock onUpgrade={() => setShowPricingModal(true)} />
                      )}
                    </div>
                  </div>
                )}

                {/* Pricing Suggestions */}
                {activeTab === 'pricing' && (
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h1 className="text-2xl font-bold text-gray-800">Pricing Suggestions</h1>
                          <p className="text-gray-500">AI-powered pricing analysis and recommendations</p>
                        </div>
                        {userPlan !== 'premium' && <PlanBadge plan={userPlan} />}
                      </div>
                      {userPlan === 'premium' ? (
                        <AIMarketingToolsSingle 
                          toolId="pricing"
                          userPlan={userPlan}
                          generatedDescription={generatedDescription}
                          formData={formData}
                          userId={user?.user_id}
                          API={API}
                        />
                      ) : (
                        <PremiumLock onUpgrade={() => setShowPricingModal(true)} />
                      )}
                    </div>
                  </div>
                )}

                {/* Settings Section */}
                {activeTab === 'settings' && (
                  <div className="max-w-4xl mx-auto space-y-6">
                    <div>
                      <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
                      <p className="text-gray-600">Manage your account and preferences</p>
                    </div>

                    {/* Profile Section */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                      <h2 className="text-xl font-bold text-gray-900 mb-4">Profile Information</h2>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                          <input
                            type="text"
                            defaultValue={user?.name || ''}
                            placeholder="Enter your name"
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-700"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                          <input
                            type="email"
                            value={user?.email || ''}
                            disabled
                            className="w-full px-4 py-2.5 bg-gray-100 border border-gray-300 rounded-xl text-gray-500 cursor-not-allowed"
                          />
                          <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                        </div>
                        <button className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors">
                          Save Changes
                        </button>
                      </div>
                    </div>

                    {/* Subscription Section */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                      <h2 className="text-xl font-bold text-gray-900 mb-4">Subscription</h2>
                      <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-100">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-gray-900">Current Plan:</p>
                            <PlanBadge plan={userPlan} />
                          </div>
                          <p className="text-sm text-gray-600">
                            {userPlan === 'premium' ? '$59.99/month - All features unlocked' : 
                             userPlan === 'basic' ? '$29.99/month - Basic features' : 
                             'Free - Limited features'}
                          </p>
                        </div>
                        {userPlan !== 'premium' && (
                          <button 
                            onClick={() => setShowPricingModal(true)}
                            className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors flex items-center gap-2"
                          >
                            <Crown className="w-4 h-4" />
                            Upgrade
                          </button>
                        )}
                      </div>
                      
                      {/* Manage Subscription Button - for paid users with Stripe */}
                      {(userPlan === 'premium' || userPlan === 'basic' || userPlan === 'brokerage') && (
                        <div className="mt-4 flex flex-col sm:flex-row gap-3">
                          <button 
                            onClick={async () => {
                              try {
                                const userId = user?.user_id || user?.id;
                                if (!userId) {
                                  showToast('Please log in to manage your subscription', 'error');
                                  return;
                                }
                                showToast('Opening billing portal...', 'info');
                                const res = await axios.post(`${API}/subscription/manage`, {
                                  user_id: userId,
                                  return_url: window.location.origin + '/settings'
                                });
                                if (res.data.success && res.data.portal_url) {
                                  window.location.href = res.data.portal_url;
                                } else {
                                  // Show helpful error for users who haven't paid through Stripe
                                  if (res.data.error?.includes('No subscription found')) {
                                    showToast('Your account was set up before billing was enabled. Contact support to manage your subscription.', 'info');
                                  } else {
                                    showToast(res.data.error || 'Unable to open billing portal', 'error');
                                  }
                                }
                              } catch (err) {
                                console.error('Portal error:', err?.response?.data || err);
                                const errorMsg = err?.response?.data?.detail?.[0]?.msg || 'Failed to open billing portal. Please try again.';
                                showToast(errorMsg, 'error');
                              }
                            }}
                            className="flex-1 px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                          >
                            <CreditCard className="w-4 h-4" />
                            Manage Subscription
                          </button>
                          <p className="text-xs text-gray-500 sm:hidden">Update payment method, view invoices, or cancel</p>
                        </div>
                      )}
                      
                      {userPlan === 'premium' && (
                        <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-100">
                          <div className="flex items-center gap-2 text-green-700">
                            <Check className="w-5 h-5" />
                            <span className="font-medium">Premium Active</span>
                          </div>
                          <p className="text-sm text-green-600 mt-1">You have access to all premium features</p>
                        </div>
                      )}
                      
                      {/* Help text for paid users */}
                      {(userPlan === 'premium' || userPlan === 'basic' || userPlan === 'brokerage') && (
                        <p className="mt-3 text-xs text-gray-500 hidden sm:block">
                          Click "Manage Subscription" to update payment method, view invoices, or cancel your plan
                        </p>
                      )}
                    </div>

                    {/* Login & Security Section */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                          <Lock className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-gray-900">Login & Security</h2>
                          <p className="text-sm text-gray-500">Manage your password and account security</p>
                        </div>
                      </div>
                      
                      {/* Password Status Badge */}
                      <div className={`mb-4 p-3 rounded-xl ${hasPassword ? 'bg-green-50 border border-green-100' : 'bg-yellow-50 border border-yellow-100'}`}>
                        <div className="flex items-center gap-2">
                          {hasPassword ? (
                            <>
                              <Check className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-medium text-green-700">Password is set</span>
                            </>
                          ) : (
                            <>
                              <AlertCircle className="w-4 h-4 text-yellow-600" />
                              <span className="text-sm font-medium text-yellow-700">No password set - Add one to enable email login</span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Error/Success Messages */}
                      {passwordError && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                          <div className="flex items-center gap-2 text-red-700">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-sm">{passwordError}</span>
                          </div>
                        </div>
                      )}
                      {passwordSuccess && (
                        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
                          <div className="flex items-center gap-2 text-green-700">
                            <Check className="w-4 h-4" />
                            <span className="text-sm">{passwordSuccess}</span>
                          </div>
                        </div>
                      )}
                      
                      <div className="space-y-4">
                        {/* Current Password - only show if user has password */}
                        {hasPassword && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                            <div className="relative">
                              <input 
                                type={showPasswords.current ? 'text' : 'password'}
                                value={passwordData.currentPassword}
                                onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-12"
                                placeholder="Enter current password"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {/* New Password */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {hasPassword ? 'New Password' : 'Create Password'}
                          </label>
                          <div className="relative">
                            <input 
                              type={showPasswords.new ? 'text' : 'password'}
                              value={passwordData.newPassword}
                              onChange={(e) => {
                                setPasswordData({...passwordData, newPassword: e.target.value});
                                setPasswordError('');
                                setPasswordSuccess('');
                              }}
                              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-12"
                              placeholder="Enter new password (min 6 characters)"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                          </div>
                          {/* Password strength indicator */}
                          {passwordData.newPassword && (
                            <div className="mt-2">
                              <div className="flex gap-1">
                                <div className={`h-1 flex-1 rounded ${passwordData.newPassword.length >= 6 ? 'bg-green-500' : 'bg-gray-200'}`} />
                                <div className={`h-1 flex-1 rounded ${passwordData.newPassword.length >= 8 ? 'bg-green-500' : 'bg-gray-200'}`} />
                                <div className={`h-1 flex-1 rounded ${passwordData.newPassword.length >= 10 && /[A-Z]/.test(passwordData.newPassword) ? 'bg-green-500' : 'bg-gray-200'}`} />
                                <div className={`h-1 flex-1 rounded ${passwordData.newPassword.length >= 12 && /[0-9]/.test(passwordData.newPassword) && /[^A-Za-z0-9]/.test(passwordData.newPassword) ? 'bg-green-500' : 'bg-gray-200'}`} />
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {passwordData.newPassword.length < 6 ? 'Too short' : 
                                 passwordData.newPassword.length < 8 ? 'Weak' :
                                 passwordData.newPassword.length < 10 ? 'Fair' :
                                 passwordData.newPassword.length >= 12 && /[0-9]/.test(passwordData.newPassword) ? 'Strong' : 'Good'}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {/* Confirm Password */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                          <div className="relative">
                            <input 
                              type={showPasswords.confirm ? 'text' : 'password'}
                              value={passwordData.confirmPassword}
                              onChange={(e) => {
                                setPasswordData({...passwordData, confirmPassword: e.target.value});
                                setPasswordError('');
                                setPasswordSuccess('');
                              }}
                              className={`w-full px-4 py-2.5 bg-gray-50 border rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-12 ${
                                passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword
                                  ? 'border-red-300 bg-red-50'
                                  : passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword
                                  ? 'border-green-300 bg-green-50'
                                  : 'border-gray-300'
                              }`}
                              placeholder="Confirm new password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                          </div>
                          {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                            <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                          )}
                          {passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword && (
                            <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                              <Check className="w-3 h-3" /> Passwords match
                            </p>
                          )}
                        </div>
                        
                        <button 
                          onClick={handlePasswordSubmit}
                          disabled={isChangingPassword || !passwordData.newPassword || !passwordData.confirmPassword || passwordData.newPassword !== passwordData.confirmPassword}
                          className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors flex items-center gap-2"
                        >
                          {isChangingPassword ? (
                            <>
                              <LoadingSpinner size="sm" />
                              {hasPassword ? 'Updating...' : 'Setting...'}
                            </>
                          ) : (
                            <>
                              <Lock className="w-4 h-4" />
                              {hasPassword ? 'Update Password' : 'Set Password'}
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Preferences Section */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                      <h2 className="text-xl font-bold text-gray-900 mb-4">Preferences</h2>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between py-3 border-b border-gray-100">
                          <div>
                            <p className="font-medium text-gray-800">Default Region</p>
                            <p className="text-sm text-gray-500">Set your default region for listings</p>
                          </div>
                          <select className="px-4 py-2 bg-gray-50 border border-gray-300 rounded-xl text-gray-700">
                            <option value="usa">USA</option>
                            <option value="canada">Canada</option>
                          </select>
                        </div>
                        <div className="flex items-center justify-between py-3 border-b border-gray-100">
                          <div>
                            <p className="font-medium text-gray-800">Default Style</p>
                            <p className="text-sm text-gray-500">Preferred writing style for listings</p>
                          </div>
                          <select className="px-4 py-2 bg-gray-50 border border-gray-300 rounded-xl text-gray-700">
                            <option value="luxury">Luxury</option>
                            <option value="modern">Modern</option>
                            <option value="cozy">Cozy</option>
                            <option value="professional">Professional</option>
                          </select>
                        </div>
                        <div className="flex items-center justify-between py-3">
                          <div>
                            <p className="font-medium text-gray-800">Email Notifications</p>
                            <p className="text-sm text-gray-500">Receive updates and tips</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" defaultChecked className="sr-only peer" />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Account Actions Section */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                      <h2 className="text-xl font-bold text-gray-900 mb-4">Account Actions</h2>
                      <div className="space-y-3">
                        <button 
                          onClick={handleLogout}
                          className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Admin Dashboard Section */}
                {activeTab === 'admin' && isAdmin && (
                  <AdminDashboard 
                    user={user} 
                    API_URL={BACKEND_URL}
                  />
                )}
              </div>
              </div>
            </main>
          </>
        ) : showPlansPage ? (
          /* Plans Selection Page */
          <PlansSelectionPage 
            referralCode={referralCode}
            referralPartnerName={referralPartnerName}
            onSelectPlan={async (plan) => {
              if (plan === 'free') {
                // Free plan - go to registration
                setSelectedPlan(plan);
                setShowPlansPage(false);
              } else {
                // PAID PLAN - Payment First Flow
                // Redirect to Stripe checkout immediately
                try {
                  showToast('Redirecting to secure checkout...', 'info');
                  // Include referral code if user was referred
                  const storedReferralCode = referralCode || localStorage.getItem('gl_referral_code');
                  const res = await axios.post(`${API}/payments/create-checkout`, {
                    plan_id: plan,
                    origin_url: window.location.origin,
                    user_email: '',
                    user_name: '',
                    referral_code: storedReferralCode || null
                  });
                  
                  if (res.data.success && res.data.checkout_url) {
                    // Store selected plan for after payment
                    localStorage.setItem('pending_plan', plan);
                    localStorage.setItem('pending_session', res.data.session_id);
                    // Redirect to Stripe Checkout
                    window.location.href = res.data.checkout_url;
                  } else {
                    showToast(res.data.error || 'Failed to create checkout session', 'error');
                  }
                } catch (err) {
                  console.error('Checkout error:', err);
                  showToast('Failed to start checkout. Please try again.', 'error');
                }
              }
            }}
            onBack={() => {
              setShowPlansPage(false);
              setSelectedPlan(null);
              setShowLandingPage(true);
            }}
          />
        ) : showPaymentSuccessSignup || selectedPlan ? (
          /* Registration Form after Plan Selection (Free plan OR after payment success) */
          <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #5B21B6 0%, #4C1D95 30%, #3730A3 60%, #1E3A8A 100%)' }}>
            <div className="max-w-md w-full">
              {/* Referral Banner */}
              {referralCode && referralPartnerName && (
                <div className="mb-4 bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-4 py-3 rounded-xl text-center shadow-lg">
                  <span className="font-medium">🎉 You were referred by {referralPartnerName}</span>
                  <span className="ml-2 text-white/80">— Welcome to GeniusList!</span>
                </div>
              )}
              
              {/* Payment Success Banner */}
              {showPaymentSuccessSignup && (
                <div className="mb-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-3 rounded-xl text-center shadow-lg">
                  <span className="font-medium">✅ Payment successful!</span>
                  <span className="ml-2 text-white/80">Create your account to get started.</span>
                </div>
              )}
              
              <div className="text-center mb-8">
                <img src="/logo.png" alt="GeniusList" className="w-24 h-24 rounded-2xl mx-auto mb-4 object-contain" />
                <h1 className="text-3xl font-bold text-white mb-2">GeniusList</h1>
                <p className="text-white/70">AI-Powered Real Estate Marketing</p>
                {(showPaymentSuccessSignup || selectedPlan) && selectedPlan !== 'free' && (
                  <div className="mt-2 inline-block px-3 py-1 bg-white/20 rounded-full">
                    <span className="text-white/90 text-sm">
                      {selectedPlan === 'premium' ? '👑 Premium Plan' : 
                       selectedPlan === 'basic' ? '⭐ Basic Plan' : 
                       selectedPlan === 'brokerage' ? '🏢 Brokerage Plan' : ''}
                    </span>
                  </div>
                )}
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-2xl">
                <AuthModal 
                  isOpen={true} 
                  onClose={() => {}} 
                  onAuth={(data) => {
                    handleAuth(data);
                    setSelectedPlan(null);
                    setShowPaymentSuccessSignup(false);
                    // Clean up after successful signup
                    localStorage.removeItem('pending_plan');
                    localStorage.removeItem('paid_session_id');
                  }} 
                  embedded={true}
                  selectedPlan={selectedPlan || localStorage.getItem('pending_plan')}
                  onShowPlans={() => setShowPlansPage(true)}
                  initialMode={showPaymentSuccessSignup ? "signup" : "login"}
                  referralCode={referralCode}
                  referralPartnerName={referralPartnerName}
                />
                {selectedPlan === 'free' && !showPaymentSuccessSignup && (
                  <div className="mt-4 pt-4 border-t border-gray-200 text-center">
                    <button 
                      onClick={() => {
                        setSelectedPlan(null);
                        setShowPlansPage(true);
                      }}
                      className="text-purple-600 hover:underline text-sm font-medium"
                      data-testid="change-plan"
                    >
                      ← Change Plan
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : showLandingPage ? (
          /* Landing Page for non-logged users */
          <LandingPage 
            onGetStarted={() => {
              setShowLandingPage(false);
              setShowPlansPage(true);
            }}
            onSignIn={() => {
              setShowLandingPage(false);
            }}
            referralCode={referralCode}
            referralPartnerName={referralPartnerName}
          />
        ) : (
          /* Auth Screen for non-logged users - Login/Signup entry point */
          <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #5B21B6 0%, #4C1D95 30%, #3730A3 60%, #1E3A8A 100%)' }}>
            <div className="max-w-md w-full">
              <div className="text-center mb-8">
                <img src="/logo.png" alt="GeniusList" className="w-24 h-24 rounded-2xl mx-auto mb-4 object-contain" />
                <h1 className="text-3xl font-bold text-white mb-2">GeniusList</h1>
                <p className="text-white/70">AI-Powered Real Estate Marketing</p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-2xl">
                <AuthModal 
                  isOpen={true} 
                  onClose={() => {}} 
                  onAuth={handleAuth} 
                  embedded={true}
                  onShowPlans={() => setShowPlansPage(true)}
                  referralCode={referralCode}
                  referralPartnerName={referralPartnerName}
                />
                <div className="mt-4 pt-4 border-t border-gray-200 text-center">
                  <button 
                    onClick={() => setShowLandingPage(true)}
                    className="text-purple-600 hover:underline text-sm font-medium"
                  >
                    ← Back to Home
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        {user && <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} onAuth={handleAuth} referralCode={referralCode} referralPartnerName={referralPartnerName} />}
        <PricingModal isOpen={showPricingModal} onClose={() => setShowPricingModal(false)} onSubscribe={handleSubscribe} />
        
        {/* Feedback Modal */}
        <FeedbackModal 
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          user={user}
          API_URL={BACKEND_URL}
        />
        
        {/* Pricing PDF Export Modal */}
        <PricingPdfModal 
          isOpen={showPricingPdfModal}
          onClose={() => {
            setShowPricingPdfModal(false);
            setPricingPdfData(null);
          }}
          pricingData={pricingPdfData}
          userId={user?.user_id}
        />
        
        {/* Toast Notification */}
        {toast && (
          <Toast 
            message={toast.message} 
            type={toast.type} 
            onClose={() => setToast(null)} 
          />
        )}

        {/* AI Chatbot Assistant */}
        {user && (
          <AIChatbot onNavigate={handleTabChange} />
        )}
      </div>
    </AuthContext.Provider>
  );
}

function App() {
  return <div className="App"><ListingApp /></div>;
}

export default App;
